import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { HexColorPicker, HexColorInput } from "react-colorful";
import {
  AlertTriangle, ArrowUpDown, BookMarked, Camera, Check, CheckSquare, ChevronDown, ChevronLeft,
  Folder, FolderPlus, Highlighter, Image, Link2, Maximize2, Mic, Minimize2, MoreVertical, Music, Palette, Pin, Plus,
  Redo2, Save, Search, Square, Trash2, Type, Undo2, Video, X,
} from "lucide-react";
import { NOTE_COLORS, NOTE_DARK_COLORS, NOTE_GRADIENTS } from "../../constants/palettes";
import { FONTS } from "../../constants/fonts";
import { uid } from "../../utils/storage";
import { todayStr } from "../../utils/dateHelpers";
import { Sparkle } from "../../PhosphorIcons";
import { mix, onColor, isDarkBgTheme } from "../../theme/colorEngine";
import { isValidHttpUrl, buildEmbedPillHtml } from "../../utils/linkEmbed";
import { uploadToCloudinary, isCloudinaryConfigured } from "../../cloudinary/upload";
import { usePremium } from "../../firebase/billing";
import { useMediaUpload, MediaStatus, mediaToHtml } from "../../components/attachments/useMediaUpload";
import { LockBadge } from "../../components/LockBadge";
import { Capacitor } from "@capacitor/core";
import { Keyboard } from "@capacitor/keyboard";
import { saveImageToDeviceGallery } from "../../capacitor/mediaSaver";
import { pushBackHandler } from "../../capacitor/backButton";
import { composeNotePages } from "../../utils/notePdfPages";
import { noteToMarkdown } from "../../utils/noteMarkdown";
import { collectMatchingCss, cssVarNames, usedFonts, inlineLocalUrls, buildNoteHtmlDocument } from "../../utils/noteHtmlExport";
import { isDarkNoteBg, noteBackground } from "../../utils/noteInk";
import { CONFIRM_STACK, CONFIRM_DANGER, CONFIRM_CANCEL, CONFIRM_PRIMARY, CONFIRM_COLUMN } from "../../theme/sharedStyles";
import ConfirmHeader from "../../components/ConfirmHeader";
import { DOODLE_STICKERS, doodleSrc, doodlePlaceWidth } from "../../utils/doodleStickers";
import { toLocalDelta, anchoredTopLeft, cornerScale, makeRotation, normalizeDeg } from "../../utils/stickerGesture";
import { WASHI_TAPES, TAPE_OPACITY, TAPE_DEFAULT_W, TAPE_DEFAULT_H, clampTapeThickness, clampTapeLength, tapeSrc, tapeClipPath, drawTape } from "../../utils/washiTape";

// The ready-made Washi tape and Doodle sets in the sticker panel are hidden for
// now (Jade, 2026-10-26). The code and files stay; true shows them again.
const SHOW_STICKER_SETS = false;

// Unlike the web overlay model (VirtualKeyboard API keeps the viewport full-
// size and the keyboard floats over it, so toolbars must be manually pushed
// up by the keyboard's height), the native app's WebView viewport genuinely
// shrinks when the keyboard opens (confirmed on-device: innerHeight drops
// from 857 to 541 with a 316px keyboard) — env(keyboard-inset-height) isn't
// available, so we rely on that real resize instead. That means any fixed-
// position "clear the keyboard" offset must be 0 here, not the measured
// keyboard height, or it double-counts a gap the resize already created.
const isNative = Capacitor.isNativePlatform();

function AlignIcon({ mode = "left", size = 15 }) {
  const W = 16, pad = 2.5, full = W - 2 * pad, short = full * 0.62;
  const rows = [full, short, full, short];
  const ys = [3, 6.4, 9.8, 13.2];
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" style={{ display:"block" }}>
      {ys.map((y, i) => {
        const w = mode === "justify" ? full : rows[i];
        let x = pad;
        if (mode === "center") x = (W - w) / 2;
        else if (mode === "right") x = W - pad - w;
        return <rect key={i} x={x} y={y} width={w} height={1.7} rx={0.85} fill="currentColor" />;
      })}
    </svg>
  );
}

// ── StickerMaker — make a custom sticker from a photo by free-hand tracing ───
// `editing`: re-tracing a sticker already on the note. The photo it was cut
// from opens straight away (never the gallery) and there's no Change photo.
// `initialTrace`: the sticker's current outline (fractions of the photo), drawn
// on open so Edit shows its shape; Clear trace then brings back the whole photo.
function StickerMaker({ onClose, onCreate, initialSrc, editing, initialTrace }) {
  const [imgSrc, setImgSrc]   = useState(initialSrc || null);
  const [imgDims, setImgDims] = useState(null);
  const [points, setPoints]   = useState([]);
  const [drawing, setDrawing] = useState(false);
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState("");
  const fileRef   = useRef(null);
  const imgElRef  = useRef(null);
  const objUrlRef = useRef(null);
  const overlayRef = useRef(null);
  const wrapRef   = useRef(null);

  useEffect(() => { if (!initialSrc) fileRef.current?.click(); }, [initialSrc]);
  const traceAppliedRef = useRef(false);
  useEffect(() => {
    if (!imgDims || traceAppliedRef.current || imgSrc !== initialSrc) return;
    traceAppliedRef.current = true;
    if (Array.isArray(initialTrace) && initialTrace.length >= 3)
      setPoints(initialTrace.map(([x, y]) => [x * imgDims.dispW, y * imgDims.dispH]));
  }, [imgDims, imgSrc, initialSrc, initialTrace]);
  useEffect(() => () => { if (objUrlRef.current) URL.revokeObjectURL(objUrlRef.current); }, []);

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setErr(""); setPoints([]); setImgDims(null);
    if (objUrlRef.current) URL.revokeObjectURL(objUrlRef.current);
    const url = URL.createObjectURL(f);
    objUrlRef.current = url;
    setImgSrc(url);
  };

  const onImgLoad = (e) => {
    const im = e.currentTarget;
    imgElRef.current = im;
    const maxW = Math.min(window.innerWidth - 32, 560);
    const maxH = window.innerHeight - 220;
    const r = Math.min(maxW / im.naturalWidth, maxH / im.naturalHeight, 1);
    setImgDims({ natW: im.naturalWidth, natH: im.naturalHeight,
      dispW: Math.round(im.naturalWidth * r), dispH: Math.round(im.naturalHeight * r) });
  };

  const onImgError = () => {
    if (objUrlRef.current) { URL.revokeObjectURL(objUrlRef.current); objUrlRef.current = null; }
    setImgSrc(null);
    setErr("Couldn't load that photo. If it's a OneDrive cloud file, right-click it → \"Always keep on this device,\" then try again.");
  };

  const evtPoint = (e) => {
    const rect = overlayRef.current.getBoundingClientRect();
    return [Math.max(0, Math.min(rect.width, e.clientX - rect.left)),
            Math.max(0, Math.min(rect.height, e.clientY - rect.top))];
  };

  const startDraw = (e) => { e.preventDefault(); setDrawing(true); setPoints([evtPoint(e)]); };
  const moveDraw  = (e) => { if (!drawing) return; e.preventDefault(); setPoints(p => [...p, evtPoint(e)]); };
  const endDraw   = () => setDrawing(false);

  useEffect(() => {
    const cv = overlayRef.current;
    if (!cv || !imgDims) return;
    const dpr = window.devicePixelRatio || 1;
    if (cv.width !== imgDims.dispW * dpr) { cv.width = imgDims.dispW * dpr; cv.height = imgDims.dispH * dpr; }
    const ctx = cv.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, imgDims.dispW, imgDims.dispH);
    if (points.length < 1) return;
    ctx.save();
    ctx.fillStyle = "rgba(40,10,30,0.45)";
    ctx.fillRect(0, 0, imgDims.dispW, imgDims.dispH);
    if (points.length >= 3) {
      ctx.globalCompositeOperation = "destination-out";
      ctx.beginPath();
      points.forEach((p, i) => i ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1]));
      ctx.closePath(); ctx.fill();
    }
    ctx.restore();
    ctx.beginPath();
    points.forEach((p, i) => i ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1]));
    if (!drawing && points.length >= 3) ctx.closePath();
    ctx.lineWidth = 2.5; ctx.strokeStyle = "#ff7ac0"; ctx.setLineDash([7, 5]);
    ctx.lineJoin = "round"; ctx.lineCap = "round"; ctx.stroke();
  }, [points, imgDims, drawing]);

  const makeSticker = async () => {
    if (points.length < 3 || !imgElRef.current || !imgDims) return;
    setBusy(true); setErr("");
    try {
      const cap = 1200;
      const f = Math.min(cap / imgDims.natW, cap / imgDims.natH, 1);
      const outScale = (imgDims.natW * f) / imgDims.dispW;
      const pts = points.map(([x, y]) => [x * outScale, y * outScale]);
      const xs = pts.map(p => p[0]), ys = pts.map(p => p[1]);
      const minX = Math.max(0, Math.floor(Math.min(...xs))), minY = Math.max(0, Math.floor(Math.min(...ys)));
      const w = Math.max(1, Math.ceil(Math.max(...xs)) - minX);
      const h = Math.max(1, Math.ceil(Math.max(...ys)) - minY);
      const out = document.createElement("canvas");
      out.width = w; out.height = h;
      const octx = out.getContext("2d");
      octx.beginPath();
      pts.forEach((p, i) => i ? octx.lineTo(p[0] - minX, p[1] - minY) : octx.moveTo(p[0] - minX, p[1] - minY));
      octx.closePath(); octx.clip();
      octx.drawImage(imgElRef.current, -minX, -minY, imgDims.natW * f, imgDims.natH * f);
      // Hand back the photo it was traced on too, so Edit can re-open it for a
      // new trace. Unchanged photo = the same string; a newly picked one (an
      // object URL, gone after this session) is saved as a JPEG at the size
      // the cut-out is made from.
      let source = imgSrc;
      if (imgSrc !== initialSrc || !/^(data:|https?:)/.test(imgSrc || "")) {
        const sc = document.createElement("canvas");
        sc.width = Math.round(imgDims.natW * f); sc.height = Math.round(imgDims.natH * f);
        sc.getContext("2d").drawImage(imgElRef.current, 0, 0, sc.width, sc.height);
        source = sc.toDataURL("image/jpeg", 0.85);
      }
      // The outline as fractions of the photo, so the next Edit can draw it
      // again. Thinned to at most ~150 points and 3 decimals: it syncs in the note.
      const step = Math.max(1, Math.ceil(points.length / 150));
      const trace = points.filter((_, i) => i % step === 0)
        .map(([x, y]) => [Math.round(x / imgDims.dispW * 1000) / 1000, Math.round(y / imgDims.dispH * 1000) / 1000]);
      onCreate(out.toDataURL("image/png"), { w, h }, source, trace);
      onClose();
    } catch (e) {
      console.error(e);
      setErr("Couldn't process that. Try tracing again.");
      setBusy(false);
    }
  };

  return createPortal((
    <div style={{ position:"fixed", top:0, left:0, right:0, bottom:0, width:"100vw", height:"100dvh", zIndex:2147483647, background:"rgba(20,6,16,0.92)", display:"flex", flexDirection:"column", color:"#fff", paddingTop:"var(--sat, 0px)", paddingBottom:"var(--sab, 0px)" }}>
      {/* No `accept` filter: OneDrive "online-only" files can't be picked when a
          filter is set (Windows greys out Open until they're downloaded). */}
      <input ref={fileRef} type="file" style={{ display:"none" }} onChange={onPickFile} />

      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", gap:10, padding:"14px 16px", flexShrink:0 }}>
        <button onClick={onClose} style={{ background:"rgba(255,255,255,0.12)", border:"none", color:"#fff", borderRadius:10, padding:"8px 14px", cursor:"pointer", fontFamily:"inherit", fontSize:13, fontWeight:700 }}>Cancel</button>
        <span style={{ flex:1, textAlign:"center", fontSize:14, fontWeight:800 }}>Make a sticker</span>
        <button onClick={makeSticker} disabled={points.length < 3 || busy}
          style={{ background: points.length>=3 && !busy ? "#ff7ac0" : "rgba(255,255,255,0.12)", border:"none", color:"#fff", borderRadius:10, padding:"8px 16px", cursor: points.length>=3 && !busy ? "pointer" : "default", fontFamily:"inherit", fontSize:13, fontWeight:800 }}>Done</button>
      </div>

      {/* Stage */}
      <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"0 16px", minHeight:0 }}>
        {!imgSrc ? (
          <button onClick={()=>fileRef.current?.click()} style={{ background:"rgba(255,255,255,0.1)", border:"1.5px dashed rgba(255,255,255,0.4)", color:"#fff", borderRadius:16, padding:"40px 32px", cursor:"pointer", fontFamily:"inherit", fontSize:14, fontWeight:700 }}>
            📷 Choose a photo
          </button>
        ) : (
          <div ref={wrapRef} style={{ position:"relative", ...(imgDims ? { width:imgDims.dispW, height:imgDims.dispH } : {}), maxWidth:"100%", touchAction:"none", borderRadius:10, overflow:"hidden", boxShadow:"0 8px 40px rgba(0,0,0,0.5)" }}>
            {/* crossOrigin for a hosted photo (Cloudinary sends CORS headers):
                without it the canvas is tainted and Done can't read the cut-out. */}
            <img src={imgSrc} alt="" draggable={false} onLoad={onImgLoad} onError={onImgError}
              crossOrigin={/^https?:/.test(imgSrc) ? "anonymous" : undefined}
              style={{ display:"block", userSelect:"none", pointerEvents:"none",
                ...(imgDims ? { width:"100%", height:"100%" }
                  : { width:"auto", height:"auto", maxWidth:Math.min(window.innerWidth-32,560), maxHeight:window.innerHeight-220 }) }} />
            {imgDims && (
              <canvas ref={overlayRef}
                style={{ position:"absolute", top:0, left:0, width:"100%", height:"100%", cursor:"crosshair", touchAction:"none" }}
                onPointerDown={startDraw} onPointerMove={moveDraw} onPointerUp={endDraw} onPointerLeave={endDraw} />
            )}
          </div>
        )}
        {imgSrc && <div style={{ marginTop:14, fontSize:12, color:"rgba(255,255,255,0.75)", textAlign:"center", maxWidth:420 }}>Drag your finger around what you want to keep. Lift to finish, then tap <b>Done</b>.</div>}
        {err && <div style={{ marginTop:10, fontSize:12, color:"#ffb3c7", textAlign:"center" }}>{err}</div>}
      </div>

      {/* Controls */}
      <div style={{ flexShrink:0, padding:"12px 16px calc(16px + env(safe-area-inset-bottom))", display:"flex", alignItems:"center", justifyContent:"center", gap:14 }}>
        {imgSrc && <button onClick={()=>setPoints([])} style={{ background:"rgba(255,255,255,0.12)", border:"none", color:"#fff", borderRadius:9, padding:"7px 14px", cursor:"pointer", fontFamily:"inherit", fontSize:12, fontWeight:700 }}>Clear trace</button>}
        {imgSrc && !editing && <button onClick={()=>fileRef.current?.click()} style={{ background:"rgba(255,255,255,0.12)", border:"none", color:"#fff", borderRadius:9, padding:"7px 14px", cursor:"pointer", fontFamily:"inherit", fontSize:12, fontWeight:700 }}>Change photo</button>}
      </div>

      {busy && (
        <div style={{ position:"absolute", top:0, left:0, right:0, bottom:0, background:"rgba(20,6,16,0.8)", display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ width:44, height:44, border:"4px solid rgba(255,255,255,0.2)", borderTopColor:"#ff7ac0", borderRadius:"50%", animation:"spin 0.9s linear infinite" }} />
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </div>
      )}
    </div>
  ), document.body);
}

// ── CropModal — non-destructive crop. Returns a normalized {x,y,w,h} (0..1)
//    rectangle over the ORIGINAL image, so nothing is ever thrown away and the
//    crop can be reset/undone at any time. ───────────────────────────────────
function CropModal({ src, initialCrop, defaultCrop, onClose, onDone }) {
  const [dims, setDims] = useState(null);   // {dispW,dispH}
  const [rect, setRect] = useState(null);   // crop box in display px {x,y,w,h}
  const imgRef = useRef(null);
  const drag = useRef(null);

  const normToRect = (nc, dispW, dispH) => ({
    x: nc.x * dispW, y: nc.y * dispH, w: nc.w * dispW, h: nc.h * dispH,
  });

  const onLoad = (e) => {
    const im = e.currentTarget; imgRef.current = im;
    const maxW = Math.min(window.innerWidth - 32, 560);
    const maxH = window.innerHeight - 220;
    const r = Math.min(maxW / im.naturalWidth, maxH / im.naturalHeight, 1);
    const dispW = Math.round(im.naturalWidth * r), dispH = Math.round(im.naturalHeight * r);
    setDims({ dispW, dispH });
    const nc = initialCrop && initialCrop.w ? initialCrop : { x:0, y:0, w:1, h:1 };
    setRect(normToRect(nc, dispW, dispH));
  };

  const onMove = (e) => {
    const d = drag.current; if (!d || !dims) return;
    const dx = e.clientX - d.sx, dy = e.clientY - d.sy;
    const W = dims.dispW, H = dims.dispH, MIN = 24;
    let { x, y, w, h } = d.r;
    if (d.mode === "move") {
      x = Math.max(0, Math.min(W - w, x + dx));
      y = Math.max(0, Math.min(H - h, y + dy));
    } else {
      if (d.mode.includes("e")) w = Math.max(MIN, Math.min(W - x, w + dx));
      if (d.mode.includes("s")) h = Math.max(MIN, Math.min(H - y, h + dy));
      if (d.mode.includes("w")) { const nx = Math.max(0, Math.min(x + w - MIN, x + dx)); w += x - nx; x = nx; }
      if (d.mode.includes("n")) { const ny = Math.max(0, Math.min(y + h - MIN, y + dy)); h += y - ny; y = ny; }
    }
    setRect({ x, y, w, h });
  };
  const onUp = () => { drag.current = null; window.removeEventListener("pointermove", onMove); window.removeEventListener("pointerup", onUp); };
  const startDrag = (mode) => (e) => {
    e.preventDefault(); e.stopPropagation();
    drag.current = { mode, sx: e.clientX, sy: e.clientY, r: { ...rect } };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const apply = () => {
    if (!dims || !rect) return;
    onDone({
      x: rect.x / dims.dispW, y: rect.y / dims.dispH,
      w: rect.w / dims.dispW, h: rect.h / dims.dispH,
    });
    onClose();
  };
  const reset = () => {
    if (!dims) return;
    setRect(normToRect(defaultCrop || { x:0, y:0, w:1, h:1 }, dims.dispW, dims.dispH));
  };

  const corner = (mode, pos) => (
    <div onPointerDown={startDrag(mode)}
      style={{ position:"absolute", ...pos, width:18, height:18, borderRadius:"50%", background:"#ff7ac0", border:"2px solid #fff", boxShadow:"0 1px 4px rgba(0,0,0,0.4)", cursor:"pointer", touchAction:"none" }} />
  );

  return createPortal((
    <div style={{ position:"fixed", inset:0, width:"100vw", height:"100dvh", zIndex:2147483647, background:"rgba(20,6,16,0.92)", display:"flex", flexDirection:"column", color:"#fff", paddingTop:"var(--sat, 0px)", paddingBottom:"var(--sab, 0px)" }}>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", gap:10, padding:"14px 16px", flexShrink:0 }}>
        <button onClick={onClose} style={{ background:"rgba(255,255,255,0.12)", border:"none", color:"#fff", borderRadius:10, padding:"8px 14px", cursor:"pointer", fontFamily:"inherit", fontSize:13, fontWeight:700 }}>Cancel</button>
        <span style={{ flex:1, textAlign:"center", fontSize:14, fontWeight:800 }}>Crop image</span>
        <button onClick={reset} style={{ background:"rgba(255,255,255,0.12)", border:"none", color:"#fff", borderRadius:10, padding:"8px 14px", cursor:"pointer", fontFamily:"inherit", fontSize:13, fontWeight:700 }}>Reset</button>
        <button onClick={apply} disabled={!rect}
          style={{ background:"#ff7ac0", border:"none", color:"#fff", borderRadius:10, padding:"8px 16px", cursor:"pointer", fontFamily:"inherit", fontSize:13, fontWeight:800 }}>Done</button>
      </div>

      {/* Stage */}
      <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", padding:"0 16px", minHeight:0 }}>
        <div style={{ position:"relative", ...(dims ? { width:dims.dispW, height:dims.dispH } : {}), touchAction:"none", borderRadius:8, overflow:"hidden", boxShadow:"0 8px 40px rgba(0,0,0,0.5)" }}>
          <img src={src} alt="" draggable={false} onLoad={onLoad}
            style={{ display:"block", width: dims ? dims.dispW : "auto", height: dims ? dims.dispH : "auto", userSelect:"none", pointerEvents:"none" }} />
          {rect && (
            <div onPointerDown={startDrag("move")}
              style={{ position:"absolute", left:rect.x, top:rect.y, width:rect.w, height:rect.h, boxShadow:"0 0 0 9999px rgba(20,6,16,0.55)", border:"1.5px solid #ff7ac0", cursor:"move", touchAction:"none" }}>
              {corner("nw", { top:-9, left:-9 })}
              {corner("ne", { top:-9, right:-9 })}
              {corner("sw", { bottom:-9, left:-9 })}
              {corner("se", { bottom:-9, right:-9 })}
            </div>
          )}
        </div>
      </div>
      <div style={{ textAlign:"center", fontSize:12, color:"rgba(255,255,255,0.7)", padding:"10px 16px 16px" }}>
        Drag the corners to set the crop, drag the middle to move it ✨
      </div>
    </div>
  ), document.body);
}

// ── Frame helpers — shared by the sticker layer and the crop modal ──────────

function frameBoxAspect(frame) {
  const f = frame || "none";
  if (f === "circle" || f === "square" || f === "heart" || f === "star") return 1;
  if (f === "oblong") return 1.5;
  return null; // null → use the image's natural aspect
}
// The crop a frame uses before the user crops anything (emulates object-fit:cover).
function defaultCropFor(frame, aspect) {
  const A = aspect || 1;
  const T = frameBoxAspect(frame);
  if (T == null) return { x:0, y:0, w:1, h:1 };
  if (A >= T) { const w = T / A; return { x:(1 - w)/2, y:0, w, h:1 }; }
  const h = A / T;            return { x:0, y:(1 - h)/2, w:1, h };
}
// clip-path / radius decoration for a framed image box
function frameClip(frame) {
  const f = frame || "none";
  if (f === "none" || f === "rectangle") return {};
  if (f === "rounded") return { borderRadius:14 };
  const shape = {
    square: "inset(0)",
    circle: "circle(50%)",
    oblong: "ellipse(50% 50%)",
    heart:  "url(#cj-frame-heart)",
    star:   "polygon(50% 0%,61% 35%,98% 35%,68% 57%,79% 91%,50% 70%,21% 91%,32% 57%,2% 35%,39% 35%)",
  }[f] || "inset(0)";
  return { clipPath:shape, WebkitClipPath:shape };
}

// Sticker/text-box x, y, w, h, size, and fontSize are stored as fractions of
// the note editor's own live width (not raw px), so a layout stays
// proportionally identical across devices with different editor widths —
// something placed near the right edge on a wide desktop editor no longer
// lands off-screen when the same note is reopened on a narrow phone editor,
// and vice versa. STICKER_LEGACY_BASE_W reinterprets notes saved before this
// normalization existed (still holding raw px) as if they'd been saved
// against a ~380px mobile editor, so old notes keep rendering sensibly
// instead of breaking outright; the first gesture that touches one of those
// stickers rewrites it in the new relative form (see `commit` in StickerLayer).
const STICKER_LEGACY_BASE_W = 380;
const relSticker = (s) => {
  if (s.u === "r") return s;
  const b = STICKER_LEGACY_BASE_W;
  const out = { ...s, u: "r" };
  if (out.x != null) out.x = out.x / b;
  if (out.y != null) out.y = out.y / b;
  if (out.w != null) out.w = out.w / b;
  if (out.h != null) out.h = out.h / b;
  if (out.size != null) out.size = out.size / b;
  if (out.fontSize != null) out.fontSize = out.fontSize / b;
  return out;
};

// ── StickerLayer — draggable/resizable/rotatable sticker overlay ─────────────
function StickerLayer({ stickers: rawStickers, selectedId, onSelect, onUpdate, onDelete, onCrop, onEdit, onTextFocus, onTextBlur, containerRef }) {
  const stickers = useMemo(() => rawStickers.map(relSticker), [rawStickers]);
  // Commit a patch as a fully-relative sticker — spreading `s` (already
  // relative, see `stickers` above) guarantees a legacy sticker is migrated
  // in full the moment any gesture touches it, not just the fields that
  // gesture changed.
  const commit = (s, patch) => onUpdate(s.id, { ...s, ...patch, u: "r" });
  // Measure the layer itself (inset 0, so it is exactly the editor's width).
  // It used to measure containerRef, but when a note opens the editor and this
  // layer mount together, and a child's layout effect runs before the parent's
  // ref is attached: containerRef was still null, so cw stayed at the 380
  // fallback on a ~950px desktop editor. Stickers drew at 40% of their size and
  // position, and a drag in that state saved against the wrong width, so they
  // jumped and shrank after switching notes/tabs (Jade, 2026-10-21).
  // Width 0 (a hidden tab) is ignored, so a hidden note never re-scales.
  const layerRef = useRef(null);
  const [cw, setCw] = useState(() => containerRef.current?.clientWidth || STICKER_LEGACY_BASE_W);
  useLayoutEffect(() => {
    const el = layerRef.current;
    if (!el) return;
    const update = () => { const w = el.clientWidth; if (w > 0) setCw(w); };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // Live DOM refs per sticker so gestures can mutate the node directly (no
  // per-frame React state churn — we only commit to state on pointer-up).
  const nodeRefs = useRef({});
  const setNodeRef = (id) => (el) => { if (el) nodeRefs.current[id] = el; else delete nodeRefs.current[id]; };
  const movingRef = useRef(false); // true while a text box is being dragged (suppresses empty-box auto-delete on the drag blur)
  // Touch devices: bigger handles (easier to grab with a finger).
  const coarse = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(pointer: coarse)").matches;
  const HS = coarse ? 30 : 22; // handle size
  const txtBtn = { background:"#a070d0", color:"#fff", border:"none", borderRadius:999, padding:"4px 10px", fontSize:11, fontWeight:700, fontFamily:"inherit", cursor:"pointer", boxShadow:"0 2px 6px rgba(0,0,0,0.25)" };

  // Photos (attach-flow + lasso) carry a natural `aspect` and support crop +
  // free stretch. Legacy graphic / emoji-image stickers don't.
  const isPhoto = (s) => s.type === "image" && s.aspect != null;

  // Box dimensions in px. Explicit w/h (written once the user resizes/stretches)
  // win; otherwise derive from the legacy `size` + frame/aspect.
  const dimsOf = (s) => {
    // Text boxes have an explicit width but grow vertically with their content,
    // so height is just a sensible fallback for gesture math.
    if (s.type === "text") return { w: (s.w != null ? s.w : 180 / STICKER_LEGACY_BASE_W) * cw, h: textFontPx(s) * 1.6 };
    if (s.w != null && s.h != null) return { w: s.w * cw, h: s.h * cw };
    const wpx = (s.size != null ? s.size : 120 / STICKER_LEGACY_BASE_W) * cw;
    if (s.type !== "image") return { w: wpx, h: wpx };
    const ba = frameBoxAspect(s.frame) ?? (s.aspect || 1);
    return { w: wpx, h: wpx / ba };
  };

  // Background-image style that shows only the (normalized) crop region of a
  // photo, filling the box — so stretching the box stretches the image and the
  // original is never altered (the crop is just a rectangle).
  const photoBgStyle = (s) => {
    const c = s.crop || defaultCropFor(s.frame, s.aspect || 1);
    const cw = Math.max(0.0001, c.w), ch = Math.max(0.0001, c.h);
    const posX = cw >= 1 ? 0 : (c.x / (1 - cw)) * 100;
    const posY = ch >= 1 ? 0 : (c.y / (1 - ch)) * 100;
    return {
      width:"100%", height:"100%",
      backgroundImage:`url("${s.content}")`,
      backgroundRepeat:"no-repeat",
      backgroundPosition:`${posX}% ${posY}%`,
      backgroundSize:`${100 / cw}% ${100 / ch}%`,
      ...frameClip(s.frame),
    };
  };
  const imgLegacyStyle = (s) => ({ width:"100%", height:"100%", objectFit:"contain", userSelect:"none", pointerEvents:"none", display:"block", ...frameClip(s.frame) });

  // Follow ONE finger (or the mouse) until it lifts (Jade, 2026-10-28). Moves
  // from any other finger are ignored, so a second finger resting on the note
  // can't steer the gesture, and a cancelled touch ends it too, so no listener
  // is left behind to move the sticker on a later touch.
  const track = (e, onMove, onEnd) => {
    const id = e.pointerId;
    const move = (me) => { if (me.pointerId === id) onMove(me); };
    const end = (me) => {
      if (me.pointerId !== id) return;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
      onEnd(me);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", end);
  };

  // A drag that ends off the handle fires a click on the note under the
  // pointer, and the note deselects on click, so the handles vanished after
  // every drag. Swallow that one click (dropped on the next tick if none comes).
  const swallowNextClick = () => {
    const stop = (ev) => { ev.stopPropagation(); ev.preventDefault(); };
    window.addEventListener("click", stop, { capture: true, once: true });
    setTimeout(() => window.removeEventListener("click", stop, { capture: true }), 0);
  };

  // A text box's font size in px. One with no size of its own is 18px, the
  // same number the box draws with, so the first resize starts from what's on
  // screen instead of jumping.
  const textFontPx = (s) => (s.fontSize != null ? s.fontSize * cw : 18);

  // Puts a box at its new size while the point (ax, ay) of it (fractions, 0,0
  // = top-left) stays on the same spot on screen, even when it's tilted.
  // Returns the new top-left. A text box's height follows its text, so it is
  // measured after the width / font size change.
  const placeAnchored = (node, s, o, nw, nhIn, ax, ay) => {
    const nh = nhIn != null ? nhIn : (node ? node.offsetHeight : o.h);
    const p = anchoredTopLeft({ x: o.x, y: o.y, w: o.w, h: o.h, nw, nh, deg: s.rotation || 0, ax, ay });
    if (node) { node.style.left = p.x + "px"; node.style.top = p.y + "px"; }
    return p;
  };

  const onPointerDownBody = (e, s) => {
    e.stopPropagation();
    onSelect(s.id);
    const node = nodeRefs.current[s.id];
    const startX = e.clientX, startY = e.clientY;
    const origX = s.x * cw, origY = s.y * cw;
    let nextX = origX, nextY = origY, raf = 0, moved = false;
    if (s.type === "text") movingRef.current = true; // suppress empty-box auto-delete during a move
    const apply = () => { raf = 0; if (node) { node.style.left = nextX + "px"; node.style.top = nextY + "px"; } };
    track(e, (me) => {
      moved = true;
      nextX = origX + (me.clientX - startX);
      nextY = origY + (me.clientY - startY);
      if (!raf) raf = requestAnimationFrame(apply);
    }, () => {
      if (raf) cancelAnimationFrame(raf);
      if (moved) commit(s, { x: nextX / cw, y: nextY / cw });
      if (s.type === "text") setTimeout(() => { movingRef.current = false; }, 0);
    });
  };

  // Mobile only: drag a text box from ANY part of its body. A plain tap (no
  // movement past the threshold) instead focuses it for typing.
  const onPointerDownTextDrag = (e, s) => {
    e.stopPropagation();
    onSelect(s.id);
    const node = nodeRefs.current[s.id];
    const startX = e.clientX, startY = e.clientY;
    const origX = s.x * cw, origY = s.y * cw;
    let nextX = origX, nextY = origY, raf = 0, moving = false;
    const apply = () => { raf = 0; if (node) { node.style.left = nextX + "px"; node.style.top = nextY + "px"; } };
    track(e, (me) => {
      if (!moving) {
        if (Math.hypot(me.clientX - startX, me.clientY - startY) < 6) return;
        moving = true;
        movingRef.current = true;
        node?.querySelector(".cj-textbox")?.blur();
        window.getSelection?.().removeAllRanges();
      }
      nextX = origX + (me.clientX - startX);
      nextY = origY + (me.clientY - startY);
      if (!raf) raf = requestAnimationFrame(apply);
    }, (me) => {
      if (raf) cancelAnimationFrame(raf);
      if (moving) { commit(s, { x: nextX / cw, y: nextY / cw }); setTimeout(() => { movingRef.current = false; }, 0); }
      else if (me.type === "pointerup") setTimeout(() => nodeRefs.current[s.id]?.querySelector(".cj-textbox")?.focus(), 0);
    });
  };

  // Turns by how far the finger has gone round the middle since it went down,
  // so nothing moves until the finger does (see utils/stickerGesture.js: it
  // used to snap to wherever the finger pointed from the middle).
  const onPointerDownRotate = (e, s) => {
    e.stopPropagation(); e.preventDefault();
    const node = nodeRefs.current[s.id];
    // Center from the node's live bounding box (viewport coords) — correct even
    // when the note is scrolled or the box is rotated/multi-line.
    const nr = node ? node.getBoundingClientRect() : { left: 0, top: 0, width: 0, height: 0 };
    const cx = nr.left + nr.width / 2;
    const cy = nr.top + nr.height / 2;
    const rotationAt = makeRotation(s.rotation || 0, Math.atan2(e.clientY - cy, e.clientX - cx));
    let angle = s.rotation || 0, raf = 0, moved = false;
    const apply = () => { raf = 0; if (node) node.style.transform = `rotate(${angle}deg)`; };
    track(e, (me) => {
      moved = true;
      angle = rotationAt(Math.atan2(me.clientY - cy, me.clientX - cx));
      if (!raf) raf = requestAnimationFrame(apply);
    }, () => {
      if (raf) cancelAnimationFrame(raf);
      if (!moved) return;
      swallowNextClick();
      commit(s, { rotation: normalizeDeg(angle) });
    });
  };

  // Proportional resize from the bottom-right corner (keeps aspect). The
  // top-left corner stays put on screen, even on a tilted sticker.
  const onPointerDownResize = (e, s) => {
    e.stopPropagation(); e.preventDefault();
    const node = nodeRefs.current[s.id];
    const startX = e.clientX, startY = e.clientY;
    const { w: ow, h: oh } = dimsOf(s);
    const o = { x: s.x * cw, y: s.y * cw, w: ow, h: oh };
    const ar = ow / oh || 1;
    let w = ow, h = oh, pos = { x: o.x, y: o.y }, raf = 0, moved = false;
    const apply = () => { raf = 0; if (node) { node.style.width = w + "px"; node.style.height = h + "px"; pos = placeAnchored(node, s, o, w, h, 0, 0); } };
    track(e, (me) => {
      moved = true;
      const l = toLocalDelta(me.clientX - startX, me.clientY - startY, s.rotation);
      w = Math.max(32, Math.min(900, ow * cornerScale(ow, oh, l.x, l.y)));
      h = w / ar;
      if (!raf) raf = requestAnimationFrame(apply);
    }, () => {
      if (raf) { cancelAnimationFrame(raf); apply(); }
      if (!moved) return;
      swallowNextClick();
      commit(s, { w: w / cw, h: h / cw, x: pos.x / cw, y: pos.y / cw });
    });
  };

  // Washi tape corner handle: thickness only. The length stays where it was set
  // (that's the end grips' job) and the pattern scales with the thickness.
  const onPointerDownTapeThickness = (e, s) => {
    e.stopPropagation(); e.preventDefault();
    const node = nodeRefs.current[s.id];
    const startX = e.clientX, startY = e.clientY;
    const { h: oh } = dimsOf(s);
    const oy = s.y * cw;
    // Grows from its middle line, so a tilted tape doesn't drift sideways.
    let h = oh, y = oy, raf = 0;
    const apply = () => { raf = 0; if (node) { node.style.height = h + "px"; node.style.top = y + "px"; } };
    track(e, (me) => {
      // Across the tape's own direction, so a tilted tape still thickens when
      // the handle is pulled away from it.
      const l = toLocalDelta(me.clientX - startX, me.clientY - startY, s.rotation);
      h = clampTapeThickness(oh + l.y);
      y = oy - (h - oh) / 2;
      if (!raf) raf = requestAnimationFrame(apply);
    }, () => {
      if (raf) cancelAnimationFrame(raf);
      swallowNextClick();
      commit(s, { h: h / cw, y: y / cw });
    });
  };

  // Washi tape end grips: length only. Works along the tape's own direction and
  // keeps the other end where it is on screen, even when the tape is tilted
  // (a plain width change would grow it from its box's corner and, once
  // rotated, slide the far end too).
  const onPointerDownTapeLength = (e, s, dir) => {
    e.stopPropagation(); e.preventDefault();
    const node = nodeRefs.current[s.id];
    const startX = e.clientX, startY = e.clientY;
    const { w: ow, h } = dimsOf(s);
    const rad = (s.rotation || 0) * Math.PI / 180;
    const ux = Math.cos(rad), uy = Math.sin(rad);
    const cx = s.x * cw + ow / 2, cy = s.y * cw + h / 2;
    const sign = dir === "e" ? 1 : -1;
    // The end that stays put.
    const ax = cx - sign * ux * ow / 2, ay = cy - sign * uy * ow / 2;
    let w = ow, x = s.x * cw, y = s.y * cw, raf = 0;
    const apply = () => { raf = 0; if (node) { node.style.width = w + "px"; node.style.left = x + "px"; node.style.top = y + "px"; } };
    track(e, (me) => {
      const along = (me.clientX - startX) * ux + (me.clientY - startY) * uy;
      w = clampTapeLength(ow + sign * along);
      const ncx = ax + sign * ux * w / 2, ncy = ay + sign * uy * w / 2;
      x = ncx - w / 2; y = ncy - h / 2;
      if (!raf) raf = requestAnimationFrame(apply);
    }, () => {
      if (raf) cancelAnimationFrame(raf);
      swallowNextClick();
      commit(s, { w: w / cw, x: x / cw, y: y / cw });
    });
  };

  // Text box corner resize — scales the box width AND the font size together,
  // so the whole block grows/shrinks proportionally. The top-left corner stays
  // put on screen, even on a tilted box.
  const onPointerDownTextScale = (e, s) => {
    e.stopPropagation(); e.preventDefault();
    const node = nodeRefs.current[s.id];
    const ed = node?.querySelector(".cj-textbox");
    const startX = e.clientX, startY = e.clientY;
    const ow = (s.w != null ? s.w : 180 / STICKER_LEGACY_BASE_W) * cw, ofs = textFontPx(s);
    const oh = node ? node.offsetHeight : dimsOf(s).h;
    const o = { x: s.x * cw, y: s.y * cw, w: ow, h: oh };
    let w = ow, fs = ofs, pos = { x: o.x, y: o.y }, raf = 0, moved = false;
    const apply = () => { raf = 0; if (node) { node.style.width = w + "px"; if (ed) ed.style.fontSize = fs + "px"; pos = placeAnchored(node, s, o, w, null, 0, 0); } };
    track(e, (me) => {
      moved = true;
      const l = toLocalDelta(me.clientX - startX, me.clientY - startY, s.rotation);
      w = Math.max(48, Math.min(1000, ow * cornerScale(ow, oh, l.x, l.y)));
      fs = Math.max(8, Math.min(200, ofs * (w / ow)));
      if (!raf) raf = requestAnimationFrame(apply);
    }, () => {
      if (raf) { cancelAnimationFrame(raf); apply(); }
      if (!moved) return;
      swallowNextClick();
      commit(s, { w: w / cw, fontSize: fs / cw, x: pos.x / cw, y: pos.y / cw });
    });
  };

  // Non-uniform stretch from an edge handle. dir ∈ "e","w","n","s". Reads the
  // finger along the sticker's own sides and keeps the opposite edge put on
  // screen, so a tilted one stretches the way the handle is pulled.
  const onPointerDownStretch = (e, s, dir) => {
    e.stopPropagation(); e.preventDefault();
    const node = nodeRefs.current[s.id];
    const isText = s.type === "text";
    const startX = e.clientX, startY = e.clientY;
    const { w: ow, h: dh } = dimsOf(s);
    // A text box's height follows its text: measure it.
    const oh = isText && node ? node.offsetHeight : dh;
    const o = { x: s.x * cw, y: s.y * cw, w: ow, h: oh };
    const horiz = dir === "e" || dir === "w";
    // The point that stays put: the opposite edge (a text box keeps its top
    // line, since its height changes as the text re-wraps).
    const ax = dir === "e" ? 0 : dir === "w" ? 1 : 0.5;
    const ay = dir === "s" ? 0 : dir === "n" ? 1 : (isText ? 0 : 0.5);
    let w = ow, h = oh, pos = { x: o.x, y: o.y }, raf = 0, moved = false;
    const apply = () => { raf = 0; if (!node) return;
      if (horiz) node.style.width = w + "px"; else node.style.height = h + "px";
      pos = placeAnchored(node, s, o, w, isText ? null : h, ax, ay);
    };
    track(e, (me) => {
      moved = true;
      const l = toLocalDelta(me.clientX - startX, me.clientY - startY, s.rotation);
      if (dir === "e") w = Math.max(32, Math.min(1200, ow + l.x));
      if (dir === "w") w = Math.max(32, Math.min(1200, ow - l.x));
      if (dir === "s") h = Math.max(32, Math.min(1200, oh + l.y));
      if (dir === "n") h = Math.max(32, Math.min(1200, oh - l.y));
      if (!raf) raf = requestAnimationFrame(apply);
    }, () => {
      if (raf) { cancelAnimationFrame(raf); apply(); }
      if (!moved) return;
      swallowNextClick();
      commit(s, { ...(horiz ? { w: w / cw } : { h: h / cw }), x: pos.x / cw, y: pos.y / cw });
    });
  };

  const stretchBar = (s, dir, pos, vertical) => {
    const thick = coarse ? 11 : 6, long = coarse ? 34 : 26; // keep `long` in step with barLong below
    return (
      <div onPointerDown={e=>onPointerDownStretch(e, s, dir)} title="Stretch"
        style={{ position:"absolute", ...pos, width: vertical ? thick : long, height: vertical ? long : thick, borderRadius:6, background:"#a070d0", border:"1.5px solid #fff", boxShadow:"0 1px 4px rgba(0,0,0,0.3)", cursor: vertical ? "ew-resize" : "ns-resize", zIndex:2, touchAction:"none" }} />
    );
  };

  return (
    <div ref={layerRef} className="cj-sticker-layer" style={{ position:"absolute", inset:0, pointerEvents:"none", zIndex:10 }}
      onClick={(e) => { if (e.target === e.currentTarget) onSelect(null); }}>
      <style>{`.cj-textbox:empty:before{content:attr(data-ph);color:#bca6c8;}`}</style>
      {stickers.map(s => {
        const isSelected = s.id === selectedId;
        const { w, h } = dimsOf(s);
        const photo = isPhoto(s);
        const isText = s.type === "text";
        const isTape = s.type === "tape";
        // A tape can be thinner than two handles, so its corner buttons sit
        // just above / below it instead of on its corners.
        const hv = isTape ? -(HS + 4) : -HS/2;
        // Where an edge's stretch bar would cover a corner button, the button
        // moves out beside its corner (Jade, 2026-10-28: on a one-line text box
        // the width bars sat over Rotate / Resize / Delete, so a finger aimed at
        // Rotate could grab the width instead). The side bars reach the corners
        // on a short box, the bottom bar on a narrow photo.
        const barLong = coarse ? 34 : 26;
        const sideOut = isText || (photo && h < barLong + HS);
        const hx = sideOut ? -(HS + 4) : -HS/2;
        const hvb = photo && !sideOut && w < barLong + HS ? -(HS + 4) : hv;
        return (
          <div key={s.id} ref={setNodeRef(s.id)} style={{
            position:"absolute", left: s.x * cw, top: s.y * cw,
            width: w, height: isText ? "auto" : h,
            transform: `rotate(${s.rotation || 0}deg)`,
            pointerEvents:"auto", cursor: isText ? "text" : isSelected ? "move" : "pointer",
            userSelect: isText ? "text" : "none",
            // Mobile text boxes capture the touch so they drag from anywhere;
            // desktop text boxes stay "auto" so the body text is selectable.
            // A sticker / photo moves only once it's selected (Jade, 2026-10-17):
            // a big one used to get dragged by a swipe meant to scroll the note,
            // so until it's tapped it lets the swipe scroll ("pan-x pan-y").
            touchAction: isText ? (coarse ? "none" : "auto") : isSelected ? "none" : "pan-x pan-y",
          }}
            onClick={e => { e.stopPropagation(); if (!isText && !isSelected) onSelect(s.id); }}
            // Desktop text boxes move only via the dashed border (so the body
            // text stays selectable). On mobile (touch), the whole body drags —
            // a plain tap still focuses it for typing.
            onPointerDown={e => {
              if (isText) {
                if (coarse) onPointerDownTextDrag(e, s);
                else { e.stopPropagation(); onSelect(s.id); }
              } else if (isSelected) onPointerDownBody(e, s);
              else e.stopPropagation(); // not selected: no drag; a tap selects it (onClick)
            }}>

            {/* Sticker content */}
            {isText
              ? <div className="cj-textbox" data-ph="Type here…" data-sid={s.id}
                  contentEditable suppressContentEditableWarning
                  ref={el => {
                    if (!el || document.activeElement === el) return;
                    if (s.html != null) { if (el.innerHTML !== s.html) el.innerHTML = s.html; }
                    else { const t = s.text || ""; if (el.innerText !== t) el.innerText = t; }
                  }}
                  // Save on every keystroke so the stored state is always current —
                  // otherwise a re-render (e.g. the mobile keyboard closing) could
                  // reset the box from stale/empty state and wipe what was typed.
                  onInput={e => commit(s, { html: e.currentTarget.innerHTML, text: e.currentTarget.innerText })}
                  onFocus={() => onTextFocus?.()}
                  onBlur={e => {
                    onTextBlur?.();
                    const el = e.currentTarget;
                    if (!el.innerText.trim() && !movingRef.current) onDelete(s.id); // tidy up empty boxes
                    else commit(s, { html: el.innerHTML, text: el.innerText });
                  }}
                  style={{ width:"100%", minHeight:"1.2em", outline:"none", fontSize: textFontPx(s), color: s.color || "var(--cj-tb-ink, #3a2535)", fontWeight: s.bold ? 800 : 500, lineHeight:1.35, whiteSpace:"pre-wrap", wordBreak:"break-word", fontFamily:"inherit", userSelect:"text", cursor:"text", padding:2 }} />
              : s.type === "tape"
                ? <div className="cj-tape" data-tape={s.tape} data-seed={s.id} style={{
                    width:"100%", height:"100%", opacity: TAPE_OPACITY,
                    backgroundImage:`url("${tapeSrc(s.tape)}")`, backgroundRepeat:"repeat-x", backgroundSize:"auto 100%",
                    clipPath: tapeClipPath(s.id, h), WebkitClipPath: tapeClipPath(s.id, h),
                  }} />
              : s.type === "emoji"
                ? <div style={{ width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center", fontSize: Math.min(w, h) * 0.72, lineHeight:1, userSelect:"none" }}>
                    {s.content}
                  </div>
                : photo
                  ? <div style={photoBgStyle(s)} />
                  : <img src={s.content} alt="sticker" draggable={false} style={imgLegacyStyle(s)} />
            }

            {/* Selection border */}
            {isSelected && (
              <div style={{ position:"absolute", inset:-2, border:"2px dashed #a070d0", borderRadius:8, pointerEvents:"none" }} />
            )}

            {/* Handles — only when selected */}
            {isSelected && (<>
              {/* ✕ Delete — top left */}
              <div onPointerDown={e=>{e.stopPropagation(); e.preventDefault(); onDelete(s.id);}}
                style={{ position:"absolute", top:hv, left:hx, width:HS, height:HS, borderRadius:"50%", background:"#f05060", color:"white", display:"flex", alignItems:"center", justifyContent:"center", fontSize:HS*0.55, fontWeight:700, cursor:"pointer", boxShadow:"0 2px 6px rgba(0,0,0,0.25)", zIndex:2, touchAction:"none" }}>
                ✕
              </div>
              {/* ↺ Rotate — top right */}
              <div onPointerDown={e=>onPointerDownRotate(e,s)}
                style={{ position:"absolute", top:hv, right:hx, width:HS, height:HS, borderRadius:"50%", background:"#a070d0", color:"white", display:"flex", alignItems:"center", justifyContent:"center", fontSize:HS*0.58, cursor:"grab", boxShadow:"0 2px 6px rgba(0,0,0,0.25)", zIndex:2, touchAction:"none" }}>
                ↺
              </div>
              {/* ✓ Deselect — bottom left */}
              <div onPointerDown={e=>{e.stopPropagation(); e.preventDefault(); onSelect(null);}}
                style={{ position:"absolute", bottom:hvb, left:hx, width:HS, height:HS, borderRadius:"50%", background:"#3bbf7a", color:"white", display:"flex", alignItems:"center", justifyContent:"center", fontSize:HS*0.55, cursor:"pointer", boxShadow:"0 2px 6px rgba(0,0,0,0.25)", zIndex:2, touchAction:"none" }}>
                ✓
              </div>
              {/* ⤡ Resize — bottom right. Text scales font+box; tape changes only
                  its thickness; others scale proportionally. */}
              <div onPointerDown={e=> isText ? onPointerDownTextScale(e,s) : isTape ? onPointerDownTapeThickness(e,s) : onPointerDownResize(e,s)}
                title={isText ? "Resize (scales text)" : isTape ? "Thickness" : "Resize"}
                style={{ position:"absolute", bottom:hvb, right:hx, width:HS, height:HS, borderRadius:"50%", background:"#a070d0", color:"white", display:"flex", alignItems:"center", justifyContent:"center", fontSize:HS*0.52, cursor:"nwse-resize", boxShadow:"0 2px 6px rgba(0,0,0,0.25)", zIndex:2, touchAction:"none" }}>
                ⤡
              </div>

              {/* Washi tape: a grip past each end changes the length. */}
              {isTape && ["w", "e"].map(dir => (
                <div key={"tl"+dir} onPointerDown={e=>onPointerDownTapeLength(e, s, dir)} title="Length"
                  style={{ position:"absolute", top:"50%", [dir === "w" ? "left" : "right"]: -(coarse ? 22 : 16),
                    width: coarse ? 14 : 10, height: coarse ? 36 : 28, transform:"translateY(-50%)", borderRadius:8,
                    background:"#a070d0", border:"1.5px solid #fff", boxShadow:"0 1px 4px rgba(0,0,0,0.3)",
                    cursor:"ew-resize", zIndex:2, touchAction:"none" }} />
              ))}

              {/* Text box: size · format menu · width handles. Move by dragging the
                  dashed border (the strips below), so the body stays selectable. */}
              {isText && (<>
                {stretchBar(s, "w", { left:-3, top:"50%", transform:"translateY(-50%)" }, true)}
                {stretchBar(s, "e", { right:-3, top:"50%", transform:"translateY(-50%)" }, true)}
                {/* Desktop: drag the dashed border to move (keeps body text
                    selectable). On mobile the whole body drags, so these aren't
                    needed. */}
                {!coarse && [
                  { top:-7, left:10, right:10, height:14 }, { bottom:-7, left:10, right:10, height:14 },
                  { left:-7, top:10, bottom:10, width:14 }, { right:-7, top:10, bottom:10, width:14 },
                ].map((p,i)=>(
                  <div key={"mv"+i} title="Drag to move" onPointerDown={e=>onPointerDownBody(e, s)}
                    style={{ position:"absolute", ...p, cursor:"move", zIndex:1, touchAction:"none" }} />
                ))}
                <div style={{ position:"absolute", top:-(HS+18), left:"50%", transform:"translateX(-50%)", display:"flex", gap:6, zIndex:4 }}>
                  {/* Just size here — bold/italic/underline/strike/color all live in
                      the main toolbar and apply to the highlighted text in this box. */}
                  <button title="Smaller" onMouseDown={e=>{ e.preventDefault(); commit(s, { fontSize: Math.max(10, textFontPx(s) - 2) / cw }); }}
                    style={{ ...txtBtn, fontWeight:800 }}>A−</button>
                  <button title="Bigger" onMouseDown={e=>{ e.preventDefault(); commit(s, { fontSize: Math.min(120, textFontPx(s) + 2) / cw }); }}
                    style={{ ...txtBtn, fontWeight:800, fontSize:13 }}>A+</button>
                </div>
              </>)}

              {/* Edge stretch handles + crop/reset toolbar — photos only */}
              {photo && (<>
                {stretchBar(s, "w", { left:-3, top:"50%", transform:"translateY(-50%)" }, true)}
                {stretchBar(s, "e", { right:-3, top:"50%", transform:"translateY(-50%)" }, true)}
                {stretchBar(s, "s", { bottom:-3, left:"50%", transform:"translateX(-50%)" }, false)}

                {(s.custom ? onEdit : onCrop) && (
                  <div style={{ position:"absolute", top:-(HS+18), left:"50%", transform:"translateX(-50%)", display:"flex", gap:6, zIndex:3 }}>
                    {s.custom ? (
                      <button onPointerDown={e=>{ e.stopPropagation(); e.preventDefault(); onEdit(s); }}
                        style={{ display:"flex", alignItems:"center", gap:4, background:"#a070d0", color:"#fff", border:"none", borderRadius:999, padding:"4px 10px", fontSize:11, fontWeight:700, fontFamily:"inherit", cursor:"pointer", boxShadow:"0 2px 6px rgba(0,0,0,0.25)", whiteSpace:"nowrap" }}>
                        ✏️ Edit
                      </button>
                    ) : (
                      <button onPointerDown={e=>{ e.stopPropagation(); e.preventDefault(); onCrop(s); }}
                        style={{ display:"flex", alignItems:"center", gap:4, background:"#3b9bd0", color:"#fff", border:"none", borderRadius:999, padding:"4px 10px", fontSize:11, fontWeight:700, fontFamily:"inherit", cursor:"pointer", boxShadow:"0 2px 6px rgba(0,0,0,0.25)", whiteSpace:"nowrap" }}>
                        ⛶ Crop
                      </button>
                    )}
                    {s.crop && !s.custom && (
                      <button onPointerDown={e=>{ e.stopPropagation(); e.preventDefault(); commit(s, { crop: null }); }}
                        style={{ display:"flex", alignItems:"center", gap:4, background:"rgba(60,30,50,0.85)", color:"#fff", border:"none", borderRadius:999, padding:"4px 10px", fontSize:11, fontWeight:700, fontFamily:"inherit", cursor:"pointer", boxShadow:"0 2px 6px rgba(0,0,0,0.25)", whiteSpace:"nowrap" }}>
                        ⟲ Reset
                      </button>
                    )}
                  </div>
                )}
              </>)}
            </>)}
          </div>
        );
      })}
    </div>
  );
}


export function JournalTab({ entries = {}, setEntries, categories = [], isDesktop = false, searchSlotNode = null, settingsOpen = false }) {
  const NOTEBOOK_STYLES = {
    plain:   { label:"Plain",         emoji:"📄", lineSpacing:null, bg:(c)=>c },
    wide:    { label:"Wide Ruled",    emoji:"📓", lineSpacing:36,   bg:(c)=>`repeating-linear-gradient(transparent,transparent 35px,#e8c8dc 35px,#e8c8dc 36px) ${c}` },
    college: { label:"College Ruled", emoji:"📘", lineSpacing:28,   bg:(c)=>`repeating-linear-gradient(transparent,transparent 27px,#ddc0d8 27px,#ddc0d8 28px) ${c}` },
    narrow:  { label:"Narrow Ruled",  emoji:"📒", lineSpacing:21,   bg:(c)=>`repeating-linear-gradient(transparent,transparent 20px,#ddc0d8 20px,#ddc0d8 21px) ${c}` },
    dotgrid: { label:"Dot Grid",      emoji:"🔵", lineSpacing:null, bg:(c)=>`radial-gradient(circle,#d4a8c8 1px,transparent 1px) ${c}`, bgSize:"24px 24px" },
    graph:   { label:"Graph",         emoji:"📐", lineSpacing:null, bg:(c)=>`linear-gradient(#e8d0e8 1px,transparent 1px),linear-gradient(90deg,#e8d0e8 1px,transparent 1px) ${c}`, bgSize:"24px 24px" },
  };

  const TEMPLATES = [
    { id:"blank", label:"Blank", emoji:"📄", html:"" },
    { id:"daily", label:"Daily Journal", emoji:"🌤", html:"<h2>Daily Journal</h2><p>🌤 <strong>How I'm feeling today:</strong></p><p><br></p><p>✅ <strong>What I want to accomplish:</strong></p><p><br></p><p>💭 <strong>Thoughts &amp; reflections:</strong></p><p><br></p><p>🙏 <strong>Grateful for:</strong></p><p><br></p>" },
    { id:"meeting", label:"Meeting Notes", emoji:"📋", html:"<h2>Meeting Notes</h2><p><strong>Date:</strong> &nbsp;&nbsp;<strong>Attendees:</strong></p><p><strong>Agenda:</strong></p><p><br></p><p><strong>Key points:</strong></p><p><br></p><p><strong>Action items:</strong></p><p><br></p>" },
    { id:"idea", label:"Idea Dump", emoji:"💡", html:"<h2>Idea Dump</h2><p>✨ <strong>The Idea:</strong></p><p><br></p><p>🔍 <strong>Why it matters:</strong></p><p><br></p><p>🚀 <strong>Next steps:</strong></p><p><br></p>" },
    { id:"todo", label:"To-Do List", emoji:"✅", html:"<h2>To-Do List</h2><p>Priority tasks:</p><p><br></p><p>Nice to have:</p><p><br></p>" },
  ];

  // Text colour and highlight share one palette (Jade, 2026-10-28): the plain
  // note background colours, then the dark ones (for dark notes). The old
  // highlighter brights with no close twin among the note colours stay at the
  // front; the rest of the old set was near-identical to a note pastel.
  const INK_LIGHT_COLORS = ["#fef08a", "#fde68a", "#a5f3fc", "#d1fae5", ...NOTE_COLORS];
  const INK_DARK_COLORS = NOTE_DARK_COLORS;

  const FONT_SIZES = [10,12,13,14,16,18,20,24,28,32,36,48];

  // These localStorage keys are a one-shot "open this note" signal set by an
  // external action (e.g. the "New Note" quick action in the settings drawer)
  // right before navigating to this tab. They're consumed once here and then
  // cleared immediately — otherwise switching away from Journal and back would
  // always reopen whatever note/screen was last active instead of showing the
  // main list first.
  const [screen, setScreen] = useState(() => {
    try {
      const pending = localStorage.getItem("clarity_journal_screen");
      localStorage.removeItem("clarity_journal_screen");
      return pending || "list";
    } catch { return "list"; }
  });
  const [activeNoteId, setActiveNoteId] = useState(() => {
    try {
      const pending = localStorage.getItem("clarity_journal_activeNoteId");
      localStorage.removeItem("clarity_journal_activeNoteId");
      return pending || null;
    } catch { return null; }
  });
  // Desktop "focus mode" — hides the left list pane and centers the note
  // container instead of letting it go full-bleed. Persisted so the choice
  // survives switching notes or leaving/returning to the Journal tab.
  const [deskFocus, setDeskFocusState] = useState(() => {
    try { return localStorage.getItem("clarity_journal_deskFocus") === "1"; } catch { return false; }
  });
  const setDeskFocus = (v) => setDeskFocusState(prev => {
    const next = typeof v === "function" ? v(prev) : v;
    try { localStorage.setItem("clarity_journal_deskFocus", next ? "1" : "0"); } catch {}
    return next;
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [sortMode, setSortMode] = useState("recent");
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const sortBtnRef = useRef(null);
  const [sortMenuPos, setSortMenuPos] = useState({ top: 0, left: 0 });
  const SORT_OPTIONS = [
    { key: "recent", label: "Recent" },
    { key: "oldest", label: "Oldest" },
    { key: "az", label: "A → Z" },
  ];
  const [filterFolder, setFilterFolder] = useState(null);
  const [folderMenuOpen, setFolderMenuOpen] = useState(false);
  const folderBtnRef = useRef(null);
  const [folderMenuPos, setFolderMenuPos] = useState({ top: 0, left: 0 });
  const [showTemplates, setShowTemplates] = useState(false);
  // True only for the frame right after tapping "+" to add a note, so the editor
  // slides up from the bottom on entry. Opening an existing note skips it.
  const [slideInNew, setSlideInNew] = useState(false);
  // PDF export is Premium — every entry point that exports a note as .pdf
  // goes through this gate.
  const { isPremium } = usePremium();
  // ── Stickers / image overlays — movable, resizable, croppable objects ───────
  const [showStickerMaker, setShowStickerMaker] = useState(false);
  const [stickerMakerSrc, setStickerMakerSrc] = useState(null); // preload lasso with a chosen photo
  const editingStickerIdRef = useRef(null); // sticker id being re-edited via StickerMaker
  const editingTraceRef = useRef(null); // that sticker's saved outline, drawn when the maker opens
  // Stickers, lasso cutouts, and text boxes are pinned to the device-size
  // class ("desktop" vs "mobile") they were made on, and only ever render on
  // a matching class — see `dc` on addSticker and the `visibleStickers` filter
  // below. That's a real, permanent limitation (not a bug to eventually
  // fix), so every entry point that adds one of these warns first, via this
  // shared gate — once per device unless the user dismisses it for good.
  // Not `clarity_`-prefixed, so this preference stays local to the device and
  // is never swept into the account sync (see src/firebase/sync.js).
  const OVERLAY_WARNING_DISMISSED_KEY = "clarityJournalOverlayWarnDismissed";
  const [pendingOverlayAction, setPendingOverlayAction] = useState(null); // fn to run on confirm, or null when the modal is closed
  const [overlayWarnDontShowAgain, setOverlayWarnDontShowAgain] = useState(false);
  const requestOverlayAdd = (run) => {
    let dismissed = false;
    try { dismissed = localStorage.getItem(OVERLAY_WARNING_DISMISSED_KEY) === "1"; } catch {}
    if (dismissed) { run(); return; }
    setOverlayWarnDontShowAgain(false);
    setPendingOverlayAction(() => run);
  };
  const confirmOverlayAdd = () => {
    if (overlayWarnDontShowAgain) { try { localStorage.setItem(OVERLAY_WARNING_DISMISSED_KEY, "1"); } catch {} }
    const run = pendingOverlayAction;
    setPendingOverlayAction(null);
    run?.();
  };
  const [pendingImage, setPendingImage] = useState(null); // picked image awaiting an attach choice
  const [imageOptStep, setImageOptStep] = useState("choose"); // "choose" | "frame"
  const [cropSticker, setCropSticker] = useState(null); // image sticker being cropped
  const [showStickerPicker, setShowStickerPicker] = useState(false);
  const [selectedStickerId, setSelectedStickerId] = useState(null);
  // My Stickers — custom cut-outs, persisted in localStorage
  const [myStickers, setMyStickers] = useState(() => {
    try { return JSON.parse(localStorage.getItem("clarity_my_stickers") || "[]"); } catch { return []; }
  });
  // Lasso sticker maker is Premium, decided by the account alone. It used to
  // be grandfathered for anyone with a custom sticker already saved, but that
  // list lives in this phone's localStorage, so any free account on a phone
  // that had ever made one got the maker for good. Stickers already made stay
  // usable, and re-editing one (StickerLayer's onEdit) is never gated — it's
  // not creating a new one.
  // Locked buttons below just show a lock icon and do nothing when tapped —
  // no redirect to the Premium page; the Settings panel's own Upgrade
  // button is the one path there.
  const stickerMakerLocked = !isPremium;
  const openStickerMaker = (src) => {
    if (stickerMakerLocked) return;
    requestOverlayAdd(() => { setStickerMakerSrc(src); setShowStickerMaker(true); });
  };
  const [stickerSelectMode, setStickerSelectMode] = useState(false);
  const [selectedStickerIndices, setSelectedStickerIndices] = useState(new Set());
  const [showDeleteStickersConfirm, setShowDeleteStickersConfirm] = useState(false);
  const [pickTip, setPickTip] = useState(""); // friendly hint when an image won't load
  const showPickTip = (msg) => { setPickTip(msg); setTimeout(() => setPickTip(""), 7000); };
  const ONEDRIVE_TIP = "Couldn't load that image 😔 If it's stored in OneDrive (cloud icon), right-click it in File Explorer → \"Always keep on this device,\" then try again.";
  const [isDirty, setIsDirty] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showBackSaveConfirm, setShowBackSaveConfirm] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [contextDeleteNote, setContextDeleteNote] = useState(null);
  const contextMenuElRef = useRef(null);
  // The menu's height is estimated for the very first paint (avoids a flash),
  // then corrected here against its real measured height — a hardcoded guess
  // drifts on devices with different font scaling and ends up parked far from
  // the card whenever it picks the wrong above/below branch.
  useLayoutEffect(() => {
    if (!contextMenu || !contextMenuElRef.current) return;
    const { rect } = contextMenu;
    const el = contextMenuElRef.current;
    const menuHeight = el.offsetHeight;
    const gap = 2;
    const fitsBelow = rect.bottom + gap + menuHeight <= window.innerHeight;
    const top = fitsBelow ? rect.bottom + gap : Math.max(8, rect.top - gap - menuHeight);
    el.style.top = `${top}px`;
  }, [contextMenu]);
  const [activeFormats, setActiveFormats] = useState({});
  const [fontSize, setFontSize] = useState(16);
  const [showFontDd, setShowFontDd] = useState(false);
  const [showHeadingDd, setShowHeadingDd] = useState(false);
  const [showStyleDd, setShowStyleDd] = useState(false);
  const editorAreaRef = useRef(null);
  const [showBgPanel, setShowBgPanel] = useState(false);
  const [showHighlightDd, setShowHighlightDd] = useState(false);
  const [showTextColorDd, setShowTextColorDd] = useState(false);
  const [showFolderDd, setShowFolderDd] = useState(false);
  const desktopFolderDdRef = useRef(null);
  const [showInsertDd, setShowInsertDd] = useState(false);
  // "Embed link" opens a centered modal (dims/blurs the background) instead of
  // swapping inline into the keyboard-docked Insert panel — that swap used to
  // fight with the on-screen keyboard for the same slot. Each row is one link
  // to embed; "+" adds another row so several links can be queued and embedded
  // together.
  const [insertEmbedMode, setInsertEmbedMode] = useState(false);
  const [embedRows, setEmbedRows] = useState([{ url:"", error:"" }]);

  const [wordCount, setWordCount] = useState(0);
  const [charCount, setCharCount] = useState(0);
  const [editorFocused, setEditorFocused] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [kbHeight, setKbHeight] = useState(0);
  // True while the keyboard height is actively changing (sliding in/out). The
  // docked toolbar stays hidden until this clears so it snaps into its final
  // resting spot above the keyboard instead of visibly floating up with it.
  // (Only used on the visualViewport fallback path; the VirtualKeyboard API
  // path pins the toolbar with CSS env() so there is nothing to settle.)
  const [kbSettling, setKbSettling] = useState(false);
  // Native only: true from the moment the editor takes focus until the keyboard
  // either arrives or is dismissed. On native, kbHeight comes from the
  // @capacitor/keyboard bridge event, which lands well after Android has already
  // started animating the keyboard up — gating the toolbar on it alone makes the
  // bar visibly lag behind the keyboard instead of riding up with it. Focus is
  // the earliest reliable signal we have, so the toolbar shows on that and the
  // keyboard height only refines where it sits. Cleared on blur and on
  // keyboardWillHide, so a back-button dismissal still hides the bar.
  const [kbExpected, setKbExpected] = useState(false);
  // Whether the browser supports the VirtualKeyboard API (Chromium/Android). When
  // true we let the on-screen keyboard OVERLAY the page and pin the toolbar to its
  // top edge purely in CSS via env(keyboard-inset-height) — the compositor keeps
  // it glued to the keyboard, so it can't float or bounce (no JS positioning).
  // Other browsers (iOS Safari, Firefox) fall back to the visualViewport logic.
  const [vkSupported, setVkSupported] = useState(false);
  const vkSupportedRef = useRef(false);
  // Visual-viewport geometry so the mobile editor can size itself to the visible
  // area (keeps the top bar visible and lets the toolbar ride flush on top of
  // the keyboard with no float/lag).
  const [vvTop, setVvTop] = useState(0);
  const [vvH, setVvH] = useState(0);
  // When a tool panel opens we dismiss the soft keyboard and let the panel
  // occupy its space (Google-Docs style). frozenKb remembers the keyboard
  // height captured at open-time so the panel/toolbar stay put even after
  // the keyboard collapses (kbHeight → 0).
  const [frozenKb, setFrozenKb] = useState(0);
  // True while the font-size number field is focused — the OS numeric keyboard
  // is then up, so the Aa panel lifts to sit above it instead of behind it.
  const [sizeFocused, setSizeFocused] = useState(false);
  // Measured height of the currently-open tool panel, so the docked toolbar can
  // rest directly on top of it (no gap when the panel is shorter than the kb).
  const [panelH, setPanelH] = useState(0);
  const panelRef = useRef(null);
  const btnRefs = useRef({}); // toolbar anchor buttons, keyed by panel name
  // Toolbar horizontal-scroll affordance: tracks whether there's more content
  // to scroll to on each side, so a fade+arrow hint can show/hide accordingly.
  const toolbarScrollRef = useRef(null);
  const [toolbarOverflow, setToolbarOverflow] = useState({ left: false, right: false });
  const updateToolbarOverflow = () => {
    const el = toolbarScrollRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setToolbarOverflow({
      left: el.scrollLeft > 4,
      right: el.scrollLeft < maxScroll - 4,
    });
  };
  useEffect(() => {
    window.addEventListener("resize", updateToolbarOverflow);
    return () => window.removeEventListener("resize", updateToolbarOverflow);
  }, []);
  const [panelAnchor, setPanelAnchor] = useState(null); // {top,left} of the open panel's button
  const [lastTextColor, setLastTextColor] = useState("#e23b3b"); // text-color button underline preview
  const [lastHighlight, setLastHighlight] = useState("#ffe600"); // highlight button underline preview
  // Highlight custom color picker (mirrors the planner block color picker).
  const [hlPickerOpen, setHlPickerOpen] = useState(false);
  const [liveHl, setLiveHl] = useState("#ffe066");
  const [savedHighlights, setSavedHighlights] = useState(() => {
    try { return JSON.parse(localStorage.getItem("clarity_savedHighlights") || "[]"); } catch { return []; }
  });
  const addSavedHighlight = (hex) => {
    if (!hex) return;
    setSavedHighlights(prev => {
      const next = [hex, ...prev.filter(c => c !== hex)].slice(0, 20);
      localStorage.setItem("clarity_savedHighlights", JSON.stringify(next));
      return next;
    });
  };
  const [savedTextColors, setSavedTextColors] = useState(() => {
    try { return JSON.parse(localStorage.getItem("clarity_savedTextColors") || "[]"); } catch { return []; }
  });
  const [liveTc, setLiveTc] = useState("#e23b3b");
  const [tcPickerOpen, setTcPickerOpen] = useState(false);
  const addSavedTextColor = (hex) => {
    if (!hex || hex === "inherit") return;
    setSavedTextColors(prev => {
      const next = [hex, ...prev.filter(c => c !== hex)].slice(0, 20);
      localStorage.setItem("clarity_savedTextColors", JSON.stringify(next));
      return next;
    });
  };

  // Journal-only folders (Apple-Notes style, independent of Spaces).
  const [journalFolders, setJournalFolders] = useState(() => {
    try { return JSON.parse(localStorage.getItem("clarity_journal_folders") || "[]"); } catch { return []; }
  });
  const persistFolders = (next) => { setJournalFolders(next); localStorage.setItem("clarity_journal_folders", JSON.stringify(next)); };
  const addFolder = (name) => {
    const nm = (name || "").trim();
    if (!nm) return null;
    const f = { id: uid(), name: nm };
    persistFolders([...journalFolders, f]);
    return f.id;
  };
  const deleteFolder = (id) => {
    persistFolders(journalFolders.filter(f => f.id !== id));
    // Orphaned notes fall back to Unfiled.
    setEntries(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(date => { next[date] = next[date].map(n => n.folder === id ? { ...n, folder: null } : n); });
      return next;
    });
    setFilterFolder(prev => prev === id ? null : prev);
  };
  const moveNoteToFolder = (noteId, folderId) => {
    setEntries(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(date => { next[date] = next[date].map(n => n.id === noteId ? { ...n, folder: folderId } : n); });
      return next;
    });
  };
  const folderName = (id) => journalFolders.find(f => f.id === id)?.name || null;
  // Preview-card background = the note's own colour, lightened toward white so the
  // title/excerpt stay readable (gradients are already pastel — used as-is).
  const noteCardBg = (note) => {
    const c = note.noteBg || note.color || "#fff7f9";
    if (typeof c === "string" && c.includes("gradient")) return c;
    if (isDarkNoteBg(c)) return c; // a Black note's card stays black (light text)
    return mix(c, "#ffffff", 0.4);
  };
  // "No additional text" placeholder colour — picks black/white off the note's
  // own card background (via WCAG luminance) so it stays legible instead of the
  // fixed light tone that used to wash out on pastel/gradient card colours.
  const notePlaceholderColor = (note) => {
    const bg = noteCardBg(note);
    const hex = (bg.match(/#[0-9a-fA-F]{6}/) || ["#fff7f9"])[0];
    return onColor(hex);
  };
  // Accent spine colour: inherit the colour of the category matching the note's
  // folder name; fall back to the app's primary/accent colour when unfiled or
  // the folder doesn't correspond to a category.
  const noteSpineColor = (note) => {
    const fname = note.folder ? journalFolders.find(f => f.id === note.folder)?.name : null;
    if (fname) {
      const cat = categories.find(c => c.title && c.title.toLowerCase() === fname.toLowerCase());
      if (cat?.color) return cat.color;
    }
    return "var(--c-primary)";
  };
  const [folderTargetId, setFolderTargetId] = useState(null); // note id being assigned to a folder
  const [newFolderName, setNewFolderName] = useState("");
  const [creatingFolder, setCreatingFolder] = useState(false);

  const editorRef = useRef(null);
  const titleRef = useRef(null);
  const savedRangeRef = useRef(null);
  const panelTextboxRef = useRef(null); // data-sid of the text box a tool panel was opened from
  // Tracks whether the user has started typing digits into the size field so we
  // know to replace (first digit) vs append (subsequent digits).
  const typingSizeRef = useRef(false);
  const autosaveTimer = useRef(null);
  const imageInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  // Video / audio / voice recordings upload to Cloudinary and land in the note
  // at the caret. insertMediaRef is filled in below, next to insertDivider.
  const insertMediaRef = useRef(null);
  const media = useMediaUpload({ onUploaded: (att) => insertMediaRef.current?.(att) });
  const moreMenuRef = useRef(null);
  const maxVvHRef = useRef(0);
  const kbHeightRef = useRef(0);

  const allNotes = useMemo(() => {
    const arr = [];
    Object.values(entries).forEach(dayNotes => {
      if (Array.isArray(dayNotes)) dayNotes.forEach(n => arr.push(n));
    });
    return arr;
  }, [entries]);

  const activeNote = useMemo(() => allNotes.find(n => n.id === activeNoteId) || null, [allNotes, activeNoteId]);

  const filteredNotes = useMemo(() => {
    let arr = [...allNotes];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      arr = arr.filter(n => (n.title||"").toLowerCase().includes(q) || (n.content||"").toLowerCase().includes(q));
    }
    if (filterFolder === "__unfiled__") arr = arr.filter(n => !n.folder);
    else if (filterFolder) arr = arr.filter(n => n.folder === filterFolder);
    if (sortMode === "recent") arr.sort((a,b) => (b.updatedAt||"").localeCompare(a.updatedAt||""));
    else if (sortMode === "oldest") arr.sort((a,b) => (a.updatedAt||"").localeCompare(b.updatedAt||""));
    else if (sortMode === "az") arr.sort((a,b) => (a.title||"").localeCompare(b.title||""));
    else if (sortMode === "pinned") arr.sort((a,b) => (b.pinned?1:0)-(a.pinned?1:0));
    // Pinned always float to the top regardless of sort.
    arr.sort((a,b) => (b.pinned?1:0)-(a.pinned?1:0));
    return arr;
  }, [allNotes, searchQuery, filterFolder, sortMode]);

  // The format toolbar stays up while typing in the note body OR a text box
  // (Jade, 2026-10-28). It only counted the body, so on a computer the toolbar
  // went away as soon as a text box was clicked, leaving no way to change its
  // font, colour or highlight; on the phone it waited for the keyboard.
  const inWritingArea = (el) => !!el && ((editorRef.current && editorRef.current.contains(el)) || !!(el.classList && el.classList.contains("cj-textbox")));
  const onWritingFocus = () => { setEditorFocused(true); if (isNative) setKbExpected(true); updateFormats(); };
  const onWritingBlur = () => { setTimeout(() => { if (!inWritingArea(document.activeElement)) { setEditorFocused(false); setKbExpected(false); } }, 160); };

  // When a floating text box is focused, formatting commands should target ITS
  // selection (run execCommand in place) instead of restoring the main editor.
  // On mobile, opening a tool panel blurs the box to hide the keyboard, so by
  // the time a font / text colour / highlight was picked nothing was focused
  // and the pick landed in the note body instead (Jade, 2026-10-28). Like the
  // title below, fall back to the box that was being typed in when the panel
  // opened (remembered by openPanel).
  const panelTextbox = () => {
    const id = panelTextboxRef.current;
    if (!id) return null;
    const el = editorAreaRef.current?.querySelector(`.cj-textbox[data-sid="${id}"]`);
    return el && el.isConnected ? el : null;
  };
  const inTextbox = () => {
    const el = document.activeElement;
    if (el && el.classList && el.classList.contains("cj-textbox")) return true;
    if (el && el.isContentEditable) return false;
    return !!panelTextbox();
  };
  // Same idea for the note title (also its own contentEditable, separate from
  // the body editor) — highlighted title text should format in place too.
  // On mobile, opening a tool panel blurs the title to hide the keyboard, so by
  // the time a font/colour is picked nothing is focused. Fall back to where the
  // selection was when the panel opened (saved by openPanel).
  const inTitle = () => {
    const t = titleRef.current, ae = document.activeElement, r = savedRangeRef.current;
    if (!t) return false;
    if (ae === t) return true;
    if (ae && ae.isContentEditable) return false;
    return !!(r && t.contains(r.commonAncestorContainer));
  };
  // If the focused element has no highlighted selection (just a caret), select
  // its whole contents so a format change applies to everything inside it.
  const ensureWholeSelection = (el) => {
    if (!el) return null;
    const sel = window.getSelection();
    if (sel && (sel.rangeCount === 0 || sel.getRangeAt(0).collapsed)) {
      const r = document.createRange();
      r.selectNodeContents(el);
      sel.removeAllRanges();
      sel.addRange(r);
    }
    return el;
  };
  // Focus the panel's text box again and put back what was highlighted in it.
  const restoreTextboxRange = () => {
    const el = panelTextbox();
    if (!el) return null;
    if (document.activeElement !== el) {
      el.focus();
      const r = savedRangeRef.current, sel = window.getSelection();
      if (sel && r && el.contains(r.commonAncestorContainer)) { sel.removeAllRanges(); sel.addRange(r); }
    }
    return el;
  };
  const ensureTextboxSelection = () => {
    const ae = document.activeElement;
    const el = ae && ae.classList && ae.classList.contains("cj-textbox") ? ae : restoreTextboxRange();
    return el ? ensureWholeSelection(el) : null;
  };
  const ensureTitleSelection = () => {
    const t = titleRef.current;
    if (!t || !inTitle()) return null;
    if (document.activeElement !== t) {
      t.focus();
      const r = savedRangeRef.current, sel = window.getSelection();
      if (sel && r && t.contains(r.commonAncestorContainer)) { sel.removeAllRanges(); sel.addRange(r); }
    }
    return ensureWholeSelection(t);
  };
  // Existing note titles only ever had a plain-text title before rich title
  // formatting existed — escape it before dropping it into innerHTML so stray
  // "<"/"&" characters in an old title don't get parsed as markup.
  const escapeHtml = (s) => (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  const saveRange = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) savedRangeRef.current = sel.getRangeAt(0).cloneRange();
  };

  const restoreRange = () => {
    const sel = window.getSelection();
    if (sel && savedRangeRef.current) {
      sel.removeAllRanges();
      sel.addRange(savedRangeRef.current);
    }
    editorRef.current?.focus();
  };

  // One alignment button that cycles: left → center → right → justify.
  const cycleAlign = () => {
    const order = ["justifyLeft","justifyCenter","justifyRight","justifyFull"];
    const cur = order.find(c => activeFormats[c]); // justifyLeft is the default (untracked)
    const next = order[((cur ? order.indexOf(cur) : 0) + 1) % order.length];
    saveRange();
    execCmd(next);
  };

  const execCmd = (cmd, val) => {
    const tb = inTextbox();
    const tt = !tb && inTitle();
    if (tb) ensureTextboxSelection();
    else if (tt) ensureTitleSelection();
    else restoreRange();
    document.execCommand(cmd, false, val !== undefined ? val : null);
    updateFormats();
    if (!tb) scheduleAutosave();
    // Bold / italic / ... on a text box or the title while a panel is open:
    // same as a font pick, the keyboard stays down.
    if (tb || tt) keepPanel();
  };

  const updateFormats = () => {
    setActiveFormats({
      bold: document.queryCommandState("bold"),
      italic: document.queryCommandState("italic"),
      underline: document.queryCommandState("underline"),
      strikeThrough: document.queryCommandState("strikeThrough"),
      insertUnorderedList: document.queryCommandState("insertUnorderedList"),
      insertOrderedList: document.queryCommandState("insertOrderedList"),
      justifyCenter: document.queryCommandState("justifyCenter"),
      justifyRight: document.queryCommandState("justifyRight"),
      justifyFull: document.queryCommandState("justifyFull"),
    });
    const el = editorRef.current;
    if (el) {
      const text = el.innerText || "";
      setWordCount(text.trim().split(/\s+/).filter(Boolean).length);
      setCharCount(text.replace(/\n/g,"").length);
    }
  };

  useEffect(() => {
    const handler = () => updateFormats();
    document.addEventListener("selectionchange", handler);
    return () => document.removeEventListener("selectionchange", handler);
  }, []);

  useEffect(() => {
    const close = (e) => {
      // The desktop folder dropdown manages its own click-outside behaviour
      // (see desktopFolderDdRef below) — skip it here so a click that opens it
      // doesn't get immediately undone by this same-tick blanket closer.
      if (!(desktopFolderDdRef.current && desktopFolderDdRef.current.contains(e.target))) {
        setShowFolderDd(false);
      }
      setShowMoreMenu(false); setContextMenu(null); setShowHeadingDd(false); setShowFontDd(false); setShowStyleDd(false); setShowHighlightDd(false); setShowTextColorDd(false); setShowInsertDd(false); setShowBgPanel(false); setShowStickerPicker(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  // Detect mobile (used to dock the toolbar above the on-screen keyboard)
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener ? mq.addEventListener("change", update) : mq.addListener(update);
    return () => { mq.removeEventListener ? mq.removeEventListener("change", update) : mq.removeListener(update); };
  }, []);

  // ── Primary path: VirtualKeyboard API (Chromium / Android) ────────────────
  // This is how Google Docs / modern web editors keep a toolbar glued above the
  // keyboard. We opt into `overlaysContent`, so the keyboard no longer resizes
  // the page — it overlays it — and the browser exposes the keyboard rectangle
  // both to CSS (env(keyboard-inset-*)) and to JS (geometrychange + boundingRect).
  // The toolbar is then pinned with pure CSS `bottom: env(keyboard-inset-height)`,
  // which the compositor keeps locked to the keyboard's top edge every frame, so
  // it physically cannot float in or bounce. We only enable it inside the editor
  // and restore the default when leaving, so the rest of the app is untouched.
  useEffect(() => {
    if (Capacitor.isNativePlatform()) return; // native uses the Keyboard-plugin path below instead
    const vk = (typeof navigator !== "undefined") && navigator.virtualKeyboard;
    if (!vk) return;                       // unsupported → visualViewport fallback
    if (screen !== "editor") return;       // scope to the notes editor only
    vk.overlaysContent = true;
    vkSupportedRef.current = true;
    setVkSupported(true);
    const onGeom = () => {
      const h = (vk.boundingRect && vk.boundingRect.height) || 0;
      kbHeightRef.current = h;
      setKbHeight(h);
    };
    vk.addEventListener("geometrychange", onGeom);
    onGeom();
    return () => {
      vk.removeEventListener("geometrychange", onGeom);
      vk.overlaysContent = false;
      vkSupportedRef.current = false;
      setVkSupported(false);
      kbHeightRef.current = 0;
      setKbHeight(0);
    };
  }, [screen]);

  // ── Fallback path: VisualViewport API (iOS Safari, Firefox, etc.) ─────────
  // The on-screen keyboard height = the gap between the bottom of the layout
  // viewport (where `position:fixed; bottom` anchors) and the bottom of the
  // visible area: innerHeight - visualViewport.height - visualViewport.offsetTop.
  // Skipped entirely when the VirtualKeyboard API is available (above).
  // Scoped to the editor screen (like the VirtualKeyboard path above) for two
  // reasons: leaving these listeners attached while on the notes list let a
  // stray scroll event (address-bar show/hide changes the same viewport delta
  // a keyboard would) corrupt kbHeight with nothing to correct it afterward —
  // and even a clean close raced the keyboard's own closing animation, which
  // fires a few more resize events *after* navigating away. Either way, the
  // next note you opened could inherit a stale nonzero height and dock a
  // toolbar with no keyboard under it. Tearing the listener down on exit (via
  // this effect's cleanup) and resetting to 0 avoids both.
  useEffect(() => {
    if (Capacitor.isNativePlatform()) return; // native uses the Keyboard-plugin path below instead
    if (typeof navigator !== "undefined" && navigator.virtualKeyboard) return;
    if (screen !== "editor") return;
    const vv = window.visualViewport;
    if (!vv) return;
    let settleTimer;
    const onResize = () => {
      const raw = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      const prev = kbHeightRef.current;
      // hysteresis: open when raw > 80, closed when raw < 20, sticky in between.
      let next = prev;
      if (raw < 20) {
        next = 0;                                  // keyboard closed
      } else if (raw > 80) {
        // Keyboard open. Only move the dock when the height changes meaningfully
        // (>32px) — that covers open and keyboard swaps (text↔emoji), which have
        // different heights so the bar still lands flush. Smaller fluctuations
        // (suggestion bar, gesture hints, prediction reflow) are IGNORED so the
        // toolbar stays rock-steady and never bounces while you type/tap buttons.
        if (prev === 0 || Math.abs(raw - prev) > 32) next = raw;
      }
      kbHeightRef.current = next;
      setKbHeight(next);
      setVvTop(vv.offsetTop);
      setVvH(vv.height);
      // When the dock height genuinely changes (open / close / swap — not a micro
      // jitter, which we deadbanded away above), hide the toolbar until it stops
      // moving so it snaps into its final spot instead of sliding/bouncing.
      if (Math.abs(next - prev) > 8) {
        setKbSettling(true);
        clearTimeout(settleTimer);
        settleTimer = setTimeout(() => setKbSettling(false), 130);
      }
    };
    vv.addEventListener("resize", onResize);
    vv.addEventListener("scroll", onResize);
    onResize();
    return () => {
      clearTimeout(settleTimer);
      vv.removeEventListener("resize", onResize);
      vv.removeEventListener("scroll", onResize);
      kbHeightRef.current = 0;
      setKbHeight(0);
      setKbSettling(false);
    };
  }, [screen]);

  // ── Native path: @capacitor/keyboard (Android app) ────────────────────────
  // Neither web path above is reliable inside the app's WebView — navigator
  // .virtualKeyboard's geometrychange and visualViewport's resize both depend
  // on the WebView actually reporting a resized viewport when the keyboard
  // opens, which isn't guaranteed even with windowSoftInputMode="adjustResize"
  // set in AndroidManifest.xml. The Keyboard plugin (configured with
  // resize: 'native' in capacitor.config.ts) talks to Android directly and
  // fires these events with the real keyboard height, sidestepping that gap.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    if (screen !== "editor") return;
    const showSub = Keyboard.addListener("keyboardWillShow", (info) => {
      const h = info?.keyboardHeight || 0;
      kbHeightRef.current = h;
      setKbHeight(h);
    });
    const hideSub = Keyboard.addListener("keyboardWillHide", () => {
      kbHeightRef.current = 0;
      setKbHeight(0);
      setKbExpected(false);   // back-button dismissal: drop the toolbar with it
    });
    return () => {
      showSub.then((h) => h.remove());
      hideSub.then((h) => h.remove());
      kbHeightRef.current = 0;
      setKbHeight(0);
    };
  }, [screen]);

  // NOTE: screen/activeNoteId are intentionally NOT persisted across renders —
  // switching away from the Journal tab and back should always land on the
  // main list, never reopen whatever note was last open (see the one-shot
  // consume in the useState initializers above for the "New Note" exception).

  // Restore list scroll position when returning to list screen
  const listScrollRef = useRef(null);
  useEffect(() => {
    if (screen !== "list") return;
    const saved = parseInt(localStorage.getItem("clarity_journal_listScroll") || "0", 10);
    requestAnimationFrame(() => { if (listScrollRef.current) listScrollRef.current.scrollTop = saved; });
    return () => {
      if (listScrollRef.current) {
        try { localStorage.setItem("clarity_journal_listScroll", listScrollRef.current.scrollTop); } catch {}
      }
    };
  }, [screen]);

  // Measure the open panel so the docked toolbar can sit flush on top of it.
  React.useLayoutEffect(() => {
    const anyOpen = showHeadingDd || showInsertDd || showHighlightDd || showTextColorDd || showBgPanel || showStickerPicker;
    if (!anyOpen) { setPanelH(0); return; }
    const el = panelRef.current;
    if (!el) return;
    const measure = () => setPanelH(el.offsetHeight);
    measure();
    // Watch the BORDER box, not the default content box. The panel's
    // padding-bottom is var(--sab), which on native reads 0 while the keyboard
    // is up (Capacitor's insetsHandling:'css' pads the viewport by the keyboard,
    // so nothing overlaps the panel) and jumps to the gesture-bar height once
    // the keyboard finishes hiding — which is exactly what opening a panel
    // does. That growth is padding-only, so the content box never changes and a
    // default observer stays silent: panelH would keep its pre-inset value and
    // the toolbar would sit parked underneath the now-taller panel, which has
    // the higher z-index and hides it outright. The web path measures the same
    // number either way (--sab is 0 in a browser), so this is native-only in
    // effect.
    const ro = new ResizeObserver(measure);
    try { ro.observe(el, { box: "border-box" }); } catch { ro.observe(el); }
    // The inset flip emits no event of its own and lands only after the
    // keyboard's hide animation, so also re-measure across that window.
    const timers = [80, 200, 400, 650].map(ms => setTimeout(measure, ms));
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      timers.forEach(clearTimeout);
      window.removeEventListener("resize", measure);
    };
  }, [showHeadingDd, showInsertDd, showHighlightDd, showTextColorDd, showBgPanel, showStickerPicker, showFontDd, showStyleDd, hlPickerOpen, tcPickerOpen, sizeFocused, savedHighlights.length, savedTextColors.length, frozenKb, kbHeight]);

  // While the mobile editor is open it sits fixed over the viewport, so lock
  // the page (and the scrollable app shell) behind it to kill rubber-banding.
  useEffect(() => {
    if (!isMobile || screen !== "editor") return;
    const main = document.querySelector(".clarity-main");
    const prevBody = document.body.style.overflow;
    const prevMain = main ? main.style.overflow : "";
    document.body.style.overflow = "hidden";
    if (main) main.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevBody;
      if (main) main.style.overflow = prevMain;
    };
  }, [isMobile, screen]);

  // ── Undo / redo — custom history stack ──────────────────────────────────────
  // The native `document.execCommand("undo")` wipes the whole note in one shot:
  // the content is loaded by assigning `editorRef.current.innerHTML`, and the
  // browser records that single programmatic insertion as the only undoable step,
  // so the first undo reverts straight back to empty. Instead we keep our own
  // stack of HTML+caret snapshots, debounced on input, and restore them ourselves.
  const historyRef = useRef({ undo: [], redo: [], present: "", caret: null });
  const histTimer = useRef(null);
  const HIST_MAX = 120;

  const getCaretOffset = (root) => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return null;
    const range = sel.getRangeAt(0);
    if (!root.contains(range.startContainer)) return null;
    const pre = range.cloneRange();
    pre.selectNodeContents(root);
    pre.setEnd(range.startContainer, range.startOffset);
    return pre.toString().length;
  };

  const setCaretOffset = (root, offset) => {
    if (offset == null) return;
    let remaining = offset;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    let node;
    while ((node = walker.nextNode())) {
      const len = node.textContent.length;
      if (remaining <= len) {
        const range = document.createRange();
        range.setStart(node, remaining);
        range.collapse(true);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
        return;
      }
      remaining -= len;
    }
    const range = document.createRange();
    range.selectNodeContents(root);
    range.collapse(false);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
  };

  const resetHistory = (html) => {
    if (histTimer.current) { clearTimeout(histTimer.current); histTimer.current = null; }
    historyRef.current = { undo: [], redo: [], present: html || "", caret: null };
  };

  const recordHistory = () => {
    const el = editorRef.current;
    if (!el) return;
    const html = el.innerHTML;
    const h = historyRef.current;
    if (html === h.present) return;          // nothing changed since last snapshot
    h.undo.push({ html: h.present, caret: h.caret });
    if (h.undo.length > HIST_MAX) h.undo.shift();
    h.redo = [];
    h.present = html;
    h.caret = getCaretOffset(el);
  };

  const scheduleHistory = () => {
    if (histTimer.current) clearTimeout(histTimer.current);
    histTimer.current = setTimeout(recordHistory, 350);
  };

  const flushHistory = () => {
    if (histTimer.current) { clearTimeout(histTimer.current); histTimer.current = null; }
    recordHistory();
  };

  const doUndo = () => {
    const el = editorRef.current;
    if (!el) return;
    flushHistory();
    const h = historyRef.current;
    if (h.undo.length === 0) return;
    const prev = h.undo.pop();
    h.redo.push({ html: h.present, caret: h.caret });
    h.present = prev.html;
    h.caret = prev.caret;
    el.innerHTML = prev.html;
    reattachChecklists();
    el.focus();
    setCaretOffset(el, prev.caret);
    updateFormats();
    scheduleAutosave();
  };

  const doRedo = () => {
    const el = editorRef.current;
    if (!el) return;
    flushHistory();
    const h = historyRef.current;
    if (h.redo.length === 0) return;
    const next = h.redo.pop();
    h.undo.push({ html: h.present, caret: h.caret });
    h.present = next.html;
    h.caret = next.caret;
    el.innerHTML = next.html;
    reattachChecklists();
    el.focus();
    setCaretOffset(el, next.caret);
    updateFormats();
    scheduleAutosave();
  };

  const scheduleAutosave = () => {
    setIsDirty(true);
    scheduleHistory();
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => doSave(), 2000);
  };

  const doSave = () => {
    if (!activeNoteId || !editorRef.current) return;
    const htmlContent = editorRef.current.innerHTML;
    const content = editorRef.current.innerText || "";
    const titleHtml = titleRef.current?.innerHTML || "";
    const title = (titleRef.current?.innerText || "").trim();
    setEntries(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(date => {
        next[date] = next[date].map(n => {
          if (n.id === activeNoteId) return { ...n, htmlContent, content, title, titleHtml, updatedAt: new Date().toISOString() };
          return n;
        });
      });
      return next;
    });
    setIsDirty(false);
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 1800);
  };

  // Tracks the id of a note created THIS session that hasn't been confirmed to
  // have real content yet. Only fresh, still-untouched notes get auto-discarded
  // on exit — pre-existing empty notes a user merely reopens are left alone.
  const newNoteIdRef = useRef(null);
  const [emptyDiscardToast, setEmptyDiscardToast] = useState(false);

  const isEditorContentEmpty = () => {
    const html = editorRef.current ? editorRef.current.innerHTML.replace(/<br\s*\/?>/gi, "").trim() : "";
    const title = (titleRef.current?.innerText || "").trim();
    // A sticker or a background photo is still real input, even when the
    // text body and title are both untouched — only discard when NONE of
    // title/body/stickers/background have anything in them.
    const hasStickers = (activeNote?.stickers || []).length > 0;
    const hasBg = !!activeNote?.noteBg;
    return title === "" && html === "" && !hasStickers && !hasBg;
  };

  // Called whenever the currently-open note is about to be left. If it's a
  // freshly-created note the user never typed into, silently delete it instead
  // of persisting an empty entry, and flash a badge so the user knows why it's
  // gone. Returns true if it discarded (caller should skip the normal save).
  const discardIfEmpty = () => {
    if (!newNoteIdRef.current || newNoteIdRef.current !== activeNoteId) return false;
    if (!isEditorContentEmpty()) { newNoteIdRef.current = null; return false; }
    if (autosaveTimer.current) { clearTimeout(autosaveTimer.current); autosaveTimer.current = null; }
    deleteNote(activeNoteId);
    newNoteIdRef.current = null;
    setEmptyDiscardToast(true);
    setTimeout(() => setEmptyDiscardToast(false), 2500);
    return true;
  };

  const emptyDiscardToastEl = emptyDiscardToast ? createPortal(
    <div style={{ position:"fixed", left:"50%", bottom:"calc(100px + var(--sab, 0px))", transform:"translateX(-50%)", zIndex:99999, background:"#3a2035", color:"#fff", padding:"10px 18px", borderRadius:14, fontSize:13, fontWeight:700, display:"flex", alignItems:"center", gap:8, boxShadow:"0 8px 24px rgba(0,0,0,0.25)", animation:"journalEmptyToastFade 2.5s ease forwards", pointerEvents:"none", whiteSpace:"nowrap" }}>
      <style>{`@keyframes journalEmptyToastFade { 0% { opacity:0; transform:translateX(-50%) translateY(10px); } 12% { opacity:1; transform:translateX(-50%) translateY(0); } 82% { opacity:1; transform:translateX(-50%) translateY(0); } 100% { opacity:0; transform:translateX(-50%) translateY(-6px); } }`}</style>
      <Trash2 size={15}/> Empty note discarded automatically
    </div>,
    document.body
  ) : null;

  // Confirmation toast for "Save as Image" landing directly in the gallery —
  // same visual treatment as the empty-discard toast above.
  const [imageSavedToast, setImageSavedToast] = useState(false);
  useEffect(() => {
    if (!imageSavedToast) return;
    const tm = setTimeout(() => setImageSavedToast(false), 2200);
    return () => clearTimeout(tm);
  }, [imageSavedToast]);
  const imageSavedToastEl = imageSavedToast ? createPortal(
    <div style={{ position:"fixed", left:"50%", bottom:"calc(100px + var(--sab, 0px))", transform:"translateX(-50%)", zIndex:99999, background:"#3a2035", color:"#fff", padding:"10px 18px", borderRadius:14, fontSize:13, fontWeight:700, display:"flex", alignItems:"center", gap:8, boxShadow:"0 8px 24px rgba(0,0,0,0.25)", animation:"journalEmptyToastFade 2.2s ease forwards", pointerEvents:"none", whiteSpace:"nowrap" }}>
      <Check size={15}/> Saved to gallery
    </div>,
    document.body
  ) : null;

  const openNote = (note) => {
    discardIfEmpty();
    setActiveNoteId(note.id);
    setSlideInNew(false);
    setScreen("editor");
    setShowTemplates(false);
    setTimeout(() => {
      if (editorRef.current) {
        editorRef.current.innerHTML = note.htmlContent || note.content || "";
        editorRef.current.style.fontFamily = (note.font && note.font !== "normal") ? `'${note.font}', sans-serif` : "";
        reattachChecklists();
        updateFormats();
        resetHistory(editorRef.current.innerHTML);
      }
      if (titleRef.current) titleRef.current.innerHTML = note.titleHtml || escapeHtml(note.title || "");
      setFontSize(16);
    }, 30);
  };

  const createNote = (template) => {
    discardIfEmpty();
    const id = uid();
    const date = todayStr();
    // Inherit whichever folder the list is currently filtered to — creating a
    // note while inside a folder's page should land it in that folder, not
    // unfiled. "__unfiled__" is its own filter (not a real folder), so that
    // one still leaves the new note unassigned like the "All notes" view.
    const inheritedFolder = (filterFolder && filterFolder !== "__unfiled__") ? filterFolder : null;
    const newNote = { id, title:"", htmlContent:template.html, content:"", pinned:false, font:"normal", color:"#ffffff", noteStyle:"plain", noteBg:null, folder:inheritedFolder, date, updatedAt:new Date().toISOString() };
    setEntries(prev => {
      const next = { ...prev };
      next[date] = [...(next[date] || []), newNote];
      return next;
    });
    newNoteIdRef.current = id;
    setActiveNoteId(id);
    setSlideInNew(true);
    setScreen("editor");
    setShowTemplates(false);
    setTimeout(() => {
      if (editorRef.current) {
        editorRef.current.innerHTML = template.html;
        editorRef.current.focus();
        // This focus() runs from a timeout, so it's detached from the tap that
        // opened the note. Android's WebView only reliably raises the IME for
        // focus it can tie to a user gesture, so on native it often sits there
        // doing nothing until the next tap — ask the keyboard up explicitly.
        if (isNative) Keyboard.show().catch(() => {});
        updateFormats();
        resetHistory(editorRef.current.innerHTML);
      }
      if (titleRef.current) titleRef.current.innerHTML = "";
    }, 30);
    // Clear the one-shot flag after the entrance animation has played so a later
    // re-render (e.g. keyboard resize) doesn't replay the slide.
    setTimeout(() => setSlideInNew(false), 350);
  };

  // The editor's text lives only in the contentEditable DOM, not in React state.
  // Switching tabs unmounts JournalTab (the app shell keys content by tab), so on
  // return the editor div remounts EMPTY even though `activeNoteId` is restored —
  // which is why the page looked blank until you re-opened the note. This effect
  // reloads the saved note into the editor whenever the editor screen is (re)entered.
  // Guarded on `isEmpty` so it only fills a fresh/empty editor and never clobbers
  // live in-progress edits (note-to-note switches are still handled by openNote).
  useEffect(() => {
    if (screen !== "editor") return;
    const el = editorRef.current;
    if (!el) return;
    const note = allNotes.find(n => n.id === activeNoteId);
    if (!note) return;
    const isEmpty = el.innerHTML.replace(/<br\s*\/?>/gi, "").trim() === "";
    if (!isEmpty) return;
    el.innerHTML = note.htmlContent || note.content || "";
    el.style.fontFamily = (note.font && note.font !== "normal") ? `'${note.font}', sans-serif` : "";
    reattachChecklists();
    updateFormats();
    resetHistory(el.innerHTML);
    if (titleRef.current) titleRef.current.innerHTML = note.titleHtml || escapeHtml(note.title || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, activeNoteId]);

  // Live-refresh the editor from a remote update while the note is open on
  // THIS device too — complements the fill-effect above, which only ever
  // fills an EMPTY editor. Without this, leaving the same note open on two
  // devices at once means neither ever sees the other's edits until closing
  // and reopening it. Only applies when nothing local is in the way: skipped
  // while there's an unsaved edit still sitting in the 2s autosave debounce
  // (isDirty), or while the editor/title is actually focused — either would
  // mean yanking text out from under an active keystroke or caret position.
  const lastAppliedRemoteRef = useRef(null);
  useEffect(() => {
    if (screen !== "editor" || !activeNote) return;
    const el = editorRef.current;
    if (!el) return;
    if (isDirty) return;
    if (document.activeElement === el || document.activeElement === titleRef.current) return;
    if (lastAppliedRemoteRef.current === activeNote.updatedAt) return;
    const incomingHtml = activeNote.htmlContent || activeNote.content || "";
    if (incomingHtml === el.innerHTML) { lastAppliedRemoteRef.current = activeNote.updatedAt; return; }
    lastAppliedRemoteRef.current = activeNote.updatedAt;
    el.innerHTML = incomingHtml;
    el.style.fontFamily = (activeNote.font && activeNote.font !== "normal") ? `'${activeNote.font}', sans-serif` : "";
    reattachChecklists();
    updateFormats();
    resetHistory(el.innerHTML);
    if (titleRef.current) titleRef.current.innerHTML = activeNote.titleHtml || escapeHtml(activeNote.title || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeNote?.htmlContent, activeNote?.content, activeNote?.updatedAt, activeNote?.titleHtml, activeNote?.title, activeNote?.font, screen, isDirty]);

  // Persist any unsaved edits before the tab unmounts or the page is hidden. The
  // 2s autosave debounce means text typed right before leaving would otherwise be
  // lost (its only copy is the DOM, which is torn down on a tab switch). A ref to
  // the latest doSave keeps the cleanup from capturing a stale closure.
  const doSaveRef = useRef(doSave);
  doSaveRef.current = doSave;
  useEffect(() => {
    const flush = () => {
      if (autosaveTimer.current) { clearTimeout(autosaveTimer.current); autosaveTimer.current = null; }
      doSaveRef.current();
    };
    const onVis = () => { if (document.visibilityState === "hidden") flush(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pagehide", flush);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pagehide", flush);
      flush();   // also flush on unmount (switching app tabs)
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Safety net: also flush on an isMobile/isDesktop breakpoint change (e.g.
  // rotating the phone). The editor's DOM node is now kept stable across
  // that transition (see the portal above), so this shouldn't normally have
  // anything to do — but it guards against any other codepath that still
  // remounts the editor on a layout-breakpoint change.
  useEffect(() => {
    if (autosaveTimer.current) { clearTimeout(autosaveTimer.current); autosaveTimer.current = null; }
    doSaveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, isDesktop]);

  const deleteNote = (id) => {
    setEntries(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(date => {
        next[date] = next[date].filter(n => n.id !== id);
        if (next[date].length === 0) delete next[date];
      });
      return next;
    });
  };

  const duplicateNote = (note) => {
    const id = uid();
    const date = todayStr();
    const copy = { ...note, id, title:(note.title||"Untitled")+" (copy)", titleHtml: note.titleHtml ? note.titleHtml+" (copy)" : undefined, date, updatedAt:new Date().toISOString() };
    setEntries(prev => { const next={...prev}; next[date]=[...(next[date]||[]),copy]; return next; });
  };

  const togglePin = (id) => {
    setEntries(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(date => { next[date]=next[date].map(n=>n.id===id?{...n,pinned:!n.pinned}:n); });
      return next;
    });
  };

  // `patch` is normally a plain object, applied against whatever `activeNote`
  // was at the call site. It can also be a function `(note) => partialPatch` —
  // called with the note as React actually has it *at write time*, inside the
  // setState updater — for callers (like updateSticker below) that resume
  // after an await and can no longer trust a snapshot captured before it.
  const updateActiveNote = (patch) => {
    if (!activeNoteId) return;
    setEntries(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(date => { next[date]=next[date].map(n=>n.id===activeNoteId?{...n,...(typeof patch === "function" ? patch(n) : patch)}:n); });
      return next;
    });
  };

  // ── Sticker / image-overlay helpers ─────────────────────────────────────────
  const activeStickers = activeNote?.stickers || [];
  // Stickers/text boxes made before this device-gating existed have no `dc`
  // at all — those are grandfathered in and always shown, everywhere, same
  // as before. Only a sticker with a recorded `dc` gets hidden on a mismatch.
  const visibleStickers = activeStickers.filter(s => !s.dc || (s.dc === "desktop") === isDesktop);

  const addSticker = (sticker) => {
    const newS = { id: uid(), x: 60 / STICKER_LEGACY_BASE_W, y: 60 / STICKER_LEGACY_BASE_W, size: 72 / STICKER_LEGACY_BASE_W, rotation: 0, u: "r", dc: isDesktop ? "desktop" : "mobile", ...sticker };
    updateActiveNote(n => ({ stickers: [...(n.stickers || []), newS] }));
    setSelectedStickerId(newS.id);
    setShowStickerPicker(false);
    return newS;
  };

  // A data URL's raw bytes live inline in the synced note JSON — and the whole
  // account is synced as ONE Firestore document capped at 1MB (see
  // src/firebase/sync.js), so a single embedded photo can silently blow that
  // budget: the sync push fails quietly and the sticker never reaches another
  // device, even though it looks fine on the one that created it. Uploading to
  // Cloudinary (already used for Premium file attachments) and swapping the
  // sticker's `content` to the short hosted URL once the upload finishes keeps
  // the synced payload tiny. If Cloudinary isn't configured, or the upload
  // fails, the sticker just keeps its local data URL — today's behavior, still
  // fine on the device that made it.
  const dataUrlToFile = (dataUrl, filename) => {
    const [meta, b64] = dataUrl.split(",");
    const mime = /data:(.*?);base64/.exec(meta)?.[1] || "image/png";
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new File([bytes], filename, { type: mime });
  };
  const uploadStickerImage = (dataUrl, filename) => {
    if (!isCloudinaryConfigured || !dataUrl?.startsWith("data:")) return Promise.resolve(dataUrl);
    try {
      return uploadToCloudinary(dataUrlToFile(dataUrl, filename)).then(r => r.url).catch(e => {
        console.warn("[Clarity] sticker upload failed, keeping local copy", e);
        return dataUrl;
      });
    } catch (e) {
      console.warn("[Clarity] sticker upload failed, keeping local copy", e);
      return Promise.resolve(dataUrl);
    }
  };

  // Reads the note's stickers fresh at write time (via updateActiveNote's
  // functional form), not from the `activeStickers` snapshot above — this is
  // called from the async Cloudinary upload callbacks, which resume well
  // after the render that captured that snapshot. Using the stale snapshot
  // here previously caused a just-added sticker to vanish: the upload
  // finishes, the callback's map() finds no match in the old (pre-addition)
  // list, and the note gets rewritten back to that old list — deleting the
  // sticker within seconds of adding it.
  const updateSticker = (id, patch) => {
    updateActiveNote(n => ({ stickers: (n.stickers || []).map(s => s.id === id ? { ...s, ...patch } : s) }));
  };

  const deleteSticker = (id) => {
    updateActiveNote(n => ({ stickers: (n.stickers || []).filter(s => s.id !== id) }));
    setSelectedStickerId(null);
  };

  const saveMyStickers = (newList) => {
    setMyStickers(newList);
    try { localStorage.setItem("clarity_my_stickers", JSON.stringify(newList)); } catch {}
  };

  // `clarity_diary` syncs as ONE field holding every note as a single JSON
  // blob (see src/firebase/sync.js) — so even one leftover embedded photo
  // (added before the Cloudinary upload existed, or an upload that silently
  // failed) can push that single blob over Firestore's 1MB document cap and
  // block syncing for every note, not just the one with the image. New
  // stickers upload immediately (see uploadStickerImage above), but that
  // doesn't retroactively fix anything already stuck as a raw data URL from
  // before. Sweep every note once on mount and upload/swap any leftovers —
  // this is what actually un-blocks sync for accounts that hit this.
  useEffect(() => {
    if (!isCloudinaryConfigured) return;
    let cancelled = false;
    const targets = [];
    Object.values(entries).forEach(notes => (notes || []).forEach(n => (n.stickers || []).forEach(s => {
      if (s.content?.startsWith("data:")) targets.push({ noteId: n.id, stickerId: s.id, content: s.content });
    })));
    if (targets.length) (async () => {
      for (const t of targets) {
        if (cancelled) return;
        const url = await uploadStickerImage(t.content, `sticker-${t.stickerId}.jpg`);
        if (cancelled || url === t.content) continue;
        setEntries(prev => {
          const next = { ...prev };
          Object.keys(next).forEach(date => {
            next[date] = next[date].map(n => n.id !== t.noteId ? n : { ...n, stickers: (n.stickers || []).map(s => s.id === t.stickerId ? { ...s, content: url } : s) });
          });
          return next;
        });
      }
    })();
    // Also sweep the "My Stickers" recents list — same risk, different key.
    setMyStickers(prev => {
      const dataUrlOnes = prev.filter(u => u?.startsWith("data:"));
      if (!dataUrlOnes.length) return prev;
      (async () => {
        for (const oldUri of dataUrlOnes) {
          if (cancelled) return;
          const url = await uploadStickerImage(oldUri, `mysticker-${Date.now()}.png`);
          if (cancelled || url === oldUri) continue;
          setMyStickers(list => {
            const next = list.map(u => u === oldUri ? url : u);
            try { localStorage.setItem("clarity_my_stickers", JSON.stringify(next)); } catch {}
            return next;
          });
        }
      })();
      return prev;
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // one-time sweep of whatever's loaded at mount — new stickers already upload on creation

  const imgOptBtn = { display:"flex", alignItems:"center", width:"100%", padding:"12px 14px", fontSize:13, fontWeight:700, borderRadius:12, border:"1.5px solid #f0d8e8", background:"#fff", color:"var(--c-text)", cursor:"pointer", fontFamily:"inherit", textAlign:"left" };

  // Attach an image as a movable/resizable/rotatable/croppable overlay object.
  // `frame` controls the silhouette (none / rounded / circle / heart / star / …);
  // the StickerLayer handles all the gestures. We measure the natural aspect so
  // un-framed images keep their real proportions.
  const addImageSticker = (src, frame = "none") => {
    const im = new window.Image(); // window.Image — `Image` is a lucide icon here
    const place = (aspect) => {
      const newS = addSticker({ type:"image", content:src, frame, aspect, size: 200 / STICKER_LEGACY_BASE_W });
      uploadStickerImage(src, `sticker-${newS.id}.jpg`).then(url => {
        if (url !== src) updateSticker(newS.id, { content: url });
      });
    };
    im.onload  = () => place((im.naturalWidth / im.naturalHeight) || 1);
    im.onerror = () => showPickTip(ONEDRIVE_TIP);
    im.src = src;
  };

  // Place one of "Your stickers". It used to go in as a square (aspect 1), so
  // any cut-out that isn't square was squashed and changed shape (Jade,
  // 2026-10-17). Measure it like a photo, and mark it custom so Edit offers a
  // re-trace of it.
  const addSavedSticker = (uri) => {
    const im = new window.Image();
    const place = (aspect) => addSticker({ type:"image", content:uri, frame:"none", aspect, size: 120 / STICKER_LEGACY_BASE_W, custom:true });
    im.onload  = () => place((im.naturalWidth / im.naturalHeight) || 1);
    im.onerror = () => place(1);
    im.src = uri;
  };

  // A strip of washi tape, a little tilted like real tape. Length and thickness
  // are set by its end grips and corner handle (see StickerLayer).
  const addTape = (tape) => addSticker({
    type: "tape", tape,
    w: TAPE_DEFAULT_W / STICKER_LEGACY_BASE_W, h: TAPE_DEFAULT_H / STICKER_LEGACY_BASE_W,
    rotation: -4,
  });

  // One of the "Doodle" set. Placed like a photo (its own shape, stretch and
  // crop handles), from the app's own files, so nothing is uploaded.
  const addDoodle = (d) => addSticker({
    type: "image", content: doodleSrc(d.id), frame: "none",
    aspect: d.w / d.h, size: doodlePlaceWidth(d) / STICKER_LEGACY_BASE_W,
  });

  const applyHeading = (tag) => {
    // Heading levels don't apply to the title — it's already a single-line,
    // fixed-size heading of its own.
    if (inTitle()) return;
    const tb = inTextbox();
    if (tb) ensureTextboxSelection(); else { restoreRange(); editorRef.current?.focus(); }
    document.execCommand("formatBlock", false, tag === "p" ? "p" : tag);
    updateFormats();
    if (!tb) scheduleAutosave();
  };

  const applyFont = (fontName) => {
    // A text box or the title takes the font as a whole (or just the
    // highlighted part), so it needn't keep the cursor: on the phone the panel
    // stays where it is and the keyboard stays down (Jade, 2026-10-28: picking
    // a font for a text box brought the keyboard up under the open panel).
    // Tapping the note brings the keyboard back.
    if (inTextbox()) {
      ensureTextboxSelection(); // collapsed → whole box; otherwise just the highlight
      if (fontName === "normal") document.execCommand("removeFormat", false, null);
      else document.execCommand("fontName", false, fontName);
      updateFormats();
      keepPanel();
      return;
    }
    if (inTitle()) {
      ensureTitleSelection();
      document.execCommand("styleWithCSS", false, true);
      if (fontName === "normal") document.execCommand("removeFormat", false, null);
      else document.execCommand("fontName", false, fontName);
      document.execCommand("styleWithCSS", false, false);
      updateFormats();
      scheduleAutosave();
      keepPanel();
      return;
    }
    restoreRange();
    editorRef.current?.focus();
    // Apply via execCommand so it works on a real selection AND as a "pending"
    // inline format when the caret is collapsed — i.e. pick a font first, then the
    // next characters you type come out in that font (Google-Docs behavior).
    document.execCommand("styleWithCSS", false, true);
    if (fontName === "normal") document.execCommand("removeFormat", false, null);
    else document.execCommand("fontName", false, fontName);
    document.execCommand("styleWithCSS", false, false);
    updateFormats();
    scheduleAutosave();
  };

  // Apply a px font-size to the current selection only. Uses the execCommand
  // font-size=7 marker trick to tag exactly the selected run, then re-styles
  // those elements with the real px value. Does nothing when there is no
  // selection (collapsed caret) — the user must highlight text first.
  const applyPxFontSize = (px, keepRange = false) => {
    const unwrapInner = (el) => {
      el.querySelectorAll("font[style*=\'font-size\']").forEach(inner => {
        const p = inner.parentNode;
        while (inner.firstChild) p.insertBefore(inner.firstChild, inner);
        p.removeChild(inner);
      });
    };
    // Title stays a fixed heading size — no per-character font-size on it.
    if (inTitle()) return;
    const tb = inTextbox();
    if (tb) {
      ensureTextboxSelection();
      const host = document.activeElement;
      if (!host) return;
      const pre = new Set(Array.from(host.querySelectorAll("font[size=\'7\']")));
      document.execCommand("fontSize", false, "7");
      host.querySelectorAll("font[size=\'7\']").forEach(s => {
        if (!pre.has(s)) { s.removeAttribute("size"); s.style.fontSize = px + "px"; unwrapInner(s); }
      });
      updateFormats();
      return;
    }
    restoreRange();
    editorRef.current?.focus();
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount || sel.getRangeAt(0).collapsed) return;
    const host = editorRef.current;
    const pre = new Set(Array.from(host.querySelectorAll("font[size=\'7\']")));
    document.execCommand("fontSize", false, "7");
    host.querySelectorAll("font[size=\'7\']").forEach(s => {
      if (!pre.has(s)) { s.removeAttribute("size"); s.style.fontSize = px + "px"; unwrapInner(s); }
    });
    saveRange();
    updateFormats();
    if (!keepRange) scheduleAutosave();
  };
  const applyHighlight = (color) => {
    const tb = inTextbox();
    const tt = !tb && inTitle();
    if (tb) ensureTextboxSelection(); else if (tt) ensureTitleSelection(); else { restoreRange(); editorRef.current?.focus(); }
    if (!color) { document.execCommand("hiliteColor", false, "transparent"); }
    else { try { document.execCommand("hiliteColor", false, color); } catch(e) { document.execCommand("backColor", false, color); } }
    updateFormats();
    if (!tb) scheduleAutosave();
  };

  const applyTextColor = (color) => {
    const tb = inTextbox();
    const tt = !tb && inTitle();
    if (tb) ensureTextboxSelection(); else if (tt) ensureTitleSelection(); else { restoreRange(); editorRef.current?.focus(); }
    try {
      document.execCommand("styleWithCSS", false, true);
      document.execCommand("foreColor", false, color);
      document.execCommand("styleWithCSS", false, false);
    } catch(e) { document.execCommand("foreColor", false, color); }
    updateFormats();
    if (!tb) scheduleAutosave();
  };

  const insertDivider = () => {
    restoreRange();
    editorRef.current?.focus();
    document.execCommand("insertHTML", false, "<hr/><p><br></p>");
    scheduleAutosave();
  };

  const insertMedia = (att) => {
    restoreRange();
    editorRef.current?.focus();
    document.execCommand("insertHTML", false, mediaToHtml(att));
    scheduleAutosave();
  };
  insertMediaRef.current = insertMedia;

  // Robust image insert that works on mobile. After the gallery picker closes
  // the contentEditable usually loses its caret, so execCommand("insertImage")
  // silently fails (nothing to insert into). Instead we insert a real <img>
  // node straight into the DOM at the saved caret, falling back to appending
  // at the end of the editor. Returns the inserted <img> so callers can later
  // swap its src (e.g. after a background downscale).
  // Try to shrink a data URL via canvas (mobile photos are huge and would
  // overflow localStorage). Resolves with the smaller JPEG, or the original
  // src if the image couldn't be decoded/drawn (e.g. HEIC on some browsers).
  const downscaleDataUrl = (src) => new Promise((resolve) => {
    const im = new window.Image();
    im.onload = () => {
      const MAX = 1400; // cap on the longest edge
      let { width, height } = im;
      if (!width || !height) { resolve(src); return; }
      if (width > MAX || height > MAX) {
        const scale = MAX / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      try {
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(im, 0, 0, width, height);
        const out = canvas.toDataURL("image/jpeg", 0.82);
        resolve(out && out.length > 32 && out.length < src.length ? out : src);
      } catch (err) { resolve(src); }
    };
    im.onerror = () => resolve(src);
    im.src = src;
  });

  // Read a picked image file, downscale it, then open the attach-options modal
  // so the photo can be added as a movable/resizable/croppable overlay (or cut
  // into a custom sticker with the lasso).
  const handleImageFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const src = ev.target?.result;
      if (!src) { showPickTip(ONEDRIVE_TIP); return; }
      downscaleDataUrl(src).then((small) => {
        setImageOptStep("choose");
        setPendingImage(small || src);
      });
    };
    reader.onerror = () => showPickTip(ONEDRIVE_TIP);
    reader.readAsDataURL(file);
  };

  const insertChecklist = () => {
    restoreRange();
    editorRef.current?.focus();
    // Wrapper is contenteditable=false (so the tick box is clickable, not edited);
    // the label span stays editable. A delegated click handler toggles the box.
    const html = `<div class="jrnl-check" contenteditable="false" style="display:flex;align-items:flex-start;gap:9px;margin:6px 0"><span class="jrnl-check-box" style="width:19px;height:19px;border:2px solid #2a2a2a;border-radius:6px;flex-shrink:0;margin-top:1px;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;font-size:13px;font-weight:800;color:#fff;line-height:1"></span><span class="jrnl-check-label" contenteditable="true" style="outline:none;flex:1;line-height:1.55"><br></span></div><p><br></p>`;
    document.execCommand("insertHTML", false, html);
    scheduleAutosave();
  };

  const reattachChecklists = () => {};

  // Inserts one or more pasted links as small pills (site name only, e.g.
  // "canva.com"), each stacked on its own line — no network call, labels are
  // derived straight from the URLs. The Range was saved when the Insert
  // dropdown opened (the modal steals focus/selection from the editor), so
  // it's restored right before inserting.
  const addEmbedRow    = () => setEmbedRows(rows => [...rows, { url:"", error:"" }]);
  const removeEmbedRow = (idx) => setEmbedRows(rows => rows.length > 1 ? rows.filter((_,i)=>i!==idx) : rows);
  const updateEmbedRow = (idx, patch) => setEmbedRows(rows => rows.map((r,i)=> i===idx ? {...r, ...patch, error:""} : r));
  const submitEmbed = () => {
    let hasError = false;
    const checked = embedRows.map(r => {
      const url = r.url.trim();
      if (!url) return { ...r, error:"" };
      if (!isValidHttpUrl(url)) { hasError = true; return { ...r, error:"Enter a valid link (https://…)" }; }
      return { ...r, error:"" };
    });
    const valid = checked.filter(r => r.url.trim());
    if (hasError || !valid.length) {
      setEmbedRows(hasError ? checked : checked.map((r,i)=> i===0 ? { ...r, error:"Add at least one link" } : r));
      return;
    }
    const html = valid.map(r => buildEmbedPillHtml(r.url.trim())).join("") + "<p><br></p>";
    restoreRange();
    document.execCommand("insertHTML", false, html);
    scheduleAutosave();
    setInsertEmbedMode(false);
    setShowInsertDd(false);
  };

  // ── Markdown auto-format (input rules) ─────────────────────────────────────
  // Watches what you type and transforms it on the fly, Notion/Docs-style:
  //   "- " / "* " / "+ "  → bullet list      "1. " → numbered list
  //   "# " / "## " / "### " → heading        "> "  → blockquote
  //   "[] " / "[ ] "      → checklist         "---" → divider
  //   **bold**  _italic_  ~~strike~~          → inline marks
  const BLOCK_TAGS = /^(P|DIV|H1|H2|H3|H4|H5|H6|LI|BLOCKQUOTE|PRE)$/;
  const getBlockEl = (node) => {
    let el = node && node.nodeType === 3 ? node.parentNode : node;
    const ed = editorRef.current;
    while (el && el !== ed && !BLOCK_TAGS.test(el.nodeName)) el = el.parentNode;
    return (el && el !== ed) ? el : null;
  };

  // Inline marks: select the "**inner**" run, replace with plain inner text,
  // apply the mark to it, then collapse and clear the active mark so typing
  // continues unformatted.
  const tryInlineRule = (range) => {
    const tn = range.startContainer;
    if (tn.nodeType !== 3) return false;
    const off = range.startOffset;
    const before = tn.textContent.slice(0, off);
    const rules = [
      { re: /\*\*([^*]+)\*\*$/,        cmd: "bold" },
      { re: /(?<![\w*])\*([^*\s][^*]*)\*$/, cmd: "italic" },
      { re: /(?<![\w_])_([^_]+)_$/,    cmd: "italic" },
      { re: /~~([^~]+)~~$/,            cmd: "strikeThrough" },
    ];
    for (const { re, cmd } of rules) {
      const m = before.match(re);
      if (!m) continue;
      const inner = m[1];
      const start = off - m[0].length;
      if (start < 0) continue;
      const r = document.createRange();
      r.setStart(tn, start); r.setEnd(tn, off);
      const sel = window.getSelection();
      sel.removeAllRanges(); sel.addRange(r);
      document.execCommand("insertText", false, inner);
      // Re-select the inserted inner text and apply the mark
      const after = window.getSelection();
      if (after.rangeCount) {
        const c = after.getRangeAt(0);
        const endOff = c.startOffset, sNode = c.startContainer;
        const startOff = Math.max(0, endOff - inner.length);
        const r2 = document.createRange();
        r2.setStart(sNode, startOff); r2.setEnd(sNode, endOff);
        after.removeAllRanges(); after.addRange(r2);
        document.execCommand(cmd);
        after.collapseToEnd();
        // Clear the mark so the next characters aren't formatted
        if (document.queryCommandState(cmd)) document.execCommand(cmd);
      }
      return true;
    }
    return false;
  };

  const runInputRules = () => {
    const sel = window.getSelection();
    if (!sel || !sel.isCollapsed || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    const block = getBlockEl(range.startContainer);
    if (!block) return;
    // Never auto-format inside a checklist label
    if (block.closest && block.closest(".jrnl-check")) return;

    // Text from the start of the current block to the caret
    const preRange = document.createRange();
    preRange.setStart(block, 0);
    preRange.setEnd(range.endContainer, range.endOffset);
    const textBefore = preRange.toString();

    const insideList = block.nodeName === "LI";

    const clearTrigger = () => { preRange.deleteContents(); };

    // ── Block-level rules ──────────────────────────────────────────────
    if (!insideList) {
      // Divider — three dashes on their own line
      if (textBefore === "---") {
        clearTrigger();
        document.execCommand("insertHTML", false, "<hr/><p><br></p>");
        scheduleAutosave();
        return;
      }
      // Bullet list
      if (/^[-*+] $/.test(textBefore)) {
        clearTrigger();
        document.execCommand("insertUnorderedList");
        scheduleAutosave();
        return;
      }
      // Numbered list
      if (/^\d+\. $/.test(textBefore)) {
        clearTrigger();
        document.execCommand("insertOrderedList");
        scheduleAutosave();
        return;
      }
      // Headings
      const h = textBefore.match(/^(#{1,3}) $/);
      if (h) {
        clearTrigger();
        document.execCommand("formatBlock", false, "h" + h[1].length);
        scheduleAutosave();
        return;
      }
      // Blockquote
      if (textBefore === "> ") {
        clearTrigger();
        document.execCommand("formatBlock", false, "blockquote");
        scheduleAutosave();
        return;
      }
      // Checklist
      if (textBefore === "[] " || textBefore === "[ ] ") {
        clearTrigger();
        insertChecklist();
        return;
      }
    }

    // ── Inline marks (work anywhere, including inside lists) ───────────
    if (tryInlineRule(range)) scheduleAutosave();
  };

  // With the VirtualKeyboard API in overlay mode the browser won't auto-scroll
  // the caret above the keyboard (it treats the whole viewport as visible), so
  // we nudge the editor's own scroll to keep the caret in the open strip
  // between the top and the toolbar/keyboard.
  //
  // Native (Android app) used to just return here on the assumption that
  // windowSoftInputMode="adjustResize" always shrinks window.innerHeight to
  // end at the keyboard's top edge, so the browser's own caret-follow would
  // handle it with no help needed. In practice that resize sometimes lands a
  // beat after a brand-new line is inserted at the very bottom of the note
  // (pressing Enter on the last line), so the caret-follow fires against the
  // still-full-height viewport and the new line ends up hidden behind the
  // keyboard until you scroll manually — intermittent because it's a race,
  // not a hard failure. Running the same correction there too (using the
  // already-shrunk innerHeight as the reference, since adjustResize did
  // eventually catch up) is a harmless backstop.
  const keepCaretVisible = () => {
    const kb = kbHeightRef.current || 0;
    if (!vkSupportedRef.current && !(isNative && kb > 0)) return; // web fallback path scrolls natively
    const sel = typeof window !== "undefined" && window.getSelection && window.getSelection();
    if (!sel || !sel.rangeCount) return;
    let rect = sel.getRangeAt(0).getBoundingClientRect();
    // Collapsed carets can report a zero rect; fall back to the focused element.
    if (!rect || (rect.top === 0 && rect.bottom === 0 && rect.left === 0)) {
      const el = sel.anchorNode && (sel.anchorNode.nodeType === 1 ? sel.anchorNode : sel.anchorNode.parentElement);
      if (el && el.getBoundingClientRect) rect = el.getBoundingClientRect();
    }
    if (!rect) return;
    const TOOLBAR = 56;                             // toolbar height above the keyboard
    // On native, adjustResize already shrinks window.innerHeight down to the
    // keyboard's top edge, so subtracting kb again here would double-count
    // it. The VirtualKeyboard-overlay path keeps the full viewport height
    // instead, so kb still needs subtracting there.
    const safeBottom = vkSupportedRef.current ? (window.innerHeight - kb - TOOLBAR - 8) : (window.innerHeight - TOOLBAR - 8);
    const area = editorAreaRef.current;
    if (!area) return;
    if (rect.bottom > safeBottom) area.scrollTop += (rect.bottom - safeBottom);
  };

  const onEditorInput = () => {
    runInputRules();
    scheduleAutosave();
    updateFormats();
    keepCaretVisible();
    // A new line at the very bottom (e.g. pressing Enter on the note's last
    // line) can land before the editor area's own bottom padding — which
    // reserves the keyboard-height gap via the CSS env(keyboard-inset-height)
    // var — has actually caught up, so this first scroll can get clamped to
    // a scrollHeight that hasn't grown yet and the new line stays hidden
    // behind the keyboard until a manual scroll. One retry once that padding
    // has had a beat to settle fixes it without a visible extra jump.
    setTimeout(keepCaretVisible, 60);
  };

  // When the keyboard opens (or grows) under the VirtualKeyboard API, pull the
  // caret up into the visible strip so you never type behind the keyboard.
  useEffect(() => {
    if (kbHeight > 0) requestAnimationFrame(keepCaretVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kbHeight]);

  const onEditorKeyDown = (e) => {
    // Undo / redo — handle ourselves so the native (broken) execCommand undo
    // can't wipe the whole note. Cmd/Ctrl+Z = undo, +Shift = redo, Ctrl+Y = redo.
    if ((e.metaKey || e.ctrlKey) && (e.key === "z" || e.key === "Z")) {
      e.preventDefault();
      if (e.shiftKey) doRedo(); else doUndo();
      return;
    }
    if ((e.metaKey || e.ctrlKey) && (e.key === "y" || e.key === "Y")) {
      e.preventDefault();
      doRedo();
      return;
    }
    // Flush history at paragraph/word boundaries so each segment is independently
    // undoable — the same principle Google Docs uses (new undo step at Enter/Space).
    if (e.key === "Enter" || e.key === " ") {
      flushHistory();
    }
    // When the format (Aa) panel is open, intercept digit/backspace/enter so the
    // user can type a font size WITHOUT moving focus away from the editor — the
    // selection stays visually highlighted the whole time. Desktop-only: it
    // depends on a physical keyboard driving the editor's focus, which on
    // mobile would mean summoning the on-screen keyboard right on top of this
    // toolbar — exactly what the +/- stepper is there to avoid.
    if (showHeadingDd && !showFontDd && !showStyleDd && !isMobile) {
      if (/^\d$/.test(e.key)) {
        e.preventDefault();
        let newSize;
        if (!typingSizeRef.current) {
          newSize = parseInt(e.key, 10);
          typingSizeRef.current = true;
        } else {
          const s = String(fontSize === "" ? "" : fontSize);
          newSize = parseInt((s + e.key).slice(-3), 10);
          if (isNaN(newSize)) newSize = parseInt(e.key, 10);
        }
        setFontSize(newSize || "");
        applyPxFontSize(Math.max(6, Math.min(200, newSize || 6)), true);
        return;
      }
      if (e.key === "Backspace") {
        e.preventDefault();
        setFontSize(prev => {
          const s = String(prev).slice(0, -1);
          return s === "" ? "" : (parseInt(s, 10) || "");
        });
        return;
      }
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        const v = Math.max(6, Math.min(200, parseInt(fontSize, 10) || 16));
        setFontSize(v);
        applyPxFontSize(v);
        return;
      }
    }
    // Tab / Shift+Tab → indent / outdent (nest & un-nest list items)
    if (e.key === "Tab") {
      e.preventDefault();
      document.execCommand(e.shiftKey ? "outdent" : "indent");
      scheduleAutosave();
      return;
    }
    // Shift+Enter → soft line break instead of a new paragraph
    if (e.key === "Enter" && e.shiftKey) {
      e.preventDefault();
      document.execCommand("insertLineBreak");
      scheduleAutosave();
      return;
    }
    // Enter inside a checklist label → insert a new checklist row below
    if (e.key === "Enter" && !e.shiftKey) {
      const sel = window.getSelection();
      if (sel && sel.isCollapsed && sel.rangeCount) {
        const range = sel.getRangeAt(0);
        const node = range.startContainer;
        const el = node.nodeType === 3 ? node.parentElement : node;
        const checkLabel = el && el.closest && el.closest(".jrnl-check-label");
        if (checkLabel) {
          e.preventDefault();
          const row = checkLabel.closest(".jrnl-check");
          // Empty label → exit checklist
          if (checkLabel.textContent.trim() === "") {
            const p = document.createElement("p");
            p.innerHTML = "<br>";
            row.after(p);
            row.remove();
            const r2 = document.createRange();
            r2.setStart(p, 0);
            r2.collapse(true);
            sel.removeAllRanges();
            sel.addRange(r2);
            scheduleAutosave();
            return;
          }
          const newRow = document.createElement("div");
          newRow.className = "jrnl-check";
          newRow.contentEditable = "false";
          newRow.setAttribute("style", "display:flex;align-items:flex-start;gap:9px;margin:6px 0");
          const newBox = document.createElement("span");
          newBox.className = "jrnl-check-box";
          newBox.setAttribute("style", "width:19px;height:19px;border:2px solid #2a2a2a;border-radius:6px;flex-shrink:0;margin-top:1px;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;font-size:13px;font-weight:800;color:#fff;line-height:1");
          const newLabel = document.createElement("span");
          newLabel.className = "jrnl-check-label";
          newLabel.contentEditable = "true";
          newLabel.setAttribute("style", "outline:none;flex:1;line-height:1.55");
          newLabel.innerHTML = "<br>";
          newRow.appendChild(newBox);
          newRow.appendChild(newLabel);
          row.after(newRow);
          newLabel.focus();
          const r = document.createRange();
          r.setStart(newLabel, 0);
          r.collapse(true);
          sel.removeAllRanges();
          sel.addRange(r);
          scheduleAutosave();
          return;
        }
      }
    }
    // Enter on an empty list item → exit the list
    if (e.key === "Enter" && !e.shiftKey) {
      const sel = window.getSelection();
      if (sel && sel.isCollapsed && sel.rangeCount) {
        const block = getBlockEl(sel.getRangeAt(0).startContainer);
        if (block && block.nodeName === "LI" && block.textContent.trim() === "") {
          e.preventDefault();
          document.execCommand("outdent");
          scheduleAutosave();
          return;
        }
      }
    }

    if (e.key !== "Backspace") return;
    const sel = window.getSelection();
    if (!sel || !sel.isCollapsed || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    const sc = range.startContainer;
    const el = sc.nodeType === 3 ? sc.parentElement : sc;

    // Case 1: caret at the very START of a checklist row's label. Don't merge
    // into the previous line — instead drop the checkbox and turn THIS row into
    // a normal text line in place, keeping any text it held. That's the only way
    // back to writing normally once a checkbox exists; the line stays put.
    const label = el && el.closest && el.closest(".jrnl-check-label");
    if (label && editorRef.current && editorRef.current.contains(label)) {
      const probe = range.cloneRange();
      probe.selectNodeContents(label);
      probe.setEnd(range.startContainer, range.startOffset);
      const atStart = probe.toString().length === 0;
      if (atStart) {
        e.preventDefault();
        const row = label.closest(".jrnl-check");
        const line = document.createElement("div");
        line.setAttribute("style", "line-height:1.55");
        while (label.firstChild) line.appendChild(label.firstChild);
        if (!line.firstChild) line.appendChild(document.createElement("br"));
        row.replaceWith(line);
        const r = document.createRange();
        r.setStart(line, 0);
        r.collapse(true);
        sel.removeAllRanges();
        sel.addRange(r);
        scheduleAutosave();
        return;
      }
    }

    // Remaining cases only fire at the very start of a block.
    if (range.startOffset !== 0) return;

    // Case 2: caret at the start of the line that immediately follows a
    // checklist row → remove that preceding checklist row.
    let node = sc;
    while (node && node.parentNode !== editorRef.current) node = node.parentNode;
    const prev = node && node.previousSibling;
    if (prev && prev.classList && prev.classList.contains("jrnl-check")) {
      e.preventDefault();
      prev.remove();
      scheduleAutosave();
    }
  };

  // Long-press an embedded link pill to reveal a small "Remove link" menu —
  // the pill itself is too small to fit a permanent x button, so the menu is
  // summoned on demand instead. A short tap still opens the link as usual;
  // linkMenuFiredRef distinguishes the two by whether the hold timer completed.
  const linkMenuTimerRef = useRef(null);
  const linkMenuFiredRef = useRef(false);
  const linkMenuStartRef = useRef({ x:0, y:0 });
  const [linkMenu, setLinkMenu] = useState(null); // { x, y, wrapper }
  const clearLinkMenuTimer = () => { if (linkMenuTimerRef.current) { clearTimeout(linkMenuTimerRef.current); linkMenuTimerRef.current = null; } };
  const onEditorPointerDown = (e) => {
    const anchor = e.target.closest && e.target.closest("a.cj-embed-pill");
    if (!anchor) return;
    linkMenuFiredRef.current = false;
    linkMenuStartRef.current = { x: e.clientX, y: e.clientY };
    clearLinkMenuTimer();
    linkMenuTimerRef.current = setTimeout(() => {
      linkMenuFiredRef.current = true;
      const wrapper = anchor.closest("[contenteditable='false']") || anchor;
      const r = anchor.getBoundingClientRect();
      setLinkMenu({ x: r.left, y: r.bottom + 6, wrapper });
    }, 500);
  };
  const onEditorPointerMove = (e) => {
    if (!linkMenuTimerRef.current) return;
    const dx = e.clientX - linkMenuStartRef.current.x, dy = e.clientY - linkMenuStartRef.current.y;
    if (Math.hypot(dx, dy) > 10) clearLinkMenuTimer();
  };
  const removeEmbeddedLink = () => {
    if (!linkMenu || !linkMenu.wrapper) return;
    const wrapper = linkMenu.wrapper;
    const next = wrapper.nextSibling;
    if (next && next.nodeType === 3 && /^[\s ]+$/.test(next.textContent)) next.remove();
    wrapper.remove();
    setLinkMenu(null);
    scheduleAutosave();
  };

  // Toggle a checklist tick when its box is clicked (works inside contentEditable)
  const onEditorClick = (e) => {
    if (linkMenuFiredRef.current) { e.preventDefault(); linkMenuFiredRef.current = false; return; }
    const box = e.target.closest && e.target.closest(".jrnl-check-box");
    if (!box) {
      // contentEditable normally swallows link clicks (just places the caret) —
      // intercept and open it for real, same as RichNotesEditor's link handling.
      const anchor = e.target.closest && e.target.closest("a[href]");
      if (anchor) { e.preventDefault(); window.open(anchor.href, "_blank", "noopener,noreferrer"); }
      return;
    }
    const label = box.parentElement && box.parentElement.querySelector(".jrnl-check-label");
    const checked = box.getAttribute("data-checked") === "1";
    if (checked) {
      box.removeAttribute("data-checked");
      box.textContent = "";
      box.style.background = "transparent";
      if (label) { label.style.textDecoration = "none"; label.style.opacity = "1"; }
    } else {
      box.setAttribute("data-checked", "1");
      box.textContent = "✓";
      box.style.background = "#2a2a2a";
      if (label) { label.style.textDecoration = "line-through"; label.style.opacity = "0.55"; }
    }
    scheduleAutosave();
  };


  // Native Android: the browser Blob + <a download> trick below is a no-op
  // inside a Capacitor WebView (no download manager, blob: URLs don't resolve
  // to a save action) — write the file to disk via @capacitor/filesystem and
  // hand it to the real Android share sheet via @capacitor/share instead.
  // `data` is a plain UTF-8 string for text formats, or base64 for binary
  // (pdf) — pass encoding:"utf8" only for the former.
  const saveAndShareNative = async (filename, data, encoding) => {
    const [{ Filesystem, Directory, Encoding }, { Share }] = await Promise.all([
      import("@capacitor/filesystem"), import("@capacitor/share"),
    ]);
    const res = await Filesystem.writeFile({
      path: filename, data, directory: Directory.Cache,
      encoding: encoding === "utf8" ? Encoding.UTF8 : undefined,
    });
    await Share.share({ title: filename, url: res.uri });
  };


  const exportNote = (fmt) => {
    if (!activeNote) return;
    const title = (titleRef.current?.innerText || activeNote.title || "journal").trim() || "journal";
    const html = editorRef.current?.innerHTML || activeNote.htmlContent || "";
    const text = editorRef.current?.innerText || activeNote.content || "";
    const typedTitle = (titleRef.current?.innerText || activeNote.title || "").trim();
    if (fmt === "txt") {
      if (isNative) { saveAndShareNative(title+".txt", text, "utf8"); }
      else { const b=new Blob([text],{type:"text/plain"}); const a=document.createElement("a"); a.href=URL.createObjectURL(b); a.download=title+".txt"; a.click(); }
    }
    else if (fmt === "md") {
      // Headings, bold / italic, lists, checklists, links and images in
      // Markdown (Jade, 2026-10-28: it used to be the same plain words as .txt).
      const md = noteToMarkdown({ title: typedTitle, html });
      if (isNative) { saveAndShareNative(title+".md", md, "utf8"); }
      else { const b=new Blob([md],{type:"text/markdown"}); const a=document.createElement("a"); a.href=URL.createObjectURL(b); a.download=title+".md"; a.click(); }
    }
    else if (fmt === "html") {
      exportNoteAsHtml(title, html);
    }
    else if (fmt === "pdf") {
      if (!isPremium) { setShowMoreMenu(false); return; }
      // Build a clean, off-screen copy of the note at A4 width and hand it to
      // html2pdf, which downloads a real .pdf file directly (no print dialog).
      // We wait for any embedded images to finish decoding first — that was the
      // earlier failure mode that produced a blank/broken file.
      // Every page is filled edge to edge with the NOTE's own background (its
      // colour, gradient or photo), never the app's (Jade, 2026-10-18). It used to
      // sit only behind the text, in a box with white margins and a white rest of
      // the page. A photo note is drawn like on screen: white paper, image on top.
      const editorBgStr = (typeof editorBg === "string" && editorBg) ? editorBg : "#ffffff";
      const pageBgCss = photoBg
        ? `background-color:#ffffff;background-image:${photoImg};background-size:cover;background-position:center;background-repeat:no-repeat;`
        : `background:${editorBgStr};`;
      const fontFam = (activeNote.font && activeNote.font !== "normal")
        ? `'${activeNote.font}', sans-serif`
        : "'Quicksand','Nunito',sans-serif";
      const esc = (s) => String(s).replace(/[&<>]/g, c => ({ "&":"&amp;", "<":"&lt;", ">":"&gt;" }[c]));
      // CRITICAL: html2canvas measures an OUT-OF-FLOW element (position:fixed or
      // absolute) as zero height, so it captured a 0-px-tall canvas and produced
      // a blank PDF. The element handed to html2canvas must be `position:static`
      // (in normal flow) so its height is measured correctly. We therefore keep
      // `wrap` static and hide it inside an off-screen *container* instead — the
      // positioning lives on the holder, not the capture target. (Verified with
      // html2canvas 1.4.1: static target → full render; positioned target → 0px.)
      // The text is rendered on a transparent copy; each page is then put together
      // by hand (background, then that page's slice of text inside a margin) and
      // handed to html2pdf as ready-made full pages with no PDF margin.
      const PAGE_W = Math.round(210 / 25.4 * 96);   // A4 width in CSS px
      const PAGE_H = Math.round(297 / 25.4 * 96);
      const MARGIN = 48;                             // ~12.7mm on every side
      const W = PAGE_W - 2 * MARGIN;                 // text column width
      const holder = document.createElement("div");
      holder.style.cssText = `position:fixed;left:-10000px;top:0;width:${PAGE_W}px;z-index:-1;pointer-events:none;`;
      // note-light: dark mode's catch-alls must not repaint the copy (a white
      // note exported black, Jade 2026-10-17).
      holder.className = "note-light" + (darkNote ? " note-ink-light" : "");
      const wrap = document.createElement("div");
      // Static (no position) so html2canvas measures its height. `pre-wrap`
      // mirrors the editor (which uses white-space:pre-wrap) so the user's line
      // breaks/spacing are preserved, while long lines still wrap to the page.
      wrap.className = "clarity-pdf-export";
      wrap.style.cssText = `width:${W}px;background:transparent;padding:0;`
        + `font-family:${fontFam};font-size:16px;font-weight:500;line-height:1.8;color:${darkNote ? "#f1edf3" : "#2d1f26"};box-sizing:border-box;`
        + `white-space:pre-wrap;overflow-wrap:break-word;word-break:break-word;`;
      wrap.innerHTML =
        // All rules scoped to .clarity-pdf-export so they never touch the live app
        // during the off-screen render. The first rule guarantees nothing can
        // exceed the page width (wide elements, nowrap, long tokens, images) — the
        // cause of the right-edge text cut-off.
        `<style>.clarity-pdf-export, .clarity-pdf-export *{white-space:pre-wrap!important;overflow-wrap:break-word!important;word-break:break-word!important;max-width:100%!important;box-sizing:border-box!important}`
        + ` .clarity-pdf-export img,.clarity-pdf-export video,.clarity-pdf-export table,.clarity-pdf-export iframe{height:auto!important}`
        + ` .clarity-pdf-export img{border-radius:8px}`
        + ` .clarity-pdf-export ul,.clarity-pdf-export ol{padding-left:24px;margin:4px 0} .clarity-pdf-export li{margin:2px 0}`
        + ` .clarity-pdf-export hr{border:none;border-top:2px solid #e8c8d8;margin:16px 0}`
        + ` .clarity-pdf-export blockquote{margin:8px 0;padding:4px 0 4px 14px;border-left:3px solid #d4789a;font-style:italic}`
        + ` .clarity-pdf-export .jrnl-check{display:flex;align-items:flex-start;gap:9px;margin:6px 0}`
        + ` .clarity-pdf-export .jrnl-check-box{width:18px;height:18px;border:2px solid ${darkNote ? "#e0dae4" : "#2a2a2a"};border-radius:6px;flex-shrink:0}</style>`
        // Only a title the user typed: an untitled note gets no heading, not the
        // "journal" file-name fallback (Jade, 2026-10-17).
        + (typedTitle ? `<h1 style="font-size:24px;font-weight:800;margin:0 0 18px">${esc(typedTitle)}</h1>` : "")
        + html;
      // One page-sized copy of the note's background, captured once and reused.
      const pageBg = document.createElement("div");
      pageBg.style.cssText = `width:${PAGE_W}px;height:${PAGE_H}px;${pageBgCss}`;
      holder.appendChild(wrap);
      holder.appendChild(pageBg);
      document.body.appendChild(holder);
      const imgs = Array.from(wrap.querySelectorAll("img"));
      const ready = Promise.all(imgs.map(img => (img.complete && img.naturalWidth)
        ? Promise.resolve()
        : new Promise(res => { img.onload = img.onerror = res; })));
      const finish = () => { try { document.body.removeChild(holder); } catch (e) {} };
      const SCALE = 2;
      ready
        .then(() => Promise.all([import("html2canvas"), import("html2pdf.js")]))
        .then(([{ default: html2canvas }, { default: html2pdf }]) => Promise.all([
          html2canvas(wrap, { scale: SCALE, backgroundColor: null, useCORS: true, logging: false }),
          html2canvas(pageBg, { scale: SCALE, backgroundColor: null, useCORS: true, logging: false }),
        ]).then(([text, bgCanvas]) => {
          const pages = composeNotePages(text, bgCanvas, MARGIN * SCALE);
          const worker = html2pdf().set({
            margin: 0,
            filename: (title || "journal") + ".pdf",
            image: { type: "jpeg", quality: 0.98 },
            jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          }).from(pages, "canvas");
          if (isNative) {
            // .save() drives the same browser-only download trick — pull the
            // PDF out as a data URI instead and hand its base64 payload to the
            // native filesystem+share path above.
            return worker.outputPdf("datauristring").then(dataUri =>
              saveAndShareNative((title || "journal") + ".pdf", dataUri.split(",")[1]));
          }
          return worker.save();
        }))
        .then(finish)
        .catch((err) => { finish(); console.error("PDF export failed:", err); });
    }
    else if (fmt === "image") {
      if (!isPremium) { setShowMoreMenu(false); return; }
      exportNoteAsImage(title);
    }
    setShowMoreMenu(false);
  };

  // Mobile-only "save note as one long image" export. Unlike the PDF export
  // above (which rebuilds the note from raw title/html strings, so it can
  // flow across printable pages), this clones the ACTUAL live note view —
  // title, body, stickers and background exactly as rendered on screen — so
  // the exported image is pixel-identical to the note, not a re-typeset copy.
  // A copy of the note as it's drawn on screen, laid out off screen at its
  // real width and full height, without the date line or an empty title, for
  // Save as Image and Export .html. The caller removes `holder` when done.
  // Deselect stickers (and wait a frame) before calling, so no handles are copied.
  const makeNoteCopy = () => {
    const src = editorAreaRef.current;
    if (!src) return null;
    const captureWidth = Math.max(360, Math.round(src.getBoundingClientRect().width)) || 420;
    // The page's height on screen: a short note still exports as the whole
    // page you see, not just its lines of text (Jade, 2026-10-28).
    const pageHeight = Math.round(src.clientHeight) || 0;
    const clone = src.cloneNode(true);
    // Undo the live element's scroll/viewport constraints so the WHOLE
    // note — not just whatever's currently scrolled into view — renders
    // as one continuous (long) image at its natural height.
    // Relative (not static), so the sticker layer (inset 0) is laid out on
    // the copy itself and grows with it below.
    clone.style.position = "relative";
    clone.style.overflow = "visible";
    clone.style.overflowY = "visible";
    clone.style.height = "auto";
    clone.style.maxHeight = "none";
    clone.style.width = captureWidth + "px";
    // The live page adds room for the keyboard / an open panel at the bottom.
    clone.style.paddingBottom = "24px";
    clone.querySelectorAll("[contenteditable]").forEach(el => el.removeAttribute("contenteditable"));

    const holder = document.createElement("div");
    holder.style.cssText = `position:fixed;left:-10000px;top:0;width:${captureWidth}px;z-index:-1;pointer-events:none;`;
    holder.className = "note-light" + (darkNote ? " note-ink-light" : ""); // keep the note's own colours in dark mode
    // The note's real background (color/gradient) lives on an ancestor of
    // editorAreaRef, not on editorAreaRef itself — reproduce it here so
    // the clone matches exactly. A photo/wallpaper background is already
    // painted on editorAreaRef via its own className/CSS var, carried over
    // by cloneNode, so this wrapper's background only matters for the
    // plain color/gradient case.
    const bgWrap = document.createElement("div");
    bgWrap.style.cssText = `width:${captureWidth}px;background:${photoBg ? "var(--c-bg-card)" : editorBg};`;
    bgWrap.appendChild(clone);
    holder.appendChild(bgWrap);
    document.body.appendChild(holder);

    // No date line, and no "Title" placeholder on an untitled note (Jade,
    // 2026-10-17). Removing them moves the text up, so the stickers (placed
    // from the top of the note) move up by the same amount to stay beside it.
    const cloneEditor = clone.querySelector(".jrnl-editor");
    const topBefore = cloneEditor ? cloneEditor.getBoundingClientRect().top : 0;
    clone.querySelectorAll(".jrnl-meta").forEach(el => el.remove());
    const cloneTitle = clone.querySelector(".jrnl-title");
    if (cloneTitle && !cloneTitle.innerText.trim()) cloneTitle.remove();
    const lift = cloneEditor ? topBefore - cloneEditor.getBoundingClientRect().top : 0;
    if (lift > 0) clone.querySelectorAll(".cj-sticker-layer").forEach(el => { el.style.top = -lift + "px"; el.style.bottom = lift + "px"; });

    // Full height (Jade, 2026-10-28: the image stopped short of the page).
    // The copy was only as tall as its text, so the page below the last line,
    // and any sticker / text box placed there, was cut off. It is now at least
    // the page's height on screen, and tall enough for the lowest sticker
    // (tilted ones measured as drawn).
    const cloneTop = clone.getBoundingClientRect().top;
    let fullHeight = Math.max(pageHeight, clone.offsetHeight);
    clone.querySelectorAll(".cj-sticker-layer > div").forEach(el => {
      fullHeight = Math.max(fullHeight, el.getBoundingClientRect().bottom - cloneTop + 24);
    });
    clone.style.minHeight = Math.ceil(fullHeight) + "px";
    return { src, holder, bgWrap, clone, captureWidth };
  };

  const exportNoteAsImage = (title) => {
    if (!editorAreaRef.current) return;
    // Deselecting first (and waiting a frame) drops any sticker selection
    // border/handles from the DOM before we clone it, so editing chrome
    // never ends up baked into the exported image.
    setSelectedStickerId(null);
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const copy = makeNoteCopy();
      if (!copy) return;
      const { clone, holder, bgWrap, captureWidth } = copy;

      // Photo stickers are drawn as a no-repeat background-image, which
      // html2canvas paints with a repeating pattern: the image's opposite edge
      // showed as thin dark lines along the sticker's box (Jade, 2026-10-17).
      // In the copy, swap each for a real <img> placed exactly like the
      // background (same size / position maths), clipped by its box.
      // Washi tape: html2canvas ignores clip-path, so the torn ends would come
      // out square. Paint each tape onto a canvas instead (same tile, same tear).
      const tapesReady = Promise.all(Array.from(clone.querySelectorAll(".cj-tape")).map(el => new Promise(res => {
        const W = el.offsetWidth, H = el.offsetHeight, SC = 2;
        const img = new window.Image();
        img.onload = () => {
          try {
            const cv = document.createElement("canvas");
            cv.width = Math.ceil(W * SC); cv.height = Math.ceil(H * SC);
            cv.style.cssText = `width:${W}px;height:${H}px;display:block;`;
            drawTape(cv.getContext("2d"), img, el.dataset.seed, W, H, SC);
            el.replaceWith(cv);
          } catch (err) { console.error("Washi tape export failed:", err); }
          res();
        };
        img.onerror = res;
        img.src = tapeSrc(el.dataset.tape);
      })));
      clone.querySelectorAll(".cj-sticker-layer div:not(.cj-tape)").forEach(el => {
        const m = /url\(["']?(.*?)["']?\)/.exec(el.style.backgroundImage || "");
        if (!m) return;
        const W = el.offsetWidth, H = el.offsetHeight;
        const pct = (v, i, d) => { const n = parseFloat(String(v || "").split(" ")[i]); return isNaN(n) ? d : n / 100; };
        const iw = W * pct(el.style.backgroundSize, 0, 1), ih = H * pct(el.style.backgroundSize, 1, 1);
        const img = document.createElement("img");
        img.src = m[1];
        img.style.cssText = `position:absolute;left:${(W - iw) * pct(el.style.backgroundPosition, 0, 0)}px;top:${(H - ih) * pct(el.style.backgroundPosition, 1, 0)}px;width:${iw}px;height:${ih}px;max-width:none;display:block;`;
        el.style.backgroundImage = "none";
        el.style.position = "relative";
        el.style.overflow = "hidden";
        el.appendChild(img);
      });

      const imgs = Array.from(clone.querySelectorAll("img"));
      const ready = Promise.all([tapesReady, ...imgs.map(img => (img.complete && img.naturalWidth)
        ? Promise.resolve()
        : new Promise(res => { img.onload = img.onerror = res; }))]);
      const finish = () => { try { document.body.removeChild(holder); } catch (e) {} };
      ready
        .then(() => import("html2canvas"))
        .then(({ default: html2canvas }) => {
          // Phones refuse very big canvases (the image comes out blank), so a
          // very long note is drawn at a little less than 2x.
          const W = bgWrap.offsetWidth || captureWidth, H = bgWrap.offsetHeight || 1;
          const scale = Math.min(2, 16384 / H, Math.sqrt(16777216 / (W * H)));
          return html2canvas(bgWrap, { scale, useCORS: true, backgroundColor: null, logging: false });
        })
        .then(canvas => {
          const baseName = (title || "journal").replace(/[\\/:*?"<>|]/g, "_");
          if (isNative) {
            const dataUrl = canvas.toDataURL("image/png");
            // Straight to the device gallery — no share sheet. Falls back to
            // the share-sheet path only if the gallery save itself fails, so
            // the user still ends up with the image one way or another.
            return saveImageToDeviceGallery(dataUrl, baseName)
              .then(() => setImageSavedToast(true))
              .catch(err => {
                console.error("Gallery save failed, falling back to share:", err);
                return saveAndShareNative(baseName + ".png", dataUrl.split(",")[1]);
              });
          }
          return new Promise(resolve => canvas.toBlob(blob => {
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = baseName + ".png";
            a.click();
            resolve();
          }, "image/png"));
        })
        .then(finish)
        .catch((err) => { finish(); console.error("Image export failed:", err); });
    }));
  };

  // Export .html: the note as it looks (Jade, 2026-10-28): its background,
  // title, formatted text, checklists, stickers and text boxes, as one file any
  // browser opens. Built from the same copy as Save as Image, plus the app's
  // style rules that apply to it and the current values of the colours it
  // uses (utils/noteHtmlExport.js). If anything goes wrong it falls back to
  // the plain body HTML it used to save, so there's always a file.
  const exportNoteAsHtml = (title, bodyHtml) => {
    const save = (full) => {
      if (isNative) { saveAndShareNative(title+".html", full, "utf8"); return; }
      const b = new Blob([full], { type:"text/html" }); const a = document.createElement("a");
      a.href = URL.createObjectURL(b); a.download = title+".html"; a.click();
    };
    const plain = () => save(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title.replace(/[&<>]/g, c => ({ "&":"&amp;", "<":"&lt;", ">":"&gt;" }[c]))}</title></head><body>${bodyHtml}</body></html>`);
    if (!editorAreaRef.current) { plain(); return; }
    setSelectedStickerId(null);
    requestAnimationFrame(() => requestAnimationFrame(async () => {
      const copy = makeNoteCopy();
      if (!copy) { plain(); return; }
      const { src, holder, bgWrap, captureWidth } = copy;
      try {
        bgWrap.classList.add("cj-export-page");
        // The app's text settings reach the note from far above it.
        const cs = window.getComputedStyle(src);
        ["font-family", "font-size", "font-weight", "color", "line-height", "letter-spacing", "-webkit-font-smoothing", "text-rendering"]
          .forEach(p => { const v = cs.getPropertyValue(p); if (v) bgWrap.style.setProperty(p, v); });
        const css = collectMatchingCss(holder);
        // Colours written as var(--c-text) & co., with their values right now.
        cssVarNames(bgWrap.outerHTML + css.join("\n")).forEach(name => {
          const v = cs.getPropertyValue(name).trim();
          if (v) bgWrap.style.setProperty(name, v);
        });
        await inlineLocalUrls(bgWrap);
        // The heart photo frame clips with an SVG shape that lives in the app.
        const heart = bgWrap.innerHTML.includes("cj-frame-heart") ? (document.getElementById("cj-frame-heart")?.closest("svg")?.outerHTML || "") : "";
        save(buildNoteHtmlDocument({
          title,
          body: bgWrap.outerHTML,
          css,
          fonts: usedFonts(bgWrap.outerHTML + css.join("\n")),
          width: captureWidth,
          dark: document.documentElement.hasAttribute("data-dark"),
          wrapperClass: holder.className,
          deskBg: darkNote ? "#1c1b1f" : "#ececec",
          extra: heart,
        }));
      } catch (err) {
        console.error("HTML export failed, saving the plain text instead:", err);
        plain();
      } finally {
        try { document.body.removeChild(holder); } catch (e) {}
      }
    }));
  };

  const shareNote = async () => {
    const title = (titleRef.current?.innerText || activeNote?.title || "Journal Entry").trim() || "Journal Entry";
    const text = editorRef.current?.innerText || activeNote?.content || "";
    if (isNative) {
      try { const { Share } = await import("@capacitor/share"); await Share.share({ title, text }); } catch(e) {}
    }
    else if (navigator.share) { try { await navigator.share({title,text}); } catch(e) {} }
    else { navigator.clipboard.writeText(text); }
    setShowMoreMenu(false);
  };

  const currentNoteColor = activeNote?.color || "#fff7f9";
  const editorBg = activeNote?.noteBg || currentNoteColor;
  // Image note backgrounds render via a per-image CSS class (so each can be sized
  // correctly) with the URL passed through a --note-img variable. New notes store
  // "photo:<id>"; older notes stored a raw url(...) shorthand → treat as the
  // original wildflower frame for backward compatibility.
  let photoCls = null, photoImg = null, photoAspect = null;
  if (typeof editorBg === "string" && editorBg.includes("url(")) {
    photoCls = "npb-frame";
    photoImg = editorBg.match(/url\((['"]?.*?['"]?)\)/)?.[0];
  }
  const photoBg = !!photoCls;
  const darkNote = isDarkNoteBg(editorBg);
  // "Aspect-lock" notes: scene/photo images that carry an intrinsic aspect ratio
  // make the writing canvas take that exact shape (letterboxed on a backdrop) so
  // the image is shown in full — never cropped, stretched or split. Abstract
  // gradients have no `aspect`, so they stay full-bleed on the normal page.
  const aspectLock = photoBg && photoAspect;

  // ── Toolbar / tool-panel docking ──────────────────────────────────────────
  // On mobile the toolbar ONLY exists while editing: it docks just above the
  // on-screen keyboard. There is no top toolbar. When a tool button is tapped
  // we dismiss the keyboard and the tool's options take over that same slice of
  // the screen (Google-Docs style); tapping the page brings the keyboard back.
  const anyPanelOpen = showHeadingDd || showInsertDd || showHighlightDd || showTextColorDd || showBgPanel || showStickerPicker || showFolderDd;
  const kbOpen       = isMobile && kbHeight > 80;
  // Mobile "docked" mode: toolbar + panels are pinned to the bottom slot, which
  // is the live keyboard (kbHeight) or, while a panel is open, the frozen height.
  const dockActive   = isMobile && (kbOpen || anyPanelOpen);
  // When the VirtualKeyboard API is driving things and no panel is open, the
  // toolbar is pinned by CSS env(keyboard-inset-height) (see its style below) and
  // we do NOT compute a JS `bottom` for it — that's what kills the float/bounce.
  // A JS bottom is only needed while a panel is open (keyboard dismissed) or on
  // the visualViewport fallback path.
  const toolbarUsesEnv = isMobile && vkSupported && !anyPanelOpen;
  // Toolbar bottom: rest it on top of the open panel (which is anchored at the
  // screen bottom, or above the numeric keyboard while the size field is up),
  // else just above the live keyboard. On native the viewport already ends at
  // the keyboard's top edge (see isNative comment above), so the keyboard's
  // own contribution to this offset is 0 there — only panelH (the panel's own
  // height, unrelated to the keyboard) still applies.
  const kbOffset     = isNative ? 0 : kbHeight;
  // panelH already includes the panel's own --sab padding (see panelDock
  // below), so the toolbar rests flush on top of it with no buffer — the
  // native overlap this used to paper over was the panel growing by --sab
  // after measurement, which the border-box observer above now catches.
  const dockBottom   = anyPanelOpen ? (sizeFocused ? kbOffset + panelH : panelH) : kbOffset;
  const toolbarFixed = dockActive;
  // Mobile: show the toolbar once a panel is open, or once the keyboard is up.
  // On the VirtualKeyboard path env() pins it so it appears already in place;
  // on the fallback path we wait for kbSettling to clear so it doesn't slide up.
  // Desktop: only while the editor is focused.
  // Native adds kbExpected so the bar appears on focus rather than waiting for
  // the keyboard-height bridge event (see kbExpected above) — it docks at
  // bottom:0 either way, so it simply rides up as the viewport is inset.
  const toolbarVisible = !isMobile
    ? (editorFocused || anyPanelOpen)
    : (anyPanelOpen || kbExpected || (kbOpen && (vkSupported || !kbSettling)));

  // Docked panels sit at the bottom and grow only as tall as their content,
  // capped at the keyboard's slice of the screen (so short panels — Insert,
  // Highlight — don't leave a big empty box; tall ones scroll).
  // bottom:0 + paddingBottom (not bottom:"var(--sab)") so the panel's own
  // background/border extends all the way to the true screen edge — with a
  // bare `bottom` offset the gap between the panel and the edge showed
  // whatever was behind it (the writing area's own text, if scrolled far
  // enough down). Padding also means offsetHeight (panelH, measured below)
  // already includes the inset, so nothing downstream needs to add it again.
  const panelDock = dockActive
    ? { position:"fixed", bottom:0, paddingBottom:"var(--sab, 0px)", left:0, right:0, maxHeight:frozenKb, zIndex:5002, overflowY:"auto",
        borderTop:"1.5px solid var(--c-border-light)", boxShadow:"0 -10px 24px rgba(0,0,0,0.10)" }
    // Desktop: a frosted card that FLOATS under the top bar (absolute, so it
    // overlays the page and never pushes the writing area down).
    : { position:"absolute", top:60, left:"50%", transform:"translateX(-50%)", zIndex:200,
        maxWidth:"calc(100% - 32px)", maxHeight:"calc(100% - 90px)", overflowY:"auto",
        borderRadius:18, border:"1px solid rgba(240,200,225,0.7)",
        background:"rgba(255,247,251,0.92)", backdropFilter:"blur(14px)", WebkitBackdropFilter:"blur(14px)",
        boxShadow:"0 12px 30px rgba(150,70,120,0.16)" };

  // Returns a fixed-position style that pins a panel below the button that
  // opened it. panelAnchor = {top,left} is captured (viewport coords) at open
  // time. Mobile falls back to panelDock (docks at the bottom as usual).
  const anchorPanelStyle = (width) => {
    if (dockActive) return panelDock;
    const a  = panelAnchor;
    // documentElement.clientWidth is the reliable viewport width for clamping a
    // position:fixed element (excludes the scrollbar; matches the fixed ICB).
    const vw = (typeof document !== "undefined" && document.documentElement)
      ? document.documentElement.clientWidth
      : (typeof window !== "undefined" ? window.innerWidth : 1024);
    const w  = typeof width === "number" ? width : 360;
    // Never let the panel exceed the viewport (12px breathing room each side),
    // so the rightmost column is never clipped — even when the page is zoomed.
    const effW = Math.min(w, vw - 24);
    let top  = a ? a.top : 64;
    // Center the panel horizontally on the button, then clamp to the viewport.
    let left = (a ? a.cx : vw / 2) - effW / 2;
    left = Math.max(12, Math.min(left, vw - effW - 12));
    return {
      position:"fixed", zIndex:5002, overflowY:"auto", overflowX:"hidden",
      boxSizing:"border-box", // include padding in the width so nothing clips
      top, left,
      width: effW,
      maxWidth:"calc(100vw - 24px)", // CSS belt-and-suspenders against overflow
      maxHeight:"calc(100vh - 120px)",
      border:"1px solid rgba(240,200,225,0.7)",
      background:"rgba(255,247,251,0.95)",
      backdropFilter:"blur(14px)", WebkitBackdropFilter:"blur(14px)",
      boxShadow:"0 12px 30px rgba(150,70,120,0.16)",
      borderRadius:0,
    };
  };

  // Desktop panels use position:fixed but live inside .clarity-tab-content,
  // which carries a transform — that makes fixed resolve against the container
  // instead of the viewport, throwing off the captured-rect alignment. Portal
  // them to <body> so fixed is viewport-relative and matches the anchor coords.
  // Mobile already renders the editor (and thus these panels) under <body>.
  const panelPortal = (node) => (isMobile ? node : createPortal(node, document.body));

  // Plain-text preview for list cards. The note body lives in `htmlContent`
  // (not `content`, which stays empty), so strip its tags + common entities.
  const noteExcerpt = (note) => {
    const raw = (note && (note.htmlContent || note.content)) || "";
    if (!raw) return "";
    return raw
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/g, " ").replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<").replace(/&gt;/g, ">")
      .replace(/\s+/g, " ")
      .trim();
  };

  // Open a tool panel: remember the selection, freeze the keyboard height, show
  // just this panel, then blur the editor so the keyboard slides away and the
  // panel can sit in its place.
  const openPanel = (which) => {
    // Capture the anchor button's viewport rect NOW (before any state change
    // causes a re-render that might shift the toolbar). React 18 batches the
    // setState calls below into one render, so the panel will use these coords.
    if (!dockActive) {
      const btn = btnRefs.current[which];
      if (btn) { const r = btn.getBoundingClientRect(); setPanelAnchor({ top: r.bottom + 4, cx: r.left + r.width / 2 }); }
    }
    saveRange();
    // The text box being typed in, if any, so the panel's picks go to it. When
    // nothing is focused (going straight from one panel to another, which had
    // already hidden the keyboard) the previous panel's box carries over.
    const ae = document.activeElement;
    if (ae && ae.isContentEditable) panelTextboxRef.current = ae.classList.contains("cj-textbox") ? ae.getAttribute("data-sid") : null;
    if (kbHeight > 80) setFrozenKb(kbHeight);
    // Sync the size stepper to the actual font size at the caret, once, when the
    // format panel opens — not continuously (the selectionchange approach caused
    // the stale closure to reset the stepper every time the user clicked +/-).
    if (which === "heading") {
      typingSizeRef.current = false; // reset digit-typing mode
      const sel = window.getSelection();
      let node = sel && sel.anchorNode;
      if (node && node.nodeType === 3) node = node.parentElement;
      if (node && editorRef.current?.contains(node)) {
        const cs = parseInt(window.getComputedStyle(node).fontSize, 10);
        if (cs) setFontSize(cs);
      }
    }
    setShowHeadingDd(which === "heading");
    setShowInsertDd(which === "insert");
    setShowHighlightDd(which === "highlight");
    setShowTextColorDd(which === "textcolor");
    setShowBgPanel(which === "bg");
    setShowStickerPicker(which === "sticker");
    setShowFontDd(false); setShowStyleDd(false);
    setShowFolderDd(false); // a tool panel and the folder menu never coexist
    if (isMobile) dismissKeyboard();
  };
  // Force the on-screen keyboard down so a tool panel takes its place instantly.
  // A plain editorRef.blur() isn't reliable: the focus may sit on a nested
  // editable (a checklist label), and with the VirtualKeyboard API in
  // overlaysContent mode the keyboard can linger until explicitly hidden. So we
  // blur whatever is actually focused AND call virtualKeyboard.hide().
  const dismissKeyboard = () => {
    setTimeout(() => {
      const ae = document.activeElement;
      if (ae && typeof ae.blur === "function") ae.blur();
      editorRef.current?.blur();
      try { navigator.virtualKeyboard && navigator.virtualKeyboard.hide && navigator.virtualKeyboard.hide(); } catch (e) {}
    }, 0);
  };
  const closePanels = () => {
    setShowHeadingDd(false); setShowInsertDd(false); setShowHighlightDd(false); setShowTextColorDd(false);
    setShowBgPanel(false); setShowStickerPicker(false);
    setShowFontDd(false); setShowStyleDd(false); setHlPickerOpen(false);
    panelTextboxRef.current = null;
  };
  // Leaving the editor: save, then — on mobile with the keyboard/toolbar docked —
  // let that dock slide away FIRST and only navigate to the list once it's gone,
  // so the transition reads as "keyboard retracts → back to journal" instead of
  // the whole screen swapping out from under an open keyboard. Desktop (no dock)
  // navigates immediately.
  const handleBack = () => {
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    // Only write when there's actually something unsaved. doSave() stamps a
    // fresh updatedAt, and updatedAt is what the "Recent" sort orders by — so
    // saving unconditionally meant merely OPENING a note and backing out
    // floated it to the top of the list as if it had been edited. isDirty is
    // set by scheduleAutosave(), which every real edit path goes through, so
    // a pending debounced edit still gets flushed here.
    if (!discardIfEmpty() && isDirty) doSave();
    if (isMobile && (dockActive || kbHeightRef.current > 80)) {
      closePanels();
      dismissKeyboard();
      setTimeout(() => setScreen("list"), 280);
    } else {
      setScreen("list");
    }
  };

  // Android hardware back button: this screen has no browser-history entry
  // of its own (see capacitor/backButton.js), so without this the hardware
  // back button would fall through to the app-exit handler entirely,
  // skipping both the note list and any unsaved-changes warning. Claims the
  // press itself here instead, and — if there's an edit still sitting in the
  // 2s autosave debounce — asks before leaving rather than silently saving
  // (or worse, letting the app-exit path race the pending write).
  useEffect(() => {
    if (screen !== "editor") return;
    return pushBackHandler(() => { requestClose(); return true; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, isDirty]);

  // Shared by the hardware back button above and the on-screen back arrow
  // below, so "unsaved changes?" shows up the same way either way in.
  const requestClose = () => {
    if (isDirty) { setShowBackSaveConfirm(true); return; }
    handleBack();
  };

  const saveAndLeave = () => {
    setShowBackSaveConfirm(false);
    handleBack();
  };
  const discardAndLeave = () => {
    setShowBackSaveConfirm(false);
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    setIsDirty(false);
    setScreen("list");
  };

  const togglePanel = (which) => {
    const open = { heading:showHeadingDd, insert:showInsertDd, highlight:showHighlightDd, textcolor:showTextColorDd, bg:showBgPanel, sticker:showStickerPicker }[which];
    if (open) { if (!restoreTextboxRange()) restoreRange(); closePanels(); } else { openPanel(which); }
  };
  // Apply a format from inside a panel without summoning the keyboard back: the
  // apply* helpers focus the editor to run execCommand, so we re-blur right after
  // to keep the keyboard hidden and the panel in view. Unlike dismissKeyboard()
  // (used when a panel first opens), this blurs synchronously in the same tick
  // as the focus() call above it — no setTimeout gap — so the keyboard never
  // gets a chance to animate up. That's what makes repeated +/- taps feel
  // continuous instead of flashing the keyboard for a beat on every press.
  const keepPanel = () => {
    if (!isMobile || !anyPanelOpen) return;
    // Remember what's highlighted now, so the next pick from the same panel
    // restores this, not the selection from before the last change. Only a
    // selection in the note itself (the size field's isn't one).
    const sel = window.getSelection();
    let at = sel && sel.rangeCount ? sel.getRangeAt(0).commonAncestorContainer : null;
    if (at && at.nodeType === 3) at = at.parentElement;
    if (at && (editorRef.current?.contains(at) || titleRef.current?.contains(at) || at.closest?.(".cj-textbox"))) saveRange();
    const ae = document.activeElement;
    if (ae && typeof ae.blur === "function") ae.blur();
    editorRef.current?.blur();
    try { navigator.virtualKeyboard && navigator.virtualKeyboard.hide && navigator.virtualKeyboard.hide(); } catch (e) {}
  };

  // Apply a highlight color, and only remember a *custom* color in the Saved
  // list when it was actually applied to selected text (not while merely
  // browsing the hue picker).
  const applyHl = (color, save) => {
    const hadSelection = !!(savedRangeRef.current && !savedRangeRef.current.collapsed);
    applyHighlight(color);
    if (save && color && hadSelection) addSavedHighlight(color);
    if (color) setLastHighlight(color);          // remember for the button preview
    keepPanel();
    closePanels();                                // close immediately after a pick
  };
  const applyTc = (color) => {
    applyTextColor(color);
    if (color && color !== "inherit") {
      setLastTextColor(color);
      addSavedTextColor(color);
    }
    keepPanel();
    closePanels();
  };

  // The Highlight and Text color swatches: the "none / default" circle and the
  // light colours, then a line, then the dark colours. They nearly touched
  // (Jade, 2026-10-28: about 1px apart on a phone): every button has a 36px
  // minimum (App.jsx, for fingers), so the circles outgrew their 29px columns.
  // Now each is a 30px circle inside a 36px tap area, centred in a column at
  // least 42px wide: ~15px between circles across, 12px down.
  const inkSwatches = (noneLabel, onPick) => {
    const swatch = (c, title) => (
      <button key={c||"none"} title={title} onMouseDown={e=>e.preventDefault()} onClick={()=>onPick(c)}
        style={{ width:36, height:36, border:"none", background:"transparent", cursor:"pointer", padding:0,
          display:"flex", alignItems:"center", justifyContent:"center" }}>
        <span style={{ width:30, height:30, borderRadius:"50%", background:c||"transparent", boxSizing:"border-box",
          border:c?"1px solid rgba(0,0,0,0.10)":"1.5px solid var(--c-border-light)",
          display:"flex", alignItems:"center", justifyContent:"center" }}>
          {!c && <X size={13} color="var(--c-text-soft)"/>}
        </span>
      </button>
    );
    const grid = { display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(42px, 1fr))", rowGap:6, justifyItems:"center" };
    return (<>
      <div style={grid}>
        {swatch(null, noneLabel)}
        {INK_LIGHT_COLORS.map(c => swatch(c, c))}
      </div>
      <div style={{ height:1, background:"var(--c-border-light)", opacity:0.7, margin:"14px 2px" }}/>
      <div style={{ ...grid, marginBottom:14 }}>
        {INK_DARK_COLORS.map(c => swatch(c, c))}
      </div>
    </>);
  };

  const tbStyle = `
    .ctb{height:32px;border:none;border-radius:8px;display:flex;align-items:center;justify-content:center;background:rgba(255,255,255,0.55);cursor:pointer;font-family:inherit;box-sizing:border-box;flex-shrink:0;transition:background 0.12s,color 0.12s;}
    .ctb:active{background:rgba(212,88,154,0.08);}
    .ctb-on{background:#ffffff!important;color:var(--c-primary)!important;}
    .ctb-icon{width:32px;}
    .ctb-wide{padding:0 10px;min-width:88px;gap:5px;}
    .ctb-num{padding:0 7px;min-width:60px;gap:4px;}
    .clt-div{width:1px;height:20px;background:#d8b8cc;opacity:0.45;flex-shrink:0;margin:0 5px;}
    .clt{scrollbar-width:none;}
    .clt::-webkit-scrollbar{display:none;}
    @keyframes cjNoteSlideUp{from{transform:translateY(100%);}to{transform:translateY(0);}}
    .jrnl-editor:focus{outline:none;}
    .jrnl-title:focus{outline:none;}
    .jrnl-title:empty:before{content:attr(data-ph);color:var(--c-text-soft);opacity:0.7;}
    .jrnl-editor img{max-width:100%;border-radius:8px;}
    .jrnl-editor hr{border:none;border-top:2px solid #e8c8d8;margin:16px 0;}
    .jrnl-editor ul,.jrnl-editor ol{padding-left:24px;margin:4px 0;}
    .jrnl-editor li{margin:2px 0;}
    .jrnl-editor ul ul,.jrnl-editor ol ol,.jrnl-editor ul ol,.jrnl-editor ol ul{padding-left:20px;}
    .jrnl-editor blockquote{margin:8px 0;padding:4px 0 4px 14px;border-left:3px solid var(--c-primary);color:var(--c-text-soft);font-style:italic;}
    /* Photo (image) note backgrounds, sized per artwork to stay sharp.
       npb-frame = corner frame: cover on phones (no stretch), 100% 100% on wider
       desktop notes so the corners still frame the page.
       npb-left = left-edge border: cover, anchored left so the flowers hug the
       left side undistorted while the writing space stays on the right.
       npb-cover = full-bleed wallpaper (e.g. pastel silk): cover, centered. */
    .npb-frame,.npb-left,.npb-cover{background-color:#ffffff;background-image:var(--note-img);background-repeat:no-repeat;}
    .npb-frame{background-position:center center;background-size:cover;}
    @media (min-width:760px){.npb-frame{background-size:100% 100%;}}
    .npb-left{background-position:left center;background-size:auto 100%;}
    .npb-cover{background-position:center center;background-size:cover;background-attachment:scroll;}
  `;

  // Shared "Move to folder" picker (used from both the editor header and the
  // list's card menu). Rendered in both screens.
  const folderTargetNote = folderTargetId ? allNotes.find(n => n.id === folderTargetId) : null;
  const folderModalEl = folderTargetNote ? createPortal(
    <div onClick={()=>{ setFolderTargetId(null); setCreatingFolder(false); }} style={{ position:"fixed", inset:0, zIndex:9500, background:"rgba(60,30,50,0.42)", display:"flex", alignItems:"flex-start", justifyContent:"center" }}>
      <div data-tour="journal-folder-drawer" onClick={e=>e.stopPropagation()} style={{ background:"var(--c-bg-card)", borderRadius:"0 0 22px 22px", padding:"calc(18px + var(--sat, 0px)) 18px 22px", width:"100%", maxWidth:520, boxShadow:"0 8px 40px rgba(0,0,0,0.2)", maxHeight:"72vh", display:"flex", flexDirection:"column" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
          <span style={{ fontWeight:900, fontSize:16, color:"var(--c-text)" }}>Move to folder</span>
          <button onClick={()=>{ setFolderTargetId(null); setCreatingFolder(false); }} style={{ background:"transparent", border:"none", cursor:"pointer", padding:4 }}><X size={18} color="var(--c-text-soft)"/></button>
        </div>
        <div style={{ overflowY:"auto", display:"flex", flexDirection:"column", gap:6 }}>
          <button onClick={()=>{ moveNoteToFolder(folderTargetId, null); setFolderTargetId(null); }}
            style={{ display:"flex", alignItems:"center", gap:10, padding:"12px 14px", borderRadius:13, border:"1.5px solid var(--c-border-light)", background: !folderTargetNote.folder ? "var(--c-bg-soft)" : "transparent", cursor:"pointer", fontFamily:"inherit", fontSize:14, fontWeight:700, color:"var(--c-text)" }}>
            <X size={16} color="var(--c-text-soft)"/> None (Unfiled)
            {!folderTargetNote.folder && <Check size={16} color="var(--c-primary)" style={{ marginLeft:"auto" }}/>}
          </button>
          {journalFolders.map(f => (
            <button key={f.id} onClick={()=>{ moveNoteToFolder(folderTargetId, f.id); setFolderTargetId(null); }}
              style={{ display:"flex", alignItems:"center", gap:10, padding:"12px 14px", borderRadius:13, border:"1.5px solid var(--c-border-light)", background: folderTargetNote.folder===f.id ? "var(--c-bg-soft)" : "transparent", cursor:"pointer", fontFamily:"inherit", fontSize:14, fontWeight:700, color:"var(--c-text)" }}>
              <Folder size={16} color="var(--c-primary)"/> <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{f.name}</span>
              {folderTargetNote.folder===f.id && <Check size={16} color="var(--c-primary)" style={{ marginLeft:"auto" }}/>}
            </button>
          ))}
        </div>
        {creatingFolder ? (
          <div style={{ marginTop:12, display:"flex", flexDirection:"column", gap:9 }}>
            <div style={{ display:"flex", gap:8 }}>
              <input autoFocus value={newFolderName} onChange={e=>setNewFolderName(e.target.value)}
                onKeyDown={e=>{ if(e.key==="Enter"){ const id=addFolder(newFolderName); if(id){ moveNoteToFolder(folderTargetId, id); setFolderTargetId(null);} setNewFolderName(""); setCreatingFolder(false); } }}
                placeholder="Folder name" style={{ flex:1, padding:"11px 14px", borderRadius:12, border:"1.5px solid var(--c-primary)", background:"#fff", fontSize:14, fontFamily:"inherit", outline:"none" }}/>
              <button onClick={()=>{ const id=addFolder(newFolderName); if(id){ moveNoteToFolder(folderTargetId, id); setFolderTargetId(null);} setNewFolderName(""); setCreatingFolder(false); }}
                style={{ padding:"11px 18px", borderRadius:12, border:"none", background:"var(--c-primary)", color:"#fff", cursor:"pointer", fontWeight:800, fontSize:13 }}>Create</button>
            </div>
            {(() => {
              const usedNames = new Set(journalFolders.map(f => f.name.toLowerCase()));
              const sugg = [
                ...(!usedNames.has("uncategorized") ? [{ id: "__uncategorized__", title: "Uncategorized", emoji: "🗂", color: "#b0a4bc" }] : []),
                ...categories.filter(c => c.title && !usedNames.has(c.title.toLowerCase())),
              ];
              if (sugg.length === 0) return null;
              return (
                <div data-tour="journal-spaces-section">
                  <span style={{ fontSize:10, fontWeight:800, color:"var(--c-text-soft)", textTransform:"uppercase", letterSpacing:0.5 }}>From your categories</span>
                  <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginTop:6 }}>
                    {sugg.map(c => (
                      <button key={c.id} onClick={()=>{ const id=addFolder(c.title); if(id){ moveNoteToFolder(folderTargetId, id); setFolderTargetId(null);} setNewFolderName(""); setCreatingFolder(false); }}
                        style={{ display:"flex", alignItems:"center", gap:5, padding:"6px 11px", borderRadius:999, border:`1.5px solid ${(c.color||"var(--c-border-light)")}`, background:"#fff", cursor:"pointer", fontFamily:"inherit", fontSize:12, fontWeight:700, color:"var(--c-text)" }}>
                        {c.emoji && <span style={{ fontSize:13 }}>{c.emoji}</span>}{c.title}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        ) : (
          <button data-tour="journal-new-folder-btn" onClick={()=>{ setNewFolderName(""); setCreatingFolder(true); }}
            style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:6, marginTop:12, padding:"12px", borderRadius:13, border:"1.5px dashed var(--c-border-light)", background:"transparent", color:"var(--c-primary)", cursor:"pointer", fontFamily:"inherit", fontSize:14, fontWeight:800 }}>
            <FolderPlus size={16}/> New folder
          </button>
        )}
      </div>
    </div>, document.body) : null;

  // Right-click menu for note cards/rows — shared by the mobile grid and the
  // desktop list pane so there's a single source of truth.
  const contextMenuEl = contextMenu ? (() => {
    const { rect } = contextMenu;
    const menuWidth = 160;
    const estMenuHeight = 210;
    const centerX = rect.left + rect.width / 2;
    const left = Math.min(Math.max(centerX, menuWidth / 2 + 8), window.innerWidth - menuWidth / 2 - 8);
    const gap = 2;
    const fitsBelow = rect.bottom + gap + estMenuHeight <= window.innerHeight;
    const top = fitsBelow ? rect.bottom + gap : Math.max(8, rect.top - gap - estMenuHeight);
    return createPortal(
    <>
      <div onClick={()=>setContextMenu(null)} style={{ position:"fixed", inset:0, zIndex:9599 }} />
      <div ref={contextMenuElRef} onMouseDown={e=>e.stopPropagation()} style={{ position:"fixed", top, left, transform:"translateX(-50%)", background:"#ffffff", border:"1.5px solid #f0d0e8", borderRadius:13, padding:5, zIndex:9600, boxShadow:"0 8px 28px rgba(160,60,120,0.13)", minWidth:menuWidth }}>
        {[
          {label:contextMenu.note.pinned?"Unpin":"Pin",act:()=>{togglePin(contextMenu.note.id);setContextMenu(null);}},
          {label:"Move to folder…",act:()=>{ setNewFolderName(""); setCreatingFolder(false); setFolderTargetId(contextMenu.note.id); setContextMenu(null); }},
          {label:"Duplicate",act:()=>{duplicateNote(contextMenu.note);setContextMenu(null);}},
          {label:"Open",act:()=>{openNote(contextMenu.note);setContextMenu(null);}},
          {label:"Delete",act:()=>{setContextDeleteNote(contextMenu.note);setContextMenu(null);},danger:true},
        ].map(item=>(
          <button key={item.label} onClick={item.act}
            style={{ display:"block", width:"100%", textAlign:"left", padding:"8px 12px", borderRadius:9, border:"none", cursor:"pointer", fontFamily:"inherit", background:"transparent", color:item.danger?"#e8647a":"#3a2035", fontSize:13 }}
            onMouseEnter={e=>e.currentTarget.style.background="#fff0f7"}
            onMouseLeave={e=>e.currentTarget.style.background="transparent"}
          >{item.label}</button>
        ))}
      </div>
    </>,
    document.body
    );
  })() : null;

  const contextDeleteConfirmEl = contextDeleteNote ? createPortal(
    <div style={{ position:"fixed", inset:0, zIndex:9700, background:"rgba(60,30,50,0.38)", display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
      <div style={{ background:"var(--c-bg-card)", borderRadius:18, padding:28, maxWidth:320, width:"100%", boxShadow:"0 8px 40px rgba(0,0,0,0.18)", textAlign:"center" }}>
        <ConfirmHeader title="Are you sure you want to delete this note?" />
        <div style={CONFIRM_STACK}>
          <button onClick={()=>setContextDeleteNote(null)} style={CONFIRM_CANCEL}>Cancel</button>
          <button onClick={()=>{ deleteNote(contextDeleteNote.id); setContextDeleteNote(null); }} style={CONFIRM_DANGER}>Delete</button>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  // Desktop full-screen / focus toggle — hides the left list pane and centers
  // the note (still at its normal width, just without the preview column
  // beside it) instead of letting it stretch full-bleed. Lives at the
  // top-left of the editor header, mobile has no such concept.
  const focusToggleBtn = isDesktop ? (
    <button onClick={() => setDeskFocus(v => !v)} title={deskFocus ? "Show note list" : "Center note & hide list"}
      style={{ display:"flex", alignItems:"center", justifyContent:"center", width:34, height:34, borderRadius:10, border:"none", background:"transparent", color: deskFocus ? "var(--c-primary)" : "var(--c-text-soft)", cursor:"pointer", flexShrink:0 }}>
      {deskFocus ? <Minimize2 size={16}/> : <Maximize2 size={16}/>}
    </button>
  ) : null;

  // ── Folder dropdown button — replaces the old horizontally-scrollable pill
  // row on mobile, and sits beside the search bar on both mobile and desktop.
  // Opens a popover listing "All Notes" + every folder (with counts) + Unfiled,
  // plus a "New folder" row that expands into an inline text field.
  const renderFolderDropdown = () => {
    const counts = {}; let unfiled = 0;
    allNotes.forEach(n => { if (n.folder && folderName(n.folder)) counts[n.folder] = (counts[n.folder]||0)+1; else unfiled++; });
    const chips = [{ key:null, label:"All Notes", count:allNotes.length }];
    journalFolders.forEach(f => chips.push({ key:f.id, label:f.name, count:counts[f.id]||0, removable:true }));
    if (unfiled > 0) chips.push({ key:"__unfiled__", label:"Unfiled", count:unfiled });
    const usedNames = new Set(journalFolders.map(f => f.name.toLowerCase()));
    const catSuggestions = [
      ...(!usedNames.has("uncategorized") ? [{ id: "__uncategorized__", title: "Uncategorized", emoji: "🗂", color: "#b0a4bc" }] : []),
      ...categories.filter(c => c.title && !usedNames.has(c.title.toLowerCase())),
    ];
    return (
      <>
        <button ref={folderBtnRef} onClick={() => {
            if (!folderMenuOpen && folderBtnRef.current) {
              const r = folderBtnRef.current.getBoundingClientRect();
              setFolderMenuPos({ top: r.bottom + 6, left: Math.min(r.left, window.innerWidth - 230 - 8) });
            }
            setCreatingFolder(false);
            setFolderMenuOpen(v => !v);
          }}
          title="Folders" className="jr-ctl"
          style={{ position:"relative", flexShrink:0, width:36, height:36, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", background: filterFolder ? "var(--c-bg-active)" : "var(--c-bg-card)", border: filterFolder ? "1.5px solid var(--c-primary)" : "1.5px solid var(--c-border-light)", color: filterFolder ? "var(--c-primary)" : "var(--c-text-soft)", cursor:"pointer" }}>
          <Folder size={16}/>
        </button>
        {folderMenuOpen && createPortal(
          <>
            <div onClick={()=>{ setFolderMenuOpen(false); setCreatingFolder(false); }} style={{ position:"fixed", inset:0, zIndex:19990 }} />
            <div style={{ position:"fixed", top:folderMenuPos.top, left:folderMenuPos.left, zIndex:19999, background:"var(--c-bg-card)", border:"1.5px solid var(--c-border-light)", borderRadius:14, boxShadow:"0 8px 24px rgba(0,0,0,0.15)", padding:8, width:230, maxHeight:360, overflowY:"auto", display:"flex", flexDirection:"column", gap:4 }} className="clt">
              {chips.map(ch => {
                const active = filterFolder === ch.key;
                return (
                  <button key={ch.key||"all"} onClick={()=>{ setFilterFolder(ch.key); setFolderMenuOpen(false); }}
                    onContextMenu={ch.removable ? (e)=>{ e.preventDefault(); if(window.confirm(`Delete folder "${ch.label}"? Notes inside become Unfiled.`)) deleteFolder(ch.key); } : undefined}
                    style={{ display:"flex", alignItems:"center", gap:8, width:"100%", padding:"8px 10px", borderRadius:8, border:"none", background: active ? "var(--c-bg-active)" : "transparent", color:"var(--c-text)", fontSize:13, fontWeight: active?700:500, cursor:"pointer", fontFamily:"inherit", textAlign:"left" }}>
                    {ch.key && ch.key!=="__unfiled__" && <Folder size={13} color={active?"var(--c-primary)":"var(--c-text-soft)"} />}
                    <span style={{ flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{ch.label}</span>
                    <span style={{ fontSize:10.5, opacity:0.7 }}>{ch.count}</span>
                    {active && <Check size={13} color="var(--c-primary)" />}
                  </button>
                );
              })}
              <div style={{ height:1, background:"var(--c-border-light)", margin:"4px 2px" }} />
              {!creatingFolder ? (
                <button data-tour="journal-new-folder-btn" onClick={()=>{ setNewFolderName(""); setCreatingFolder(true); }}
                  style={{ display:"flex", alignItems:"center", gap:8, width:"100%", padding:"8px 10px", borderRadius:8, border:"none", background:"transparent", color:"var(--c-primary)", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"inherit", textAlign:"left" }}>
                  <FolderPlus size={14}/> New folder
                </button>
              ) : (
                <div style={{ padding:"4px 2px", display:"flex", flexDirection:"column", gap:8 }}>
                  <div style={{ display:"flex", gap:6 }}>
                    <input autoFocus value={newFolderName} onChange={e=>setNewFolderName(e.target.value)}
                      onKeyDown={e=>{ if(e.key==="Enter"){ const id=addFolder(newFolderName); if(id) setFilterFolder(id); setNewFolderName(""); setCreatingFolder(false); setFolderMenuOpen(false); } if(e.key==="Escape"){ setNewFolderName(""); setCreatingFolder(false); } }}
                      placeholder="Folder name" style={{ flex:1, minWidth:0, padding:"7px 10px", borderRadius:8, border:"1.5px solid var(--c-primary)", background:"#fff", fontSize:12.5, fontFamily:"inherit", outline:"none" }}/>
                    <button onClick={()=>{ const id=addFolder(newFolderName); if(id) setFilterFolder(id); setNewFolderName(""); setCreatingFolder(false); setFolderMenuOpen(false); }}
                      style={{ flexShrink:0, padding:"7px 12px", borderRadius:8, border:"none", background:"var(--c-primary)", color:"#fff", cursor:"pointer", fontWeight:700, fontSize:12.5 }}>Add</button>
                  </div>
                  {catSuggestions.length > 0 && (
                    <div>
                      <span style={{ fontSize:9.5, fontWeight:800, color:"var(--c-text-soft)", textTransform:"uppercase", letterSpacing:0.5 }}>From your categories</span>
                      <div style={{ display:"flex", gap:5, flexWrap:"wrap", marginTop:5 }}>
                        {catSuggestions.map(c => {
                          const existing = journalFolders.find(f => f.name.toLowerCase() === c.title.toLowerCase());
                          return (
                            <button key={c.id} onClick={()=>{ const id = existing ? existing.id : addFolder(c.title); if(id) setFilterFolder(id); setNewFolderName(""); setCreatingFolder(false); setFolderMenuOpen(false); }}
                              style={{ display:"flex", alignItems:"center", gap:4, padding:"5px 9px", borderRadius:999, border:`1.5px solid ${(c.color||"var(--c-border-light)")}`, background:"#fff", cursor:"pointer", fontFamily:"inherit", fontSize:11, fontWeight:700, color:"var(--c-text)" }}>
                              {c.emoji && <span style={{ fontSize:12 }}>{c.emoji}</span>}{c.title}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>,
          document.body
        )}
      </>
    );
  };

  // ── Desktop master–detail shell ───────────────────────────────────────────
  // Wide screens (≥1024px) get a persistent left pane (search · folders · note
  // list) with the editor — or an empty state — living in the right pane, so
  // opening a note never hides the list. Mobile / narrow keeps the existing
  // single-column flow (editor portal) untouched below.
  const desktopShell = (rightPane) => {
    // Full-screen / focus mode hides the list pane (only while a note is open,
    // so the list is always reachable from the empty state).
    const collapsed = deskFocus && screen === "editor";
    return (
      <div style={{ display:"flex", height:"100%", width:"100%", background:"transparent", overflow:"hidden", padding:16, gap: collapsed ? 0 : 16, boxSizing:"border-box", justifyContent: collapsed ? "center" : "flex-start" }}>
        <style>{tbStyle}</style>
        {/* LEFT — folders + note list (floating rounded card) */}
        {!collapsed && (
        <aside style={{ width:330, flexShrink:0, display:"flex", flexDirection:"column", height:"100%", borderRadius:24, border:"1.5px solid var(--c-border-light)", background:"var(--c-bg-card)", boxShadow:"0 6px 28px var(--c-shadow)", overflow:"hidden" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, padding:"18px 18px 12px" }}>
            <span style={{ fontSize:21, fontWeight:900, color:"var(--c-text)", flex:1, letterSpacing:"-0.3px" }}>Journal</span>
            <button data-tour="journal-add-btn" onClick={()=>createNote({html:""})} title="New entry"
              style={{ display:"flex", alignItems:"center", gap:6, padding:"8px 14px", borderRadius:12, border:"none", background:"linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))", color:"var(--c-on-primary)", fontWeight:800, fontSize:13, cursor:"pointer" }}>
              <Plus size={16}/> New
            </button>
          </div>

          {/* Search + sort + folder row — mirrors the mobile layout */}
          <div style={{ display:"flex", alignItems:"center", gap:8, padding:"0 18px 10px" }}>
            <div style={{ position:"relative", flex:1, minWidth:0, display:"flex", alignItems:"center" }}>
              <Search className="jr-ctl-ico" size={14} color="var(--c-text-soft)" style={{ position:"absolute", left:10, pointerEvents:"none" }}/>
              <input className="jr-ctl" value={searchQuery} onChange={e=>setSearchQuery(e.target.value)} placeholder="Search entries…"
                style={{ width:"100%", padding:"8px 34px 8px 32px", borderRadius:99, border:"1.5px solid var(--c-border-light)", background:"var(--c-bg-card)", fontSize:13, color:"var(--c-text)", fontFamily:"inherit", outline:"none", boxSizing:"border-box" }}/>
              <button ref={sortBtnRef} onClick={() => {
                  if (!sortMenuOpen && sortBtnRef.current) {
                    const r = sortBtnRef.current.getBoundingClientRect();
                    setSortMenuPos({ top: r.bottom + 6, left: Math.min(r.left, window.innerWidth - 140 - 8) });
                  }
                  setSortMenuOpen(v => !v);
                }}
                title="Sort notes" className="jr-ctl-ico"
                style={{ position:"absolute", right:6, width:26, height:26, display:"flex", alignItems:"center", justifyContent:"center", background:"none", border:"none", cursor:"pointer", color:"var(--c-text-soft)", borderRadius:"50%" }}>
                <ArrowUpDown size={15}/>
              </button>
            </div>
            {renderFolderDropdown()}
          </div>

          {/* note list */}
          <div ref={listScrollRef} style={{ flex:1, overflowY:"auto", padding:"2px 12px 18px", display:"flex", flexDirection:"column", gap:11 }} className="clt">
            {filteredNotes.length === 0 ? (
              <div style={{ textAlign:"center", color: isDarkBgTheme() ? "#ffffff" : "#000000", fontWeight:800, padding:"34px 14px", fontSize:13 }}>
                {filterFolder ? "No notes in this folder yet." : "No entries yet. Hit New to start."}
              </div>
            ) : filteredNotes.map(note => {
              const excerpt = noteExcerpt(note).slice(0,140);
              const dt = note.updatedAt ? new Date(note.updatedAt).toLocaleString("en-US",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"}) : note.date;
              const spine = noteSpineColor(note);
              const cardBg = noteCardBg(note);
              const sel = screen === "editor" && note.id === activeNoteId;
              return (
                <button key={note.id} className={"note-light" + (isDarkNoteBg(noteBackground(note)) ? " note-ink-light" : "")} onClick={()=>openNote(note)}
                  onContextMenu={e=>{ e.preventDefault(); const r=e.currentTarget.getBoundingClientRect(); setContextMenu({rect:{left:r.left,width:r.width,top:r.top,bottom:r.bottom},note}); }}
                  // flexShrink:0 — the list is a fixed-height flex column, so once it holds
                  // more notes than fit, flex squeezed every card down to minHeight and
                  // collapsed the overflow:hidden excerpt to 0px (desktop showed no body text).
                  style={{ textAlign:"left", display:"flex", flexDirection:"column", flexShrink:0, gap:5, padding:"13px 14px 13px 16px", borderRadius:14, cursor:"pointer", fontFamily:"inherit", position:"relative", boxSizing:"border-box", minHeight:70, border: sel?"1.5px solid var(--c-primary)":"1.5px solid var(--c-border-mid)", background: cardBg, boxShadow: sel?"0 2px 12px var(--c-shadow)":"none", transition:"background 0.12s, box-shadow 0.12s, border-color 0.12s" }}
                  onMouseEnter={e=>{ if(!sel) e.currentTarget.style.boxShadow="0 2px 10px var(--c-shadow)"; }}
                  onMouseLeave={e=>{ if(!sel) e.currentTarget.style.boxShadow="none"; }}>
                  {/* Single selection signifier: a left accent bar (no competing borders) —
                      a thicker rounded pill, inset so its ends curve like the card. */}
                  {sel && <span style={{ position:"absolute", left:5, top:12, bottom:12, width:5, borderRadius:999, background:spine }} />}
                  <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                    {note.pinned && <Pin size={11} color="var(--c-primary)" style={{ flexShrink:0 }}/>}
                    <span style={{ flex:1, fontSize:13.5, fontWeight:800, color:"var(--c-text)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                      {note.title || <span style={{ color:"var(--c-text-soft)", fontStyle:"italic", fontWeight:700 }}>Untitled</span>}
                    </span>
                  </div>
                  {excerpt
                    ? <span style={{ fontSize:12, color:notePlaceholderColor(note), lineHeight:1.45, display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical", overflow:"hidden" }}>{excerpt}</span>
                    : <span style={{ fontSize:12, color:notePlaceholderColor(note), opacity:0.55, fontStyle:"italic" }}>No additional text</span>}
                  <div style={{ display:"flex", alignItems:"center", gap:6, marginTop:"auto", paddingTop:2 }}>
                    <span style={{ marginLeft:"auto", fontSize:10, color:notePlaceholderColor(note), fontWeight:600, flexShrink:0, opacity:0.7 }}>{dt}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </aside>
        )}

        {/* RIGHT — editor or empty state (floating rounded card). In focus mode
            (collapsed) it keeps exactly the width it had beside the list (the
            row minus the 330px list and 16px gap), and the outer row's
            justifyContent:center centres it. It used to cap at 820px, so the
            note visibly shrank when centred (Jade, 2026-10-21). */}
        <main style={{ flex: collapsed ? "none" : 1, width: collapsed ? "calc(100% - 346px)" : undefined, minWidth:0, height:"100%", position:"relative", overflow:"hidden", display:"flex", flexDirection:"column", background:"var(--c-bg-card)", borderRadius:24, border:"1.5px solid var(--c-border-light)", boxShadow:"0 6px 28px var(--c-shadow)" }}>
          {rightPane}
        </main>

        {contextMenuEl}
        {contextDeleteConfirmEl}
        {/* In editor state the editor renders its own move-to-folder modal, so
            only mount it here for the empty/list state to avoid a duplicate. */}
        {screen !== "editor" && folderModalEl}
        {emptyDiscardToastEl}
        {imageSavedToastEl}
      </div>
    );
  };

  // Empty-state shown in the right pane when no note is open (desktop).
  const desktopEmptyState = (
    <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", color:"var(--c-text-soft)", gap:14, padding:40, textAlign:"center" }}>
      <BookMarked size={62} color="var(--c-border-light)"/>
      <p style={{ margin:0, fontSize:19, fontWeight:800, color:"var(--c-text-soft)" }}>Pick an entry to read</p>
      <p style={{ margin:0, fontSize:14, maxWidth:320 }}>Select a note from the list on the left, or start a fresh one.</p>
      <button onClick={()=>createNote({html:""})} style={{ display:"flex", alignItems:"center", gap:6, background:"linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))", color:"var(--c-on-primary)", border:"none", borderRadius:12, padding:"10px 22px", fontWeight:800, fontSize:14, cursor:"pointer", marginTop:4 }}>
        <Plus size={16}/> New Entry
      </button>
    </div>
  );

  if (screen === "editor" && activeNote) {
    const folderRowStyle = (sel) => ({ display:"flex", alignItems:"center", gap:8, width:"100%", textAlign:"left", padding:"9px 10px", borderRadius:10, border:"none", background: sel ? "rgba(212,120,154,0.12)" : "transparent", cursor:"pointer", fontFamily:"inherit", fontSize:13, fontWeight:700, color:"var(--c-text)" });
    const editorNode = (
      // note-light: the note keeps its own paper in dark mode (Jade, 2026-10-17).
      <div className={"note-light" + (darkNote ? " note-ink-light" : "")} style={{ display:"flex", flexDirection:"column", background: photoBg ? "var(--c-bg-card)" : editorBg, overflow:"hidden",
        ...(isMobile
          // On mobile, pin the editor to the *layout* viewport at a stable full
          // height. We deliberately do NOT track vvH here: the visual viewport
          // grows when Android hides its URL bar mid-typing, which would drag a
          // bottom-anchored toolbar down behind the keyboard. Instead the
          // toolbar is position:fixed at bottom:kbHeight (layout-viewport
          // relative, immune to URL-bar jitter) and the editor pads itself.
          // Size to the visual viewport (not the layout viewport) so the top bar
          // is never scrolled out of view when the keyboard opens, and the
          // in-flow toolbar sits exactly on top of the keyboard.
          ? { position:"fixed", top: vvH ? vvTop : 0, left:0, right:0, height: vvH || undefined, bottom: vvH ? "auto" : 0, zIndex:4000, borderRadius:0, border:"none", boxShadow:"none", ...(slideInNew ? { animation:"cjNoteSlideUp 0.30s cubic-bezier(0.22,1,0.36,1) both" } : {}) }
          // On desktop, fill the whole content area (right of the sidebar) edge to
          // edge — colour/gradient notes cover the full screen; legacy aspect-
          // locked photo notes are letterboxed by the inner "stage". The backdrop
          // (filling the area right of the sidebar) is added at the portal below.
          : { position:"relative", width:"100%", height:"100%", borderRadius:0, border:"none", boxShadow:"none" }) }}>
        <style>{tbStyle}</style>

        {/* Hidden clip-path defs for shaped image frames (heart needs true curves) */}
        <svg width="0" height="0" style={{ position:"absolute" }} aria-hidden="true">
          <defs>
            <clipPath id="cj-frame-heart" clipPathUnits="objectBoundingBox">
              <path d="M0.5,1 C0.5,1 0,0.72 0,0.32 C0,0.12 0.16,0 0.3,0 C0.41,0 0.5,0.09 0.5,0.18 C0.5,0.09 0.59,0 0.7,0 C0.84,0 1,0.12 1,0.32 C1,0.72 0.5,1 0.5,1 Z" />
            </clipPath>
          </defs>
        </svg>

        {/* Image attach options — plain image · framed · lasso sticker */}
        {pendingImage && createPortal((
          <div onMouseDown={()=>setPendingImage(null)}
            style={{ position:"fixed", inset:0, zIndex:2147483646, background:"rgba(20,6,16,0.55)", display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
            <div onMouseDown={e=>e.stopPropagation()}
              style={{ background:"#fff", borderRadius:20, padding:"18px 18px 14px", width:"100%", maxWidth:360, boxShadow:"0 20px 60px rgba(0,0,0,0.35)", fontFamily:"inherit" }}>
              <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:14 }}>
                <img src={pendingImage} alt="" onError={()=>{ setPendingImage(null); showPickTip(ONEDRIVE_TIP); }} style={{ width:48, height:48, objectFit:"cover", borderRadius:10, border:"1.5px solid #f0d8e8" }} />
                <div style={{ fontSize:14, fontWeight:800, color:"var(--c-primary)" }}>
                  {imageOptStep === "frame" ? "Pick a frame ^^" : "How do you wanna add it?"}
                </div>
              </div>

              {imageOptStep === "choose" ? (
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  <button onMouseDown={e=>{ e.preventDefault(); const src=pendingImage; setPendingImage(null); requestOverlayAdd(() => addImageSticker(src, "none")); }}
                    style={imgOptBtn}>🖼️ <span style={{ marginLeft:8 }}>Attach as image</span></button>
                  <button onMouseDown={e=>{ e.preventDefault(); setImageOptStep("frame"); }}
                    style={imgOptBtn}>💠 <span style={{ marginLeft:8 }}>Attach with a frame</span></button>
                  <button onMouseDown={e=>{ e.preventDefault(); if (stickerMakerLocked) return; const src=pendingImage; setPendingImage(null); openStickerMaker(src); }}
                    style={{ ...imgOptBtn, ...(stickerMakerLocked ? { color: "var(--c-text-soft)", cursor: "default" } : {}) }}>
                    ✂️ <span style={{ marginLeft:8 }}>Cut out a sticker (lasso)</span>
                    {stickerMakerLocked && <span style={{ marginLeft: "auto" }}><LockBadge size={18} /></span>}
                  </button>
                </div>
              ) : (
                <div>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:8 }}>
                    {[["rectangle","▭","Rectangle"],["rounded","▢","Rounded"],["square","◼","Square"],["circle","⬤","Circle"],["oblong","⬭","Oblong"],["heart","❤️","Heart"],["star","⭐","Star"]].map(([f,ic,lbl])=>(
                      <button key={f} onMouseDown={e=>{ e.preventDefault(); const src=pendingImage; setPendingImage(null); requestOverlayAdd(() => addImageSticker(src, f)); }}
                        style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4, padding:"10px 4px", borderRadius:12, border:"1.5px solid #f0d8e8", background:"#fff", cursor:"pointer", fontFamily:"inherit" }}
                        onMouseEnter={e=>e.currentTarget.style.background="#fff0f7"} onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
                        <span style={{ fontSize:20, lineHeight:1 }}>{ic}</span>
                        <span style={{ fontSize:9, fontWeight:700, color:"var(--c-text-soft)" }}>{lbl}</span>
                      </button>
                    ))}
                  </div>
                  <button onMouseDown={e=>{ e.preventDefault(); setImageOptStep("choose"); }}
                    style={{ marginTop:12, fontSize:12, color:"var(--c-text-soft)", background:"transparent", border:"none", cursor:"pointer", fontFamily:"inherit" }}>← Back</button>
                </div>
              )}

              <div style={{ marginTop:12, textAlign:"right" }}>
                <button onMouseDown={e=>{ e.preventDefault(); setPendingImage(null); }}
                  style={{ fontSize:12, color:"var(--c-text-soft)", background:"transparent", border:"none", cursor:"pointer", fontFamily:"inherit" }}>Cancel</button>
              </div>
            </div>
          </div>
        ), document.body)}

        {/* Device-mismatch warning — shown once (or every time, until dismissed
            for good) before a sticker, lasso cutout, or text box is actually
            added, since it'll only ever render on this same size of device. */}
        {pendingOverlayAction && createPortal((
          <div onMouseDown={()=>setPendingOverlayAction(null)}
            style={{ position:"fixed", inset:0, zIndex:2147483646, background:"rgba(20,6,16,0.55)", display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
            <div onMouseDown={e=>e.stopPropagation()}
              style={{ background:"#fff", borderRadius:20, padding:"18px 18px 14px", width:"100%", maxWidth:360, boxShadow:"0 20px 60px rgba(0,0,0,0.35)", fontFamily:"inherit" }}>
              <div style={{ fontSize:14, fontWeight:800, color:"var(--c-primary)", marginBottom:8 }}>Heads up</div>
              <p style={{ margin:"0 0 14px", fontSize:13, color:"var(--c-text)", lineHeight:1.5 }}>
                {isDesktop
                  ? "If you open this note later on a smaller device, like your phone, you won't be able to see this attachment. It's a layout difference between device sizes."
                  : "If you open this note later on a larger device, like a laptop, you won't be able to see this attachment. It's a layout difference between device sizes."}
              </p>
              <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:12, color:"var(--c-text-soft)", marginBottom:14, cursor:"pointer" }}>
                <input type="checkbox" checked={overlayWarnDontShowAgain} onChange={e=>setOverlayWarnDontShowAgain(e.target.checked)} />
                Don't show this again
              </label>
              <div style={{ display:"flex", justifyContent:"flex-end", gap:8 }}>
                <button onMouseDown={e=>{ e.preventDefault(); setPendingOverlayAction(null); }}
                  style={{ fontSize:12, color:"var(--c-text-soft)", background:"transparent", border:"none", cursor:"pointer", fontFamily:"inherit", padding:"8px 10px" }}>Cancel</button>
                <button onMouseDown={e=>{ e.preventDefault(); confirmOverlayAdd(); }}
                  style={{ fontSize:12, fontWeight:700, color:"#fff", background:"var(--c-primary)", border:"none", borderRadius:10, cursor:"pointer", fontFamily:"inherit", padding:"8px 14px" }}>Continue</button>
              </div>
            </div>
          </div>
        ), document.body)}

        {showStickerMaker && (
          <StickerMaker
            initialSrc={stickerMakerSrc}
            editing={!!editingStickerIdRef.current}
            initialTrace={editingTraceRef.current}
            onClose={()=>{ setShowStickerMaker(false); setStickerMakerSrc(null); editingStickerIdRef.current = null; editingTraceRef.current = null; }}
            onCreate={(uri, dim, source, trace)=>{
              const aspect = dim && dim.w && dim.h ? dim.w / dim.h : 1;
              const editId = editingStickerIdRef.current;
              editingStickerIdRef.current = null;
              let newId = editId;
              if (editId) {
                // Re-editing an existing custom sticker — update in place
                updateSticker(editId, { content: uri, aspect, originalSrc: source, trace });
              } else {
                newId = addSticker({ type:"image", content:uri, frame:"none", aspect, size: 160 / STICKER_LEGACY_BASE_W, custom:true, originalSrc: source, trace }).id;
              }
              // The source photo would sit in the synced note as raw bytes, so
              // it's uploaded and swapped for the hosted URL, same as the cut-out.
              if (source?.startsWith("data:")) uploadStickerImage(source, `sticker-src-${newId}.jpg`).then(url => {
                if (url !== source) updateSticker(newId, { originalSrc: url });
              });
              // Remember it in the user's recent custom stickers (dedupe, cap 30)
              setMyStickers(prev => {
                const next = [uri, ...prev.filter(u => u !== uri)].slice(0, 30);
                try { localStorage.setItem("clarity_my_stickers", JSON.stringify(next)); } catch {}
                return next;
              });
              // Cutouts are PNGs (transparency) and often bigger than photo
              // stickers — same silent-sync-failure risk, so upload + swap too.
              uploadStickerImage(uri, `sticker-${newId}.png`).then(url => {
                if (url === uri) return;
                updateSticker(newId, { content: url });
                setMyStickers(prev => {
                  const next = prev.map(u => u === uri ? url : u);
                  try { localStorage.setItem("clarity_my_stickers", JSON.stringify(next)); } catch {}
                  return next;
                });
              });
            }}
          />
        )}


        {cropSticker && (
          <CropModal
            src={cropSticker.content}
            initialCrop={cropSticker.crop || defaultCropFor(cropSticker.frame, cropSticker.aspect || 1)}
            defaultCrop={defaultCropFor(cropSticker.frame, cropSticker.aspect || 1)}
            onClose={()=>setCropSticker(null)}
            onDone={(crop)=>{ updateSticker(cropSticker.id, { crop }); }}
          />
        )}

        {pickTip && createPortal((
          <div onClick={()=>setPickTip("")}
            style={{ position:"fixed", left:"50%", bottom:"calc(24px + var(--sab, 0px))", transform:"translateX(-50%)", zIndex:2147483647, maxWidth:"min(92vw, 420px)", background:"#2a1622", color:"#fff", borderRadius:14, padding:"12px 16px", fontSize:12.5, lineHeight:1.5, boxShadow:"0 8px 30px rgba(0,0,0,0.4)", cursor:"pointer", fontFamily:"inherit" }}>
            {pickTip}
            <div style={{ fontSize:10, opacity:0.6, marginTop:4 }}>tap to dismiss</div>
          </div>
        ), document.body)}

        <div style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px", paddingTop: isMobile ? "calc(10px + var(--sat, 0px))" : "10px", borderBottom:"none", background: photoBg ? "var(--c-bg-card)" : "transparent", flexShrink:0, position:"relative", zIndex:230, ...(isMobile?{}:{ minHeight:62 }) }}>
          {focusToggleBtn}
          {/* Desktop: no back button — the note list pane is always one click away
              (or one focus-mode toggle away), so "+ New" and clicking a card in the
              list are the only navigation the user needs. Mobile keeps it since the
              editor is a full-screen overlay with no persistent list beside it. */}
          {!isDesktop && (
            <button onClick={requestClose}
              style={{ display:"flex", alignItems:"center", justifyContent:"center", background:"transparent", border:"none", borderRadius:10, padding:"6px", marginLeft:-6, cursor:"pointer", flexShrink:0 }}>
              <ChevronLeft size={20} color="var(--c-primary)" />
            </button>
          )}
          {isMobile ? (
            <button data-tour="journal-folder-btn" onMouseDown={e=>e.preventDefault()} onClick={()=>{ setNewFolderName(""); setCreatingFolder(false); setFolderTargetId(activeNote.id); }}
              style={{ flex:1, minWidth:0, display:"flex", alignItems:"center", gap:6, justifyContent:"center", paddingLeft:18, background:"transparent", border:"none", cursor:"pointer", fontFamily:"inherit", color: darkNote ? "#f1edf3" : "#000000", fontSize:13, fontWeight:700, overflow:"hidden" }}>
              <Folder size={14} color={darkNote ? "#f1edf3" : "#000000"} />
              <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{folderName(activeNote.folder) || "Add to folder"}</span>
            </button>
          ) : (
            <div style={{ flex:1 }} />
          )}
          <button title="Save now" onClick={()=>{ if (autosaveTimer.current) clearTimeout(autosaveTimer.current); doSave(); }}
            style={{ fontSize:11, color:savedFlash?"#48bb78":(isDirty?"var(--c-text-soft)":"#48bb78"), fontWeight:600, flexShrink:0, transition:"color 0.3s", background:"transparent", border:"none", cursor:"pointer", fontFamily:"inherit", padding:"4px 2px", marginRight:-6 }}>
            {savedFlash ? "✓ Saved" : isDirty ? "Saving… (tap to save)" : "Saved"}
          </button>
          <div style={{ position:"relative", marginRight:-6 }} ref={moreMenuRef}>
            <button onClick={()=>setShowMoreMenu(v=>!v)} onMouseDown={e=>e.stopPropagation()}
              style={{ display:"flex", alignItems:"center", justifyContent:"center", width:34, height:34, borderRadius:10, border:"none", background:"transparent", cursor:"pointer" }}>
              <MoreVertical size={16} color="var(--c-text-soft)" />
            </button>
            {showMoreMenu && (
              <div onMouseDown={e=>e.stopPropagation()} style={{ position:"absolute", top:"calc(100% + 6px)", right:0, background:"#ffffff", border:"1.5px solid #f0d0e8", borderRadius:13, padding:5, zIndex:300, boxShadow:"0 8px 28px rgba(160,60,120,0.13)", minWidth:180 }}>
                {[
                  {label:"Export .txt",act:()=>exportNote("txt")},
                  {label:"Export .md",act:()=>exportNote("md")},
                  {label:"Export .html",act:()=>exportNote("html")},
                  {label:"Export .pdf",act:()=>exportNote("pdf"),locked:!isPremium},
                  ...(isMobile ? [{label:"Save as Image",act:()=>exportNote("image"),locked:!isPremium}] : []),
                  {label:"Share / Copy",act:shareNote},
                  {label:"Duplicate",act:()=>{duplicateNote(activeNote);setShowMoreMenu(false);}},
                  {label:activeNote.pinned?"Unpin":"Pin",act:()=>{togglePin(activeNote.id);setShowMoreMenu(false);}},
                  {label:"Delete",act:()=>{setShowDeleteConfirm(true);setShowMoreMenu(false);},danger:true},
                ].map(item=>(
                  <button key={item.label} onClick={item.locked ? undefined : item.act}
                    style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8, width:"100%", textAlign:"left", padding:"8px 12px", borderRadius:9, border:"none", cursor:item.locked?"default":"pointer", fontFamily:"inherit", background:"transparent", color:item.locked?"var(--c-text-soft)":(item.danger?"#e8647a":"#3a2035"), fontSize:13 }}
                    onMouseEnter={e=>{ if(!item.locked) e.currentTarget.style.background="#ffffff"; }}
                    onMouseLeave={e=>e.currentTarget.style.background="transparent"}
                  >
                    {item.label}
                    {item.locked && <LockBadge size={18} />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Always-mounted: keep the file input outside the toolbar's conditional.
            On mobile, tapping Image closes the insert panel which un-mounts the
            toolbar; if the input lived there it would vanish while the native
            gallery is still open, so onChange would never fire on return. */}
        <input ref={imageInputRef} type="file" accept="image/*"
          style={{ position:"absolute", width:1, height:1, opacity:0, pointerEvents:"none" }}
          onChange={e=>{ const f=e.target.files && e.target.files[0]; e.target.value=""; handleImageFile(f); }} />
        {/* Camera photo goes through the same image flow as Image (overlay /
            sticker options). Video, audio and recording inputs live in `media`. */}
        <input ref={cameraInputRef} type="file" accept="image/*" capture="environment"
          style={{ position:"absolute", width:1, height:1, opacity:0, pointerEvents:"none" }}
          onChange={e=>{ const f=e.target.files && e.target.files[0]; e.target.value=""; handleImageFile(f); }} />
        <span style={{ display:"none" }}>{media.inputs}</span>
        <MediaStatus media={media} floating />

        {toolbarVisible && (
        <div style={ isMobile
          ? { display:"contents" }
          // Desktop: float the toolbar+folder centered over the top bar.
          : { position:"absolute", top:9, left:"50%", transform:"translateX(-50%)", zIndex:240,
              display:"flex", alignItems:"center", gap:8, maxWidth:"calc(100% - 220px)" } }>
        <div className="clt"
          data-tour="journal-toolbar"
          onScroll={updateToolbarOverflow}
          ref={el => { toolbarScrollRef.current = el; if (el) requestAnimationFrame(updateToolbarOverflow); }}
          style={{
          display:"flex", alignItems:"center", gap:4, padding:"7px 12px",
          background:"#ffffff", overflowX:"auto",
          touchAction:"pan-x", overscrollBehavior:"contain",
          ...(isMobile
            // Always fixed above the keyboard. When the VirtualKeyboard API is
            // active (and no panel is open) the bottom is `env(keyboard-inset-
            // height)` — the compositor locks the bar to the keyboard's top edge
            // every frame, so it cannot float in or bounce. Otherwise (panel open,
            // or fallback browsers) we use the JS-measured dock height.
            ? { position:"fixed", bottom: toolbarUsesEnv ? "env(keyboard-inset-height, 0px)" : dockBottom,
                left:0, right:0, zIndex:5001, flexShrink:0,
                background:"#ffffff",
                borderTop:"1.5px solid var(--c-border-light)", boxShadow:"0 -4px 18px rgba(0,0,0,0.10)" }
            // Desktop: a frosted rounded pill (centered by the group wrapper). The
            // Plain white pill (no theme tint).
            : { minWidth:0, borderRadius:18, border:"1px solid rgba(240,200,225,0.7)",
                background:"#ffffff", backdropFilter:"blur(14px)", WebkitBackdropFilter:"blur(14px)",
                boxShadow:"0 8px 26px rgba(150,70,120,0.14)" }),
        }}>

          {/* Scroll hint: sticks to the left edge of the scrollable toolbar
              while there's hidden content to the left, so it's obvious the
              bar scrolls instead of just ending. Sticky (not absolute) so it
              stays put through scroll without the parent needing intrinsic
              height — important since the mobile toolbar is position:fixed. */}
          {toolbarOverflow.left && (
            <div style={{ position:"sticky", left:0, alignSelf:"stretch", width:14, flexShrink:0,
              zIndex:1, pointerEvents:"none", marginRight:-4,
              background:"linear-gradient(to right, #ffffff, rgba(255,255,255,0))" }} />
          )}

          {/* Aa — text & header formatting (headings, font, size) — compact chip */}
          <div ref={el=>btnRefs.current.heading=el} style={{ position:"relative" }}>
            <button className={`ctb${showHeadingDd?" ctb-on":""}`} style={{ padding:"0 6px", gap:1 }} title="Text formatting"
              onMouseDown={e=>{ e.preventDefault(); e.stopPropagation(); togglePanel("heading"); }}>
              <span style={{ fontSize:14, fontWeight:800, fontFamily:"Georgia, serif" }}>Aa</span><ChevronDown size={11}/>
            </button>
          </div>

          <div className="clt-div"/>

          {[["B","bold"],["I","italic"]].map(([lbl,cmd])=>(
            <button key={cmd} className={`ctb ctb-icon${activeFormats[cmd]?" ctb-on":""}`} onMouseDown={e=>{ e.preventDefault(); saveRange(); execCmd(cmd); }}>
              <span style={{ fontSize:13, fontWeight:700, fontStyle:cmd==="italic"?"italic":"normal" }}>{lbl}</span>
            </button>
          ))}
          {(() => {
            const alignMode = activeFormats.justifyCenter ? "center" : activeFormats.justifyRight ? "right" : activeFormats.justifyFull ? "justify" : "left";
            return (
              <button className="ctb ctb-icon" title={`Align: ${alignMode} (tap to cycle)`} onMouseDown={e=>{ e.preventDefault(); cycleAlign(); }}>
                <AlignIcon mode={alignMode} />
              </button>
            );
          })()}

          <div className="clt-div"/>
          <button className="ctb ctb-icon" title="Undo (Ctrl+Z)" onMouseDown={e=>{ e.preventDefault(); doUndo(); }}>
            <Undo2 size={14}/>
          </button>
          <button className="ctb ctb-icon" title="Redo (Ctrl+Shift+Z)" onMouseDown={e=>{ e.preventDefault(); doRedo(); }}>
            <Redo2 size={14}/>
          </button>

          <div className="clt-div"/>

          {/* Insert — lists, checklist, divider, image (merged to save space) */}
          <div ref={el=>btnRefs.current.insert=el} style={{ position:"relative" }}>
            <button className={`ctb ctb-wide${showInsertDd?" ctb-on":""}`} style={{ minWidth:46, gap:2 }} title="Insert"
              onMouseDown={e=>{ e.preventDefault(); e.stopPropagation(); togglePanel("insert"); }}>
              <Plus size={15}/><ChevronDown size={12}/>
            </button>
          </div>
          <div className="clt-div"/>

          <div ref={el=>btnRefs.current.highlight=el} style={{ position:"relative" }}>
            <button className={`ctb ctb-icon${showHighlightDd?" ctb-on":""}`} title="Highlight" onMouseDown={e=>{ e.preventDefault(); e.stopPropagation(); togglePanel("highlight"); }}>
              <span style={{ display:"flex", flexDirection:"column", alignItems:"center", lineHeight:1 }}>
                <Highlighter size={15} />
                <span style={{ width:16, height:3, borderRadius:1, marginTop:1, background:lastHighlight }} />
              </span>
            </button>

          </div>

          <div ref={el=>btnRefs.current.textcolor=el} style={{ position:"relative" }}>
            <button className={`ctb ctb-icon${showTextColorDd?" ctb-on":""}`} title="Text color" onMouseDown={e=>{ e.preventDefault(); e.stopPropagation(); togglePanel("textcolor"); }}>
              <span style={{ fontWeight:800, fontSize:15, borderBottom:`3px solid ${lastTextColor}`, lineHeight:1, paddingBottom:1 }}>A</span>
            </button>
          </div>

          <div className="clt-div"/>

          {/* Text box — draggable/resizable floating text overlay on the note */}
          <button className="ctb ctb-icon" title="Text box" onMouseDown={e=>{ e.preventDefault(); e.stopPropagation(); closePanels(); requestOverlayAdd(() => addSticker({ type:"text" })); }}>
            <Type size={15} />
          </button>

          <div className="clt-div"/>

          {/* Sticker picker button (custom cut-out stickers) */}
          <button ref={el=>btnRefs.current.sticker=el} className={`ctb ctb-icon${showStickerPicker?" ctb-on":""}`} title="Stickers" onMouseDown={e=>{ e.preventDefault(); e.stopPropagation(); togglePanel("sticker"); }}
            style={{ position:"relative" }}>
            <Sparkle size={16} weight="fill" color="#000000" />
          </button>

          <div className="clt-div"/>

          <button ref={el=>btnRefs.current.bg=el} className={`ctb ctb-icon${showBgPanel?" ctb-on":""}`} title="Background color" onMouseDown={e=>{ e.preventDefault(); e.stopPropagation(); togglePanel("bg"); }} style={{ position:"relative" }}>
            <Palette size={15} />
            <div style={{ position:"absolute", bottom:3, right:3, width:8, height:8, borderRadius:"50%", background:currentNoteColor, border:"1px solid #ccc" }}/>
          </button>

          {/* Scroll hint: mirrors the left one, sticks to the right edge while
              there's more content to scroll to. */}
          {toolbarOverflow.right && (
            <div style={{ position:"sticky", right:0, alignSelf:"stretch", width:14, flexShrink:0,
              zIndex:1, pointerEvents:"none", marginLeft:-4,
              background:"linear-gradient(to left, #ffffff, rgba(255,255,255,0))" }} />
          )}
        </div>

        {/* Folder dropdown — sits next to the toolbar (desktop top bar) */}
        {!isMobile && (
          <div ref={desktopFolderDdRef} style={{ position:"relative", flexShrink:0 }}>
            <button data-tour="journal-folder-btn" title="Folder" onMouseDown={e=>{ e.preventDefault(); e.stopPropagation(); closePanels(); setShowFolderDd(v=>!v); }}
              style={{ display:"flex", alignItems:"center", gap:3, height:46, padding:"0 11px", cursor:"pointer", fontFamily:"inherit",
                borderRadius:18, border: showFolderDd ? "1px solid var(--c-primary)" : "1px solid rgba(240,200,225,0.7)",
                background:"#ffffff", backdropFilter:"blur(14px)", WebkitBackdropFilter:"blur(14px)",
                boxShadow:"0 8px 26px rgba(150,70,120,0.14)", color: showFolderDd ? "var(--c-primary)" : "var(--c-text)" }}>
              <Folder size={16} color="var(--c-primary)" /><ChevronDown size={13} />
            </button>
            {showFolderDd && (
              <div data-tour="journal-folder-drawer" onMouseDown={e=>e.stopPropagation()} style={{ position:"absolute", top:"calc(100% + 8px)", left:"50%", transform:"translateX(-50%)", zIndex:250, width:240,
                background:"rgba(255,247,251,0.96)", backdropFilter:"blur(14px)", WebkitBackdropFilter:"blur(14px)",
                border:"1px solid rgba(240,200,225,0.7)", borderRadius:0, boxShadow:"0 12px 30px rgba(150,70,120,0.18)", padding:8, maxHeight:360, overflowY:"auto" }}>
                <div style={{ fontSize:10, fontWeight:800, color:"var(--c-text-soft)", textTransform:"uppercase", letterSpacing:0.6, padding:"4px 8px 7px" }}>Folder</div>
                <button onClick={()=>{ moveNoteToFolder(activeNote.id, null); setShowFolderDd(false); }} style={folderRowStyle(!activeNote.folder)}>
                  <X size={15} color="var(--c-text-soft)"/> <span style={{ flex:1 }}>None (Unfiled)</span>
                  {!activeNote.folder && <Check size={15} color="var(--c-primary)"/>}
                </button>
                {journalFolders.map(f=>(
                  <button key={f.id} onClick={()=>{ moveNoteToFolder(activeNote.id, f.id); setShowFolderDd(false); }} style={folderRowStyle(activeNote.folder===f.id)}>
                    <Folder size={15} color="var(--c-primary)"/> <span style={{ flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{f.name}</span>
                    {activeNote.folder===f.id && <Check size={15} color="var(--c-primary)"/>}
                  </button>
                ))}
                <div style={{ height:1, background:"var(--c-border-light)", opacity:0.7, margin:"6px 4px" }}/>
                {creatingFolder ? (
                  <div style={{ display:"flex", flexDirection:"column", gap:9 }}>
                    <div style={{ display:"flex", gap:6, padding:"2px 4px" }}>
                      <input autoFocus value={newFolderName} onChange={e=>setNewFolderName(e.target.value)}
                        onKeyDown={e=>{ if(e.key==="Enter"){ const id=addFolder(newFolderName); if(id) moveNoteToFolder(activeNote.id, id); setNewFolderName(""); setCreatingFolder(false); setShowFolderDd(false); } }}
                        placeholder="Folder name" style={{ flex:1, minWidth:0, padding:"8px 10px", borderRadius:10, border:"1.5px solid var(--c-primary)", background:"#fff", fontSize:13, fontFamily:"inherit", outline:"none" }}/>
                      <button onClick={()=>{ const id=addFolder(newFolderName); if(id) moveNoteToFolder(activeNote.id, id); setNewFolderName(""); setCreatingFolder(false); setShowFolderDd(false); }}
                        style={{ padding:"8px 12px", borderRadius:10, border:"none", background:"var(--c-primary)", color:"#fff", cursor:"pointer", fontWeight:800, fontSize:12 }}>Add</button>
                    </div>
                    {(() => {
                      const usedNames = new Set(journalFolders.map(f => f.name.toLowerCase()));
                      const sugg = [
                        ...(!usedNames.has("uncategorized") ? [{ id: "__uncategorized__", title: "Uncategorized", emoji: "🗂", color: "#b0a4bc" }] : []),
                        ...categories.filter(c => c.title && !usedNames.has(c.title.toLowerCase())),
                      ];
                      if (sugg.length === 0) return null;
                      return (
                        <div data-tour="journal-spaces-section" style={{ padding:"0 4px" }}>
                          <span style={{ fontSize:9.5, fontWeight:800, color:"var(--c-text-soft)", textTransform:"uppercase", letterSpacing:0.5 }}>From your categories</span>
                          <div style={{ display:"flex", gap:5, flexWrap:"wrap", marginTop:5 }}>
                            {sugg.map(c => (
                              <button key={c.id} onClick={()=>{ const id=addFolder(c.title); if(id) moveNoteToFolder(activeNote.id, id); setNewFolderName(""); setCreatingFolder(false); setShowFolderDd(false); }}
                                style={{ display:"flex", alignItems:"center", gap:4, padding:"5px 9px", borderRadius:999, border:`1.5px solid ${(c.color||"var(--c-border-light)")}`, background:"#fff", cursor:"pointer", fontFamily:"inherit", fontSize:11, fontWeight:700, color:"var(--c-text)" }}>
                                {c.emoji && <span style={{ fontSize:12 }}>{c.emoji}</span>}{c.title}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <button data-tour="journal-new-folder-btn" onClick={()=>{ setNewFolderName(""); setCreatingFolder(true); }}
                    style={{ display:"flex", alignItems:"center", gap:8, width:"100%", padding:"9px 10px", borderRadius:10, border:"none", background:"transparent", cursor:"pointer", fontFamily:"inherit", fontSize:13, fontWeight:700, color:"var(--c-primary)" }}>
                    <FolderPlus size={15}/> New folder
                  </button>
                )}
              </div>
            )}
          </div>
        )}
        </div>
        )}


        {/* ── Aa format panel — dropdowns + size stepper ──────────────────── */}
        {showHeadingDd && (() => {
          const STYLE_OPTS = [["p","Normal text"],["h1","Heading 1"],["h2","Heading 2"],["h3","Heading 3"]];
          const FONT_OPTS  = FONTS.map(f=>[f,f]);
          let currentBlockTag = "p";
          try { const b = (document.queryCommandValue("formatBlock")||"").toLowerCase(); if (["h1","h2","h3"].includes(b)) currentBlockTag = b; } catch {}
          // Reflect the SELECTION's actual font, not just the note-wide default —
          // this used to always read activeNote.font (a separate whole-note
          // setting), so the dropdown showed "Default" even when the highlighted
          // text had its own font applied via applyFont().
          // Unstyled text renders in the app font, Quicksand, so that's what it shows as.
          let curFont = (activeNote.font && activeNote.font !== "normal") ? activeNote.font : "Quicksand";
          try { const qf = (document.queryCommandValue("fontName")||"").split(",")[0].replace(/["']/g,"").trim(); if (qf && FONTS.includes(qf)) curFont = qf; } catch {}
          const styleLabel = (STYLE_OPTS.find(([t])=>t===currentBlockTag)||STYLE_OPTS[0])[1];
          const fontLabel  = (FONT_OPTS.find(([v])=>v===curFont)||FONT_OPTS[0])[1];
          const commitSize = (px) => { const v = Math.max(6, Math.min(200, px||fontSize)); setFontSize(v); applyPxFontSize(v); keepPanel(); };

          // Style / font selection opens as a full-panel picker (no clipping in
          // the cramped keyboard-height slot).
          if (showStyleDd || showFontDd) {
            const isFont = showFontDd;
            const opts   = isFont ? FONT_OPTS : STYLE_OPTS;
            const cur    = isFont ? curFont : currentBlockTag;
            const back   = () => { setShowFontDd(false); setShowStyleDd(false); };
            // For a font pick in the note body we keep the editor focused (no keepPanel blur) so the
            // pending inline format survives and the next typed text uses the font.
            const pick   = (v) => { if (isFont) { applyFont(v); back(); } else { applyHeading(v); back(); keepPanel(); } };
            return panelPortal(
              <div ref={panelRef} onMouseDown={e=>e.stopPropagation()} style={{ background:"#ffffff", flexShrink:0, ...anchorPanelStyle(280), borderRadius:0 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 12px", position:"sticky", top:0, background:"#fff", zIndex:1 }}>
                  <button onMouseDown={e=>{e.preventDefault();back();}} style={{ display:"flex", alignItems:"center", gap:4, background:"var(--c-bg-soft)", border:"none", borderRadius:10, padding:"6px 10px", cursor:"pointer", fontFamily:"inherit", fontSize:12, fontWeight:700, color:"var(--c-text)" }}>
                    <ChevronLeft size={14}/> Back
                  </button>
                  <span style={{ fontSize:12, fontWeight:800, color:"var(--c-text-soft)" }}>{isFont?"Font":"Paragraph style"}</span>
                </div>
                {opts.map(([val,lbl])=>(
                  <button key={val} onMouseDown={e=>{e.preventDefault();pick(val);}}
                    style={{ display:"flex", alignItems:"center", justifyContent:"space-between", width:"100%", textAlign:"left", padding:"13px 16px", border:"none", background: cur===val?"var(--c-bg-soft)":"#fff", color: cur===val?"var(--c-primary)":"var(--c-text)", cursor:"pointer",
                      fontFamily: isFont ? val : "inherit",
                      fontSize: isFont ? 16 : (val==="p"?14:val==="h1"?22:val==="h2"?18:16),
                      fontWeight: isFont ? 600 : (val==="p"?600:800) }}>
                    <span>{lbl}</span>
                    {cur===val && <Check size={16} color="var(--c-primary)"/>}
                  </button>
                ))}
              </div>
            );
          }

          // While the size field is focused the numeric keyboard is up, so float
          // the panel just above it instead of letting it sit behind the keyboard.
          const aaDock = (dockActive && sizeFocused)
            ? { position:"fixed", bottom:kbOffset, left:0, right:0, zIndex:5002, overflowY:"auto", maxHeight:"50vh",
                borderTop:"1.5px solid var(--c-border-light)", boxShadow:"0 -10px 24px rgba(0,0,0,0.10)" }
            : anchorPanelStyle(360);
          return panelPortal(
          <div ref={panelRef} onMouseDown={e=>{ e.stopPropagation(); e.preventDefault(); }} style={{ padding:"14px 14px 16px", background:"#ffffff", flexShrink:0, ...aaDock, borderRadius:0 }}>
            <div style={{ fontSize:10, fontWeight:800, color:"var(--c-text-soft)", textTransform:"uppercase", letterSpacing:0.6, marginBottom:8 }}>Format</div>

            {/* B / I / U / S */}
            <div style={{ display:"flex", gap:6, marginBottom:12 }}>
              {[["B","bold","normal"],["I","italic","italic"],["U","underline","underline"],["S","strikeThrough","strike"]].map(([lbl,cmd,deco])=>(
                <button key={cmd} onMouseDown={e=>{e.preventDefault();execCmd(cmd);keepPanel();}}
                  style={{ flex:1, padding:"10px 0", borderRadius:12, border:"none", background:activeFormats[cmd]?"color-mix(in srgb, var(--c-primary) 16%, var(--c-bg-card))":"rgba(120,120,128,0.08)", color:activeFormats[cmd]?"var(--c-primary)":"var(--c-text)", cursor:"pointer", fontFamily:"inherit", fontSize:14, fontWeight:700, fontStyle:deco==="italic"?"italic":"normal", textDecoration:deco==="underline"?"underline":(deco==="strike"?"line-through":"none") }}>{lbl}</button>
              ))}
            </div>

            {/* Paragraph style dropdown */}
            <div style={{ marginBottom:9 }}>
              <button onMouseDown={e=>{e.preventDefault();e.stopPropagation();setShowStyleDd(true);setShowFontDd(false);}}
                style={{ width:"100%", display:"flex", alignItems:"center", justifyContent:"space-between", padding:"11px 14px", borderRadius:14, border:"none", background:"rgba(120,120,128,0.08)", cursor:"pointer", fontFamily:"inherit", fontSize:14, fontWeight:700, color:"var(--c-text)" }}>
                <span>{styleLabel}</span><ChevronDown size={16} color="var(--c-text-soft)"/>
              </button>
            </div>

            {/* Font family dropdown */}
            <div style={{ marginBottom:14 }}>
              <button onMouseDown={e=>{e.preventDefault();e.stopPropagation();setShowFontDd(true);setShowStyleDd(false);}}
                style={{ width:"100%", display:"flex", alignItems:"center", justifyContent:"space-between", padding:"11px 14px", borderRadius:14, border:"none", background:"rgba(120,120,128,0.08)", cursor:"pointer", fontFamily: curFont, fontSize:14, fontWeight:700, color:"var(--c-text)" }}>
                <span>{fontLabel}</span><ChevronDown size={16} color="var(--c-text-soft)"/>
              </button>
            </div>

            {/* Size stepper: −  [number]  + */}
            <div style={{ fontSize:10, fontWeight:800, color:"var(--c-text-soft)", textTransform:"uppercase", letterSpacing:0.6, marginBottom:7 }}>Size</div>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <button onMouseDown={e=>{e.preventDefault();commitSize((parseInt(fontSize,10)||0)-1);}}
                style={{ width:46, height:46, borderRadius:"50%", border:"none", background:"rgba(120,120,128,0.08)", cursor:"pointer", fontSize:22, fontWeight:700, color:"var(--c-text)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>−</button>
              <div title={isMobile ? "Font size" : "Type a number in the note to set size"}
                style={{ flex:1, minWidth:0, textAlign:"center", padding:"11px 0", borderRadius:14, border:"none", background:"rgba(120,120,128,0.08)", fontFamily:"inherit", fontSize:18, fontWeight:800, color:"var(--c-primary)", lineHeight:"normal", userSelect:"none" }}>
                {fontSize === "" ? "-" : fontSize}
              </div>
              <button onMouseDown={e=>{e.preventDefault();commitSize((parseInt(fontSize,10)||0)+1);}}
                style={{ width:46, height:46, borderRadius:"50%", border:"none", background:"rgba(120,120,128,0.08)", cursor:"pointer", fontSize:22, fontWeight:700, color:"var(--c-text)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>+</button>
            </div>
          </div>
          );
        })()}

        {/* ── Insert inline panel ─────────────────────────────────────────── */}
        {showInsertDd && panelPortal(
          <div ref={panelRef} onMouseDown={e=>e.stopPropagation()} style={{
            padding:"12px 14px", background:"#ffffff", flexShrink:0,
            // Even 5-across grid, so "Embed link" sits on the first row beside
            // Divider (Jade, 2026-10-06) — in the old wrapping row it didn't
            // fit and dropped to the second line.
            // Columns as wide as their widest label, pushed to both edges, so the
            // gap left of Bullet / Image matches the gap right of Embed link /
            // Record (Jade, 2026-10-16; equal 1fr columns left the outer gaps
            // uneven because the labels differ in width).
            display:"grid", gridTemplateColumns:"repeat(5, auto)", justifyContent:"space-between",
            columnGap:4, rowGap:8, alignContent:"flex-start",
            ...anchorPanelStyle(360),
            borderRadius:0,
          }}>
            {[
              {label:"Bullet",   icon:<span style={{fontSize:15}}>•≡</span>,               act:()=>execCmd("insertUnorderedList"), image:false},
              {label:"Numbered", icon:<span style={{fontSize:12,fontWeight:700}}>1≡</span>, act:()=>execCmd("insertOrderedList"),   image:false},
              {label:"Checklist",icon:<CheckSquare size={15}/>,                            act:()=>insertChecklist(),               image:false},
              {label:"Divider",  icon:<span style={{fontSize:13}}>─</span>,                 act:()=>insertDivider(),                 image:false},
              {label:"Embed link",icon:<Link2 size={15}/>,                                 act:()=>{ setEmbedRows([{ url:"", error:"" }]); setInsertEmbedMode(true); }, image:false},
              {label:"Image",    icon:<Image size={15}/>,                                  act:()=>imageInputRef.current?.click(),  image:true},
              {label:"Camera",   icon:<Camera size={15}/>,                                 act:()=>cameraInputRef.current?.click(), image:true},
              ...(isCloudinaryConfigured ? [
                {label:"Video",  icon:<Video size={15}/>,                                  act:media.openVideo,                     image:true},
                {label:"Audio",  icon:<Music size={15}/>,                                  act:media.openAudio,                     image:true},
                media.recording
                  ? {label:"Stop", icon:<Square size={14} fill="currentColor"/>,           act:media.stopRec,                       image:true}
                  : {label:"Record", icon:<Mic size={15}/>,                                act:media.startRec,                      image:true},
              ] : []),
            ].map(item=>(
              <button key={item.label}
                onMouseDown={item.image ? undefined : e=>{e.preventDefault();item.act();setShowInsertDd(false);}}
                onClick={item.image ? ()=>{item.act();setShowInsertDd(false);} : undefined}
                style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:4, padding:"8px 2px", borderRadius:10, border:"none", background:"var(--c-bg-soft)", cursor:"pointer", fontFamily:"inherit", fontSize:11, fontWeight:700, color:"var(--c-text)", minWidth:0, textAlign:"center", lineHeight:1.15 }}>
                <span style={{ color:"var(--c-primary)" }}>{item.icon}</span>
                {item.label}
              </button>
            ))}
          </div>
        )}

        {/* ── Embed-link modal: centered popup, dims + blurs the background ──
            instead of swapping the URL field into the keyboard-docked Insert
            panel (that swap fought with the on-screen keyboard for the same
            slot). "+" queues another link row; all rows embed together. ── */}
        {insertEmbedMode && createPortal(
          <div onClick={()=>setInsertEmbedMode(false)} style={{
            position:"fixed", inset:0, zIndex:9600, background:"rgba(60,30,50,0.42)",
            backdropFilter:"blur(6px)", WebkitBackdropFilter:"blur(6px)",
            display:"flex", alignItems:"center", justifyContent:"center", padding:20,
          }}>
            <div onClick={e=>e.stopPropagation()} style={{
              background:"var(--c-bg-card)", borderRadius:20, padding:"18px 18px 16px", width:"100%", maxWidth:380,
              maxHeight:"80vh", display:"flex", flexDirection:"column", boxShadow:"0 12px 44px rgba(0,0,0,0.24)",
            }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
                <span style={{ fontWeight:900, fontSize:15, color:"var(--c-text)" }}>Embed a link</span>
                <button onClick={()=>setInsertEmbedMode(false)} style={{ background:"transparent", border:"none", cursor:"pointer", padding:4 }}>
                  <X size={18} color="var(--c-text-soft)"/>
                </button>
              </div>

              {/* Scrollable so a long list of queued links doesn't grow the
                  modal past the screen — caps at roughly 3 rows tall. */}
              <div style={{ overflowY:"auto", maxHeight:200, display:"flex", flexDirection:"column", gap:10 }}>
                {embedRows.map((row, idx) => (
                  <div key={idx} style={{ display:"flex", flexDirection:"column", gap:4 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                      <input
                        autoFocus={idx === embedRows.length - 1}
                        value={row.url}
                        onChange={e=>updateEmbedRow(idx, { url:e.target.value })}
                        onKeyDown={e=>{ if(e.key==="Enter"){ e.preventDefault(); submitEmbed(); } if(e.key==="Escape"){ e.preventDefault(); setInsertEmbedMode(false); } }}
                        placeholder="Paste a link…"
                        style={{ flex:1, minWidth:0, boxSizing:"border-box", border:"1.5px solid var(--c-border-light)", borderRadius:8, padding:"9px 10px", fontSize:13, outline:"none", fontFamily:"inherit", background:"var(--c-bg-soft)", color:"var(--c-text)" }}
                      />
                      {embedRows.length > 1 && (
                        <button onClick={()=>removeEmbedRow(idx)} title="Remove this link"
                          style={{ flexShrink:0, width:30, height:30, borderRadius:"50%", border:"1.5px solid var(--c-border-light)", background:"var(--c-bg-soft)", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>
                          <X size={14} color="var(--c-text-soft)"/>
                        </button>
                      )}
                    </div>
                    {row.error && <div style={{ fontSize:11, color:"#e8647a" }}>{row.error}</div>}
                  </div>
                ))}
              </div>

              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:14 }}>
                <button onClick={addEmbedRow} title="Add another link"
                  style={{ width:36, height:36, borderRadius:"50%", border:"1.5px solid var(--c-border-light)", background:"var(--c-bg-soft)", color:"var(--c-primary)", cursor:"pointer", fontSize:20, fontWeight:700, display:"flex", alignItems:"center", justifyContent:"center" }}>
                  +
                </button>
                <div style={{ display:"flex", gap:8 }}>
                  <button onClick={()=>setInsertEmbedMode(false)}
                    style={{ padding:"9px 16px", borderRadius:10, border:"1.5px solid var(--c-border-light)", background:"transparent", color:"var(--c-text-soft)", fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>
                    Cancel
                  </button>
                  <button onClick={submitEmbed}
                    style={{ padding:"9px 18px", borderRadius:10, border:"none", background:"var(--c-primary)", color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                    Embed
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* ── Highlight inline panel ─────────────────────────────────────── */}
        {showHighlightDd && panelPortal(
          <div ref={panelRef} onMouseDown={e=>e.stopPropagation()} style={{
            padding:"14px", background:"#ffffff", flexShrink:0, ...anchorPanelStyle(300), overflowY:"auto",
          }}>
            <div style={{ fontSize:10, fontWeight:800, color:"var(--c-text-soft)", textTransform:"uppercase", letterSpacing:0.6, marginBottom:10 }}>Highlight</div>
            {/* Swatches — shared palette with Text color */}
            {inkSwatches("No highlight", c => c ? applyHl(c, true) : applyHl(null, false))}
            {/* Custom colour — hidden behind a preview button (like the planner "Set color") */}
            <button onMouseDown={e=>{ e.preventDefault(); setHlPickerOpen(v=>!v); }}
              style={{ width:"100%", padding:"7px 0", borderRadius:10, border:"none", background: hlPickerOpen ? "color-mix(in srgb, var(--c-primary) 16%, var(--c-bg-card))" : "var(--c-bg-soft)", color:"var(--c-text-accent)", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
              🎨 {hlPickerOpen ? "Hide picker" : "Custom color"}
            </button>
            {hlPickerOpen && <div style={{ marginTop:10 }}>
              <HexColorPicker color={liveHl} onChange={c=>setLiveHl(c)} style={{ width:"100%" }} />
              <div style={{ display:"flex", gap:6, marginTop:8, alignItems:"center" }}>
                <HexColorInput color={liveHl} onChange={c=>setLiveHl(c)} prefixed
                  style={{ flex:1, padding:"5px 8px", borderRadius:8, border:"1.5px solid var(--c-border-light)", fontSize:12, fontFamily:"monospace", outline:"none" }}/>
                <button onMouseDown={e=>e.preventDefault()} onClick={()=>applyHl(liveHl, true)}
                  style={{ padding:"5px 12px", borderRadius:8, border:"none", background:"var(--c-primary)", color:"#fff", cursor:"pointer", fontSize:11, fontWeight:700, fontFamily:"inherit" }}>
                  Apply
                </button>
              </div>
            </div>}
            {/* Recent highlights */}
            {savedHighlights.length > 0 && <>
              <div style={{ fontSize:10, fontWeight:800, color:"var(--c-text-soft)", textTransform:"uppercase", letterSpacing:0.6, margin:"12px 0 6px" }}>Recent</div>
              <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
                {savedHighlights.map(c=>(
                  <button key={c} title={c} onMouseDown={e=>e.preventDefault()} onClick={()=>applyHl(c, true)}
                    style={{ width:24, height:24, borderRadius:"50%", background:c, border:"1px solid rgba(0,0,0,0.10)", cursor:"pointer", padding:0 }}/>
                ))}
              </div>
            </>}
          </div>
        )}

        {/* ── Text color inline panel ───────────────────────────────────── */}
        {showTextColorDd && panelPortal(
          <div ref={panelRef} onMouseDown={e=>e.stopPropagation()} style={{
            padding:"14px", background:"#ffffff", flexShrink:0, ...anchorPanelStyle(300), overflowY:"auto",
          }}>
            <div style={{ fontSize:10, fontWeight:800, color:"var(--c-text-soft)", textTransform:"uppercase", letterSpacing:0.6, marginBottom:10 }}>Text color</div>
            {/* Swatches — identical palette + spacing to Highlight */}
            {inkSwatches("Default", c => applyTc(c || "inherit"))}
            {/* Custom colour — hidden behind a preview button (like the planner "Set color") */}
            <button onMouseDown={e=>{ e.preventDefault(); setTcPickerOpen(v=>!v); }}
              style={{ width:"100%", padding:"7px 0", borderRadius:10, border:"none", background: tcPickerOpen ? "color-mix(in srgb, var(--c-primary) 16%, var(--c-bg-card))" : "var(--c-bg-soft)", color:"var(--c-text-accent)", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
              🎨 {tcPickerOpen ? "Hide picker" : "Custom color"}
            </button>
            {tcPickerOpen && <div style={{ marginTop:10 }}>
              <HexColorPicker color={liveTc} onChange={c=>setLiveTc(c)} style={{ width:"100%" }} />
              <div style={{ display:"flex", gap:6, marginTop:8, alignItems:"center" }}>
                <HexColorInput color={liveTc} onChange={c=>setLiveTc(c)} prefixed
                  style={{ flex:1, padding:"5px 8px", borderRadius:8, border:"1.5px solid var(--c-border-light)", fontSize:12, fontFamily:"monospace", outline:"none" }}/>
                <button onMouseDown={e=>e.preventDefault()} onClick={()=>applyTc(liveTc)}
                  style={{ padding:"5px 12px", borderRadius:8, border:"none", background:"var(--c-primary)", color:"#fff", cursor:"pointer", fontSize:11, fontWeight:700, fontFamily:"inherit" }}>
                  Apply
                </button>
              </div>
            </div>}
            {/* Recent text colors */}
            {savedTextColors.length > 0 && <>
              <div style={{ fontSize:10, fontWeight:800, color:"var(--c-text-soft)", textTransform:"uppercase", letterSpacing:0.6, margin:"12px 0 6px" }}>Recent</div>
              <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
                {savedTextColors.map(c=>(
                  <button key={c} title={c} onMouseDown={e=>e.preventDefault()} onClick={()=>applyTc(c)}
                    style={{ width:24, height:24, borderRadius:"50%", background:c, border:"1px solid rgba(0,0,0,0.14)", cursor:"pointer", padding:0 }}/>
                ))}
              </div>
            </>}
          </div>
        )}

        {/* Sticker picker — custom cut-out stickers ("Mine"). Tap "Make your
            own" to lasso a photo, or place an existing cut-out. */}
        {showStickerPicker && panelPortal(
          <div ref={panelRef} onMouseDown={e=>e.stopPropagation()} style={{
            background:"#ffffff", flexShrink:0, display:"flex", flexDirection:"column",
            ...anchorPanelStyle(360), overflowY:"hidden",
          }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 12px 4px", flexShrink:0 }}>
              <span style={{ fontSize:12, fontWeight:800, color:"var(--c-primary)" }}>🌸 My stickers</span>
              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                {myStickers.length > 0 && (
                  <button onMouseDown={e=>{ e.preventDefault(); if(stickerSelectMode){ setStickerSelectMode(false); setSelectedStickerIndices(new Set()); } else { setStickerSelectMode(true); } }}
                    style={{ fontSize:11, fontWeight:700, padding:"3px 10px", borderRadius:20, border:"none", background: stickerSelectMode ? "color-mix(in srgb, var(--c-primary) 16%, var(--c-bg-card))" : "var(--c-bg-soft)", color:"var(--c-primary)", cursor:"pointer", fontFamily:"inherit" }}>
                    {stickerSelectMode ? "Cancel" : "Select"}
                  </button>
                )}
                <button onClick={()=>{ setShowStickerPicker(false); setStickerSelectMode(false); setSelectedStickerIndices(new Set()); }} title="Close" style={{ background:"transparent", border:"none", cursor:"pointer", padding:4, lineHeight:0 }}>
                  <X size={16} color="var(--c-text-soft)" />
                </button>
              </div>
            </div>
            <div style={{ overflowY:"auto", flex:1, minHeight:0, padding:"6px 12px 12px" }}>
              {!stickerSelectMode && (
                <button onMouseDown={e=>{ e.preventDefault(); if (stickerMakerLocked) return; setShowStickerPicker(false); openStickerMaker(null); }}
                  style={{ width:"100%", padding:"10px", marginBottom:12, fontSize:12, fontWeight:700, borderRadius:12, border:"2px dashed #f0d8e8", background:"#ffffff", color: stickerMakerLocked ? "var(--c-text-soft)" : "var(--c-primary)", cursor: stickerMakerLocked ? "default" : "pointer", fontFamily:"inherit", textAlign:"center" }}>
                  {stickerMakerLocked ? <span style={{ verticalAlign: -4, marginRight: 6, display: "inline-block" }}><LockBadge size={18} /></span> : "📷 "}Make your own
                  <div style={{ fontSize:10, fontWeight:400, color:"var(--c-text-soft)", marginTop:3 }}>Trace a photo with the lasso to cut out your own sticker ✨</div>
                </button>
              )}
              {/* Washi tape + Doodle sets are hidden for now (Jade, 2026-10-26): flip
                  SHOW_STICKER_SETS to bring them back. Already placed ones still show. */}
              {SHOW_STICKER_SETS && !stickerSelectMode && (<>
                <div style={{ fontSize:11, fontWeight:800, color:"var(--c-text-soft)", marginBottom:8 }}>Washi tape</div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(3, 1fr)", gap:8, marginBottom:14 }}>
                  {WASHI_TAPES.map(t => (
                    <button key={t.id} title={t.label} aria-label={t.label}
                      onMouseDown={e=>{ e.preventDefault(); requestOverlayAdd(() => addTape(t.id)); }}
                      style={{ height:38, border:"none", borderRadius:10, background:"var(--c-bg-soft)", cursor:"pointer", padding:"0 8px", display:"flex", alignItems:"center", justifyContent:"center" }}>
                      <span style={{ display:"block", width:"100%", height:20, opacity:TAPE_OPACITY,
                        backgroundImage:`url("${tapeSrc(t.id)}")`, backgroundRepeat:"repeat-x", backgroundSize:"auto 100%",
                        clipPath: tapeClipPath(t.id, 20), WebkitClipPath: tapeClipPath(t.id, 20) }} />
                    </button>
                  ))}
                </div>
                <div style={{ fontSize:11, fontWeight:800, color:"var(--c-text-soft)", marginBottom:8 }}>Doodle</div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:8, marginBottom:14 }}>
                  {DOODLE_STICKERS.map(d => (
                    <button key={d.id} title={d.label} aria-label={d.label}
                      onMouseDown={e=>{ e.preventDefault(); requestOverlayAdd(() => addDoodle(d)); }}
                      style={{ height:58, border:"none", borderRadius:10, background:"var(--c-bg-soft)", cursor:"pointer", padding:5, display:"flex", alignItems:"center", justifyContent:"center" }}>
                      <img src={doodleSrc(d.id)} alt="" loading="lazy" draggable={false}
                        style={{ maxWidth:"100%", maxHeight:"100%", objectFit:"contain", display:"block" }} />
                    </button>
                  ))}
                </div>
              </>)}
              {!stickerSelectMode && (
                <div style={{ display:"flex", alignItems:"center", gap:8, margin:"2px 0 12px" }}>
                  <div style={{ flex:1, height:1, background:"var(--c-border-light)" }}/>
                  <span style={{ fontSize:10, fontWeight:700, color:"var(--c-text-soft)", whiteSpace:"nowrap" }}>✨ Sticker packs coming soon</span>
                  <div style={{ flex:1, height:1, background:"var(--c-border-light)" }}/>
                </div>
              )}
              <div style={{ fontSize:11, fontWeight:800, color:"var(--c-text-soft)", marginBottom:8 }}>
                {stickerSelectMode ? `Select stickers to delete${selectedStickerIndices.size > 0 ? ` (${selectedStickerIndices.size} selected)` : ""}` : "Your stickers"}
              </div>
              {myStickers.length === 0 ? (
                <div style={{ textAlign:"center", padding:"14px 0", fontSize:11, color:"var(--c-text-soft)" }}>
                  No stickers yet. Tap "Make your own" to create one ^^
                </div>
              ) : (
                <>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
                    {myStickers.map((uri, i) => {
                      const sel = selectedStickerIndices.has(i);
                      return (
                        <div key={i} style={{ position:"relative", display:"inline-block" }}>
                          <button onMouseDown={e=>{ e.preventDefault();
                            if(stickerSelectMode){
                              setSelectedStickerIndices(prev=>{ const next=new Set(prev); if(next.has(i)) next.delete(i); else next.add(i); return next; });
                            } else {
                              requestOverlayAdd(() => addSavedSticker(uri));
                            }
                          }}
                            style={{ width:54, height:54, border:"none", borderRadius:12, background: sel ? "color-mix(in srgb, var(--c-primary) 16%, var(--c-bg-card))" : "var(--c-bg-soft)", cursor:"pointer", padding:4, display:"flex", alignItems:"center", justifyContent:"center", boxSizing:"border-box" }}
                            onMouseEnter={e=>{ if(!stickerSelectMode) e.currentTarget.style.background="#fff0f7"; }}
                            onMouseLeave={e=>{ if(!stickerSelectMode) e.currentTarget.style.background="var(--c-bg-soft)"; }}>
                            <img src={uri} style={{ width:"100%", height:"100%", objectFit:"contain" }} alt="" />
                          </button>
                          {stickerSelectMode && sel && (
                            <span style={{ position:"absolute", top:-4, right:-4, width:16, height:16, borderRadius:"50%", background:"var(--c-primary)", border:"2px solid #fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:9, color:"#fff", fontWeight:800, lineHeight:1, pointerEvents:"none" }}>✓</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {stickerSelectMode && selectedStickerIndices.size > 0 && (
                    <button onMouseDown={e=>{ e.preventDefault(); setSelectedStickerId(null); setShowDeleteStickersConfirm(true); }}
                      style={{ width:"100%", marginTop:14, padding:"10px", fontSize:12, fontWeight:700, borderRadius:12, border:"none", background:"#e8647a", color:"#fff", cursor:"pointer", fontFamily:"inherit" }}>
                      Delete {selectedStickerIndices.size} sticker{selectedStickerIndices.size !== 1 ? "s" : ""}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Portalled to <body> above everything (Jade, 2026-10-21): rendered in
            place it could sit under the note's selected sticker, whose red x
            deletes on pointer-down, so tapping Delete here removed the sticker
            from the note too. The note's selection is also cleared when this opens. */}
        {showDeleteStickersConfirm && createPortal(
          <div style={{ position:"fixed", inset:0, zIndex:100050, background:"rgba(60,30,50,0.38)", display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
            <div style={{ background:"var(--c-bg-card)", borderRadius:18, padding:28, maxWidth:320, width:"100%", boxShadow:"0 8px 40px rgba(0,0,0,0.18)", textAlign:"center" }}>
              <ConfirmHeader title={`Are you sure you want to delete ${selectedStickerIndices.size === 1 ? "this sticker" : `these ${selectedStickerIndices.size} stickers`}?`} />
              <div style={CONFIRM_STACK}>
                <button onClick={()=>setShowDeleteStickersConfirm(false)} style={CONFIRM_CANCEL}>Cancel</button>
                <button onClick={()=>{ saveMyStickers(myStickers.filter((_,j)=>!selectedStickerIndices.has(j))); setSelectedStickerIndices(new Set()); setStickerSelectMode(false); setShowDeleteStickersConfirm(false); }} style={CONFIRM_DANGER}>Delete</button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {showBgPanel && panelPortal(
          <div ref={panelRef} onMouseDown={e=>e.stopPropagation()} style={{
            padding:"8px 14px 16px", background:"#ffffff", flexShrink:0,
            ...anchorPanelStyle(360),
          }}>
            <div style={{ display:"flex", justifyContent:"flex-end" }}>
              <button onClick={()=>setShowBgPanel(false)} title="Close" style={{ background:"transparent", border:"none", cursor:"pointer", padding:4, lineHeight:0 }}>
                <X size={16} color="var(--c-text-soft)" />
              </button>
            </div>
            {/* Solid colors */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(40px, 1fr))", gap:10 }}>
              {NOTE_COLORS.map(c=>{
                const sel = currentNoteColor===c && !activeNote.noteBg;
                return (
                  <button key={c} title={c} onClick={()=>updateActiveNote({color:c,noteBg:null})}
                    style={{ width:"100%", aspectRatio:"1", borderRadius:"50%", background:c, cursor:"pointer",
                      border: sel ? "2.5px solid var(--c-primary)" : "1px solid rgba(120,60,100,0.12)",
                      boxShadow: sel ? "0 0 0 3px rgba(212,120,154,0.18)" : "none" }}/>
                );
              })}
            </div>
            {/* Dark colors (Jade, 2026-10-28): the note's text turns light on these. */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(40px, 1fr))", gap:10, marginTop:10 }}>
              {NOTE_DARK_COLORS.map(c=>{
                const sel = currentNoteColor===c && !activeNote.noteBg;
                return (
                  <button key={c} title={c} onClick={()=>updateActiveNote({color:c,noteBg:null})}
                    style={{ width:"100%", aspectRatio:"1", borderRadius:"50%", background:c, cursor:"pointer",
                      border: sel ? "2.5px solid var(--c-primary)" : "1px solid rgba(120,60,100,0.12)",
                      boxShadow: sel ? "0 0 0 3px rgba(212,120,154,0.18)" : "none" }}/>
                );
              })}
            </div>
            {/* Section divider between colors and gradients */}
            <div style={{ height:1, background:"var(--c-border-light)", opacity:0.7, margin:"14px 2px" }}/>
            {/* Gradients */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(40px, 1fr))", gap:10 }}>
              {NOTE_GRADIENTS.map(g=>{
                const sel = activeNote.noteBg===g.value;
                return (
                  <button key={g.id} onClick={()=>updateActiveNote({noteBg:g.value})}
                    style={{ width:"100%", aspectRatio:"1", borderRadius:"50%", background:g.value, cursor:"pointer",
                      border: sel ? "2.5px solid var(--c-primary)" : "1px solid rgba(120,60,100,0.12)",
                      boxShadow: sel ? "0 0 0 3px rgba(212,120,154,0.18)" : "none" }}/>
                );
              })}
            </div>
          </div>
        )}

        {/* Stage: when a scene image is active, this centers + letterboxes the
            canvas to the image's exact aspect ratio (contain-fit via container
            query units) so nothing is cropped. Otherwise it's a transparent
            full-size column wrapper and the canvas fills it as before. */}
        <div style={ aspectLock
          ? { flex:1, minHeight:0, order: isMobile ? 2 : 0, display:"flex", alignItems:"center", justifyContent:"center", containerType:"size", background:"var(--c-bg)" }
          : { flex:1, minHeight:0, order: isMobile ? 2 : 0, display:"flex", flexDirection:"column" } }>
        <div ref={editorAreaRef} className={photoBg ? photoCls : undefined} style={{ ...(aspectLock
            ? { aspectRatio:`${photoAspect.w} / ${photoAspect.h}`, height:`min(100cqh, calc(100cqw * ${photoAspect.h} / ${photoAspect.w}))`, maxWidth:"100%", maxHeight:"100%", width:"auto" }
            : { flex:1, width:"100%" }), overflowY:"auto", overscrollBehavior:"contain", padding: isMobile ? "24px 20px" : "24px 40px", paddingBottom: isMobile
              ? (anyPanelOpen
                  ? (dockBottom + 64) + "px"
                  : (kbOpen
                      ? (vkSupported ? "calc(env(keyboard-inset-height, 0px) + 64px)" : "56px")
                      : 24))
              : 24, ...(photoBg ? { ["--note-img"]: photoImg } : { background: "transparent" }), lineHeight:1.8, position:"relative" }}
          onClick={()=>{ if(selectedStickerId) setSelectedStickerId(null); }}>
          {/* Image / sticker overlay — movable, resizable, rotatable, croppable */}
          {visibleStickers.length > 0 && (
            <StickerLayer
              stickers={visibleStickers}
              selectedId={selectedStickerId}
              onSelect={setSelectedStickerId}
              onUpdate={updateSticker}
              onDelete={deleteSticker}
              onCrop={(s)=>setCropSticker(s)}
              // Opens the photo the sticker was traced on, to clear the trace and
              // draw a new one (Jade, 2026-10-17: it used to open the gallery).
              // Stickers made before the photo was kept fall back to the cut-out
              // itself, so they can still be re-traced (trimmed), never the gallery.
              onEdit={(s)=>{ editingStickerIdRef.current = s.id; editingTraceRef.current = s.originalSrc ? s.trace || null : null; setStickerMakerSrc(s.originalSrc || s.content || null); setShowStickerMaker(true); }}
              onTextFocus={onWritingFocus}
              onTextBlur={onWritingBlur}
              containerRef={editorAreaRef}
            />
          )}
          {/* Title lives inside the writing area (Apple-Notes style). It's a
              contentEditable (not a plain <input>) so highlighted title text
              can take the same bold/italic/font/color formatting as the body —
              a plain <input> can't carry execCommand-based rich formatting at all. */}
          <div ref={titleRef} className="jrnl-title" data-ph="Title" contentEditable={true} suppressContentEditableWarning={true}
            spellCheck={false} data-gramm="false" data-gramm_editor="false" data-enable-grammarly="false"
            onInput={()=>scheduleAutosave()}
            onKeyDown={e=>{ if (e.key === "Enter") { e.preventDefault(); editorRef.current?.focus(); } }}
            onKeyUp={()=>updateFormats()} onMouseUp={()=>updateFormats()}
            onFocus={()=>updateFormats()}
            style={{ width:"100%", outline:"none", background:"transparent", fontSize:26, fontWeight:800, color:"var(--c-text)", fontFamily:"inherit", padding:0, margin:0, boxSizing:"border-box", lineHeight:1.25, whiteSpace:"pre-wrap", wordBreak:"break-word" }}/>
          <div className="jrnl-meta" style={{ fontSize:11, color:"var(--c-text-soft)", fontWeight:600, margin:"4px 0 12px" }}>
            {activeNote.updatedAt ? new Date(activeNote.updatedAt).toLocaleString("en-US",{month:"long",day:"numeric",year:"numeric",hour:"2-digit",minute:"2-digit"}) : activeNote.date}
          </div>
          <div ref={editorRef} className="jrnl-editor" contentEditable={true} suppressContentEditableWarning={true}
            spellCheck={false} data-gramm="false" data-gramm_editor="false" data-enable-grammarly="false"
            onInput={onEditorInput}
            onClick={onEditorClick}
            onKeyDown={onEditorKeyDown}
            onPointerDown={onEditorPointerDown} onPointerMove={onEditorPointerMove}
            onPointerUp={clearLinkMenuTimer} onPointerCancel={clearLinkMenuTimer}
            onKeyUp={()=>{ updateFormats(); keepCaretVisible(); }} onMouseUp={()=>{ updateFormats(); keepCaretVisible(); }}
            onFocus={onWritingFocus}
            onBlur={onWritingBlur}
            // paddingBottom here (not on the scroll wrapper, which already carries the
            // dynamic keyboard-height padding) is a constant baseline breathing-room
            // space below the last line — the same trick Apple Notes/Google Keep use
            // so a short note (or one whose last line has no trailing blank line) never
            // reads as text jammed flush against the bottom edge. Independent of
            // keyboard state: this space is there whether or not you're actively typing.
            style={{ minHeight:400, paddingBottom: isMobile ? 80 : 40, outline:"none", fontSize:"16px", fontWeight:500, color:"var(--c-text)", fontFamily:"inherit", lineHeight:"inherit" }}
          />
        </div>
        </div>

        {/* Both confirms render on <body>, outside the note: a dark note's paper turns the
            text inside it light (note-ink-light), which made their titles and the grey
            buttons white on the white card (Jade, 2026-11-02). */}
        {showDeleteConfirm && createPortal(
          <div style={{ position:"fixed", inset:0, zIndex:9000, background:"rgba(60,30,50,0.38)", display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
            <div style={{ background:"var(--c-bg-card)", borderRadius:18, padding:28, maxWidth:320, width:"100%", boxShadow:"0 8px 40px rgba(0,0,0,0.18)", textAlign:"center" }}>
              <ConfirmHeader title="Are you sure you want to delete this note?" />
              <div style={CONFIRM_STACK}>
                <button onClick={()=>setShowDeleteConfirm(false)} style={CONFIRM_CANCEL}>Cancel</button>
                <button onClick={()=>{ deleteNote(activeNoteId); setScreen("list"); setShowDeleteConfirm(false); }} style={CONFIRM_DANGER}>Delete</button>
              </div>
            </div>
          </div>, document.body
        )}
        {showBackSaveConfirm && createPortal(
          <div style={{ position:"fixed", inset:0, zIndex:9000, background:"rgba(60,30,50,0.38)", display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
            <div style={{ background:"var(--c-bg-card)", borderRadius:18, padding:28, maxWidth:320, width:"100%", boxShadow:"0 8px 40px rgba(0,0,0,0.18)", textAlign:"center" }}>
              <ConfirmHeader tone="accent" icon={Save} title="Save this note?" subtitle="You have unsaved changes." />
              <div style={CONFIRM_COLUMN}>
                <button onClick={saveAndLeave} style={CONFIRM_PRIMARY}>Save</button>
                <div style={CONFIRM_STACK}>
                  <button onClick={()=>setShowBackSaveConfirm(false)} style={CONFIRM_CANCEL}>Keep editing</button>
                  <button onClick={discardAndLeave} style={{ ...CONFIRM_CANCEL, color:"#e53935" }}>Discard</button>
                </div>
              </div>
            </div>
          </div>, document.body
        )}
        {folderModalEl}
        {linkMenu && createPortal(
          <>
            <div onClick={()=>setLinkMenu(null)} style={{ position:"fixed", inset:0, zIndex:9700 }} />
            <div style={{ position:"fixed", top:linkMenu.y, left:linkMenu.x, zIndex:9701, background:"var(--c-bg-card)", borderRadius:12, border:"1px solid var(--c-border-light)", boxShadow:"0 10px 30px rgba(0,0,0,0.20)", overflow:"hidden", minWidth:150 }}>
              <button onClick={removeEmbeddedLink}
                style={{ display:"flex", alignItems:"center", gap:8, width:"100%", padding:"11px 14px", border:"none", background:"transparent", cursor:"pointer", fontFamily:"inherit", fontSize:13, fontWeight:700, color:"#e8647a" }}>
                <X size={14}/> Remove link
              </button>
            </div>
          </>,
          document.body
        )}
        {emptyDiscardToastEl}
        {imageSavedToastEl}
      </div>
    );
    // On mobile, portal to <body> so position:fixed is viewport-relative. An
    // ancestor (.clarity-tab-content) keeps a persisted transform from its entry
    // animation, which would otherwise make our fixed container/toolbar resolve
    // against that tall scroll box instead of the screen — breaking layout and
    // trapping all taps.
    // Desktop ≥1024px: editor lives inline in the right pane so the note list
    // stays visible (master–detail). Narrower screens keep the full-screen flow.
    if (isDesktop) return desktopShell(editorNode);
    // Always render the same wrapper <div> here (only its style varies with
    // isMobile) rather than conditionally adding/omitting it — an in/out
    // wrapper changes the tree shape one level above the contentEditable
    // note body, which used to make React tear down and remount it (wiping
    // whatever was mid-typing) whenever isMobile flipped, e.g. on rotation.
    // Mobile gets padding rather than no styles at all: this is portalled into
    // document.body, outside the app shell, so nothing else keeps its header
    // clear of the status bar or its footer clear of the gesture bar. Still one
    // wrapper either way, so the tree shape noted above is unchanged.
    return createPortal(
      <div style={isMobile
        ? { paddingTop:"var(--sat, 0px)", paddingBottom:"var(--sab, 0px)" }
        : { position:"fixed", top:0, left:96, right:0, bottom:0, zIndex:4000, background:"var(--c-bg)", display:"flex", justifyContent:"center" }}>
        {editorNode}
      </div>,
      document.body
    );
  }

  // Desktop list state: persistent left pane + empty right pane.
  if (isDesktop) return desktopShell(desktopEmptyState);

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      <style>{tbStyle}</style>

      {(() => {
        const searchBarUI = (
          <div style={{ position:"relative", flex:1, minWidth:0, display:"flex", alignItems:"center" }}>
            <Search className="jr-ctl-ico" size={14} color="var(--c-text-soft)" style={{ position:"absolute", left:10, pointerEvents:"none" }}/>
            <input className="jr-ctl" value={searchQuery} onChange={e=>setSearchQuery(e.target.value)} placeholder="Search entries…"
              style={{ width:"100%", padding:"8px 34px 8px 32px", borderRadius:99, border:"1.5px solid var(--c-border-light)", background:"var(--c-bg-card)", fontSize:13, color:"var(--c-text)", fontFamily:"inherit", outline:"none", boxSizing:"border-box" }}/>
            <button ref={sortBtnRef} onClick={() => {
                if (!sortMenuOpen && sortBtnRef.current) {
                  const r = sortBtnRef.current.getBoundingClientRect();
                  setSortMenuPos({ top: r.bottom + 6, left: Math.min(r.left, window.innerWidth - 140 - 8) });
                }
                setSortMenuOpen(v => !v);
              }}
              title="Sort notes" className="jr-ctl-ico"
              style={{ position:"absolute", right:6, width:26, height:26, display:"flex", alignItems:"center", justifyContent:"center", background:"none", border:"none", cursor:"pointer", color:"var(--c-text-soft)", borderRadius:"50%" }}>
              <ArrowUpDown size={15}/>
            </button>
          </div>
        );
        // Folder dropdown button sits beside the search bar (replaces the old
        // horizontally-scrollable folder pill row) — portalled alongside the
        // search bar into the mobile top-bar slot when one is provided, so
        // both stay visually adjacent there too. The slot div itself sets no
        // gap of its own, so the gap is applied here on a wrapper rather than
        // relying on the portal target's flex container.
        const rowUI = <div style={{ display:"flex", gap:14, alignItems:"center", width:"100%" }}>{searchBarUI}{renderFolderDropdown()}</div>;
        return searchSlotNode ? createPortal(rowUI, searchSlotNode) : (
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>{rowUI}</div>
        );
      })()}
      {sortMenuOpen && createPortal(
        <>
          <div onClick={()=>setSortMenuOpen(false)} style={{ position:"fixed", inset:0, zIndex:19990 }} />
          <div style={{ position:"fixed", top:sortMenuPos.top, left:sortMenuPos.left, zIndex:19999, background:"var(--c-bg-card)", border:"1.5px solid var(--c-border-light)", borderRadius:12, boxShadow:"0 8px 24px rgba(0,0,0,0.15)", padding:6, minWidth:130 }}>
            {SORT_OPTIONS.map(opt => (
              <button key={opt.key} onClick={()=>{ setSortMode(opt.key); setSortMenuOpen(false); }}
                style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, width:"100%", padding:"8px 10px", borderRadius:8, border:"none", background: sortMode===opt.key ? "var(--c-bg-active)" : "transparent", color:"var(--c-text)", fontSize:13, fontWeight: sortMode===opt.key?700:500, cursor:"pointer", fontFamily:"inherit", textAlign:"left" }}>
                {opt.label}
                {sortMode===opt.key && <Check size={13} color="var(--c-primary)" />}
              </button>
            ))}
          </div>
        </>,
        document.body
      )}

      {filteredNotes.length === 0 ? (
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"60px 20px", color:"var(--c-text-soft)" }}>
          <BookMarked size={54} color={isDarkBgTheme() ? "#ffffff" : "#000000"} style={{ marginBottom:18 }}/>
          <p style={{ margin:0, fontSize:18, fontWeight:800, color: isDarkBgTheme() ? "#ffffff" : "#000000" }}>{filterFolder ? "No notes here yet" : "No entries yet"}</p>
          <p style={{ margin:"8px 0 20px", fontSize:14, color: isDarkBgTheme() ? "rgba(255,255,255,0.75)" : "rgba(0,0,0,0.6)", textAlign:"center", maxWidth:280 }}>{filterFolder ? "Notes you move into this folder will show up here." : "Start writing your thoughts, ideas and memories here ^^"}</p>
          <button onClick={()=>createNote({html:""})} style={{ display:"flex", alignItems:"center", gap:6, background:"linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))", color:"var(--c-on-primary)", border:"none", borderRadius:12, padding:"10px 22px", fontWeight:800, fontSize:14, cursor:"pointer" }}>
            <Plus size={16}/> Create First Entry
          </button>
        </div>
      ) : (
        <div style={{ display:"flex", alignItems:"flex-start", gap:12, marginTop:26 }}>
          {/* Staggered two columns (not a grid): each card is as tall as its own
              text, so short notes stay short. A note goes to whichever column is
              shorter so far, using its text length as a height guess. */}
          {(() => {
            const cols = [[], []];
            const heights = [0, 0];
            filteredNotes.forEach((note, i) => {
              const lines = Math.min(6, Math.ceil(noteExcerpt(note).slice(0,600).length / 22) || 1);
              const c = heights[0] <= heights[1] ? 0 : 1;
              heights[c] += lines + 4;
              cols[c].push({ note, i });
            });
            return cols;
          })().map((col, ci) => (
          <div key={ci} style={{ flex:1, minWidth:0, display:"flex", flexDirection:"column", gap:12 }}>
          {col.map(({ note, i: _i }) => {
            const excerpt = noteExcerpt(note).slice(0,600);
            const dt = note.updatedAt ? new Date(note.updatedAt).toLocaleString("en-US",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"}) : note.date;
            const spine = noteSpineColor(note);
            return (
              <div key={note.id} onClick={()=>openNote(note)}
                onContextMenu={e=>{ e.preventDefault(); const r=e.currentTarget.getBoundingClientRect(); setContextMenu({rect:{left:r.left,width:r.width,top:r.top,bottom:r.bottom},note}); }}
                // note-light: in dark mode a note card keeps its own paper colour and
                // dark text (Jade, 2026-10-17), see theme/darkModeCss.js.
                className={"clarity-row-item note-light" + (isDarkNoteBg(noteBackground(note)) ? " note-ink-light" : "")}
                style={{ background:noteCardBg(note), borderRadius:16, border:"1.5px solid var(--c-border-mid)", cursor:"pointer", boxShadow:"0 1px 6px var(--c-shadow)", overflow:"hidden", transition:"transform 0.15s,box-shadow 0.15s", position:"relative", display:"flex", alignItems:"stretch", width:"100%", boxSizing:"border-box", animationDelay:`${_i * 0.035}s` }}
                onMouseEnter={e=>{ e.currentTarget.style.transform="translateY(-2px)"; e.currentTarget.style.boxShadow="0 6px 22px var(--c-shadow)"; }}
                onMouseLeave={e=>{ e.currentTarget.style.transform=""; e.currentTarget.style.boxShadow="0 1px 6px var(--c-shadow)"; }}
              >
                {/* Accent spine on the left — inherits the note's category colour */}
                <div style={{ width:5, flexShrink:0, background:spine, opacity:0.85 }} />
                <div style={{ padding:"15px 16px", display:"flex", flexDirection:"column", gap:5, flex:1, minWidth:0, minHeight:64, boxSizing:"border-box" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                    <p style={{ margin:0, fontSize:15, fontWeight:800, color:"var(--c-text)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", flex:1, minWidth:0 }}>
                      {note.title || <span style={{ color:"var(--c-text-soft)", fontStyle:"italic" }}>Untitled</span>}
                    </p>
                    {note.pinned && <Pin size={12} color="var(--c-primary)" style={{ flexShrink:0 }}/>}
                  </div>
                  {excerpt
                    ? <p style={{ margin:0, fontSize:12.5, color:notePlaceholderColor(note), lineHeight:1.45, display:"-webkit-box", WebkitLineClamp:6, WebkitBoxOrient:"vertical", overflow:"hidden" }}>{excerpt}</p>
                    : <p style={{ margin:0, fontSize:12.5, color:notePlaceholderColor(note), opacity:0.55, fontStyle:"italic" }}>No additional text</p>}
                  <div style={{ display:"flex", alignItems:"center", gap:5, marginTop:2, flexWrap:"wrap" }}>
                    <span style={{ fontSize:10.5, color:"var(--c-text-soft)", fontWeight:600 }}>{dt}</span>
                  </div>
                </div>
              </div>
            );
          })}
          </div>
          ))}
        </div>
      )}

      {contextMenuEl}
      {contextDeleteConfirmEl}

      {folderModalEl}

      {/* Floating + (New Entry) — fixed bottom-right, like the Habits tab */}
      {!folderTargetId && !settingsOpen && createPortal(
        <button data-tour="journal-add-btn" onClick={()=>createNote({html:""})} title="New entry"
          style={{ position:"fixed", bottom:"calc(90px + var(--sab, 0px))", right:24, zIndex:99999, width:56, height:56, borderRadius:18, border:"none", background:"linear-gradient(135deg, var(--c-primary-light) 0%, var(--c-primary-deep) 100%)", color:"var(--c-on-primary)", display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 4px 18px rgba(180,80,160,0.45)", cursor:"pointer", transition:"transform 0.15s, box-shadow 0.15s" }}
          onMouseEnter={e=>{ e.currentTarget.style.transform="scale(1.08)"; e.currentTarget.style.boxShadow="0 6px 24px rgba(180,80,160,0.55)"; }}
          onMouseLeave={e=>{ e.currentTarget.style.transform="scale(1)"; e.currentTarget.style.boxShadow="0 4px 18px rgba(180,80,160,0.45)"; }}>
          <Plus size={26} strokeWidth={2.5} />
        </button>,
        document.body
      )}

      {emptyDiscardToastEl}
      {imageSavedToastEl}
    </div>
  );
}
